import { APP_CONFIG, buildCopyrightText } from '@/config/app-config';

export default {
  common: {
    appName: APP_CONFIG.brand.name,
    appSubtitle: APP_CONFIG.brand.subtitle,
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    view: 'View',
    viewAll: 'View all',
    create: 'Create',
    search: 'Search',
    filter: 'Filter',
    refresh: 'Refresh',
    retry: 'Retry',
    back: 'Back',
    submit: 'Submit',
    close: 'Close',
    details: 'Details',
    done: 'Done',
    copy: 'Copy',
    success: 'Success',
    failed: 'Failed',
    required: 'Required',
    optional: 'Optional',
    enabled: 'Enabled',
    disabled: 'Disabled',
    switchLanguage: 'Switch language',
    dialog: 'Dialog',
    globalLoading: 'Loading...',
    loading: 'Loading...',
    saving: 'Saving…',
    resourceLoadingDescription: 'Loading application resources...',
    import: 'Import',
    export: 'Export',
    actions: 'Actions',
    noData: 'No data',
    yes: 'Yes',
    no: 'No',
  },
  toast: {
    viewportLabel: 'Notifications',
  },
  auth: {
    brandAria: 'Back to sign in',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    copyright: buildCopyrightText(),
    loginFailed: 'Sign-in failed',
    mfaFailed: 'MFA verification failed',
    verificationFailed: 'The verification code is incorrect. Please try again.',
    passkeyVerifying: 'Verifying...',
    passkeyLogin: 'Sign in with Passkey',
    or: 'or',
    mfaPrompt: 'MFA is enabled for {email}. Enter the 6-digit verification code.',
    mfaCode: 'One-time verification code',
    verifyAndSignIn: 'Verify and sign in',
    previousStep: 'Back',
    tenantIdentifier: 'Tenant identifier',
    tenantIdPlaceholder: 'Tenant ID',
    email: 'Email',
    password: 'Password',
    signingIn: 'Signing in...',
    signIn: 'Sign in',
    forgotPassword: 'Forgot password',
    trialQuestion: 'Want to try the platform?',
    applyTrial: 'Apply for a trial',
    enterEmailFirst: 'Enter your email first.',
    passkeyUnsupportedOrigin: 'This origin is not supported. Please sign in from http://localhost:6277.',
    passkeyRpMismatch: 'The Passkey domain does not match. Please sign in from http://localhost:6277.',
    passkeyNotBound: 'No Passkey is registered for this email. Register one in settings or sign in with your password.',
    passkeyFailed: 'Passkey sign-in failed. Try again or use your password.',
    mfaInvalid: 'The verification code is incorrect. Please check and try again.',
    mfaInvalidDetailed: 'The verification code is incorrect. Check the 6-digit code in your authenticator app.',
    sessionExpired: 'Your session has expired. Please sign in again.',
    sessionRevoked: 'Your account was signed in on another device. This session is no longer valid. Please sign in again.',
    ssoContinueWith: 'Continue with {provider}',
    tenantRequired: 'Enter your tenant identifier first',
    ssoErrors: {
      SSO_LOGIN_FAILED: 'Enterprise SSO sign-in failed. Try again or contact your administrator.',
      SSO_USER_NOT_PROVISIONED: 'This account is not provisioned in the platform yet.',
      SSO_MFA_SESSION_EXPIRED: 'The SSO verification session expired. Start sign-in again.',
      LOGIN_IP_NOT_ALLOWED: 'Your IP address is not allowed to sign in.',
      PASSWORD_LOGIN_DISABLED: 'Password sign-in is disabled for this tenant.',
      ENTERPRISE_MFA_REQUIRED: 'Tenant policy requires MFA. Enable it in Settings first.',
      OIDC_STATE_EXPIRED: 'The SSO state expired. Start sign-in again.',
    },
    forgot: {
      title: 'Reset password',
      description: 'Enter your account email and we will send a password reset link valid for 30 minutes.',
      sent: 'If the account exists, a reset email has been sent. Please check your inbox and spam folder.',
      tenantLabel: 'Tenant ID',
      optional: 'Optional',
      tenantPlaceholder: 'Required only when this email belongs to multiple tenants',
      sendResetEmail: 'Send reset email',
      submitting: 'Submitting...',
      backToSignIn: 'Back to sign in',
      submitFailed: 'Submission failed',
    },
    reset: {
      title: 'Set new password',
      description: 'Set a new sign-in password. This link and token expire in 30 minutes.',
      done: 'Your password has been updated and all previous sessions have been revoked. Sign in again with the new password.',
      goToSignIn: 'Go to sign in',
      token: 'Reset token',
      newPassword: 'New password',
      confirmPassword: 'Confirm password',
      updating: 'Updating...',
      updatePassword: 'Update password',
      mismatch: 'The two passwords do not match.',
      failed: 'Password reset failed',
    },
    invitation: {
      title: 'Accept account invitation',
      done: 'Your account has been created. Sign in with your email and new password.',
      token: 'Invitation token',
      name: 'Name',
      setPassword: 'Set password',
      joinTenant: 'Join tenant',
      acceptFailed: 'Failed to accept invitation',
      validation: {
        tokenRequired: 'Enter the invitation token.',
        passwordMin: 'Password must be at least 8 characters.',
      },
    },
  },
  personalAuth: {
    registerTitle: 'Personal account registration',
    registerDescription: 'Create your personal tenant and default workspace with email.',
    displayName: 'Display name',
    displayNamePlaceholder: 'e.g. Jane Doe',
    registerSubmit: 'Register',
    registerSubmitting: 'Registering…',
    registerFailed: 'Registration failed',
    emailAlreadyRegistered: 'This email is already registered. Please sign in.',
    haveAccount: 'Already have an account?',
    googleRegister: 'Register / sign in with Google',
    googleLogin: 'Sign in with Google',
    googleUnavailableTitle: 'This Google account cannot sign in',
    googleUnavailableDescription: 'This Google account is not registered. Use an account that has already been opened, or go back and apply for a trial.',
    googleUnavailableGeneric: 'Google sign-in could not be completed. Please try again later.',
    backHome: 'Back to home',
    verifyPendingTitle: 'Verify your email',
    verifyPendingDescription: 'Check your inbox and click the verification link.',
    verifySentTo: 'Verification email sent to: {email}',
    verifySuccessTitle: 'Email verified',
    verifySuccessDescription: 'You can now sign in and continue.',
    verifySuccessNotice: 'Your email is verified. Sign in to get started.',
    verifyAlreadyTitle: 'Already verified',
    verifyAlreadyDescription: 'This email address is already verified.',
    verifyAlreadyNotice: 'No further action needed. Please sign in to continue.',
    verifyExpiredTitle: 'Verification link invalid',
    verifyExpiredDescription: 'The link expired or is invalid. Please resend the verification email.',
    verifyExpiredNotice: 'Resend a new verification email and use the link in that message.',
    resendVerification: 'Resend verification email',
    resendAccepted: 'Verification email sent. Please check your inbox.',
    resendFailed: 'Failed to send verification email',
    emailNotVerified: 'Not verified',
    homeTitle: 'Personal home',
    homeLoadFailed: 'Failed to load personal home',
    workspaceType: 'Workspace type',
    entityType: 'Entity type',
    currency: 'Currency',
    fiscalYear: 'Fiscal year start',
    googleLinkRequired: 'An account with this email already exists. Sign in first, then link Google.',
    welcomeTitle: 'Welcome, {name}',
    welcomeDescription: 'Your personal workspace is ready. Start from the dashboard.',
    verifyPendingInline: 'Some features are available after email verification.',
    setupCompleteTitle: 'Account setup',
    setupCompleteDescription: 'Initial setup for sole proprietors is complete.',
    accountEmail: 'Email',
    workspaceName: 'Workspace',
    entityTypeSoleProprietor: 'Sole proprietor',
    fiscalYearValue: 'Starts {month}/{day} · {currency}',
    nextStepsTitle: 'Next steps',
    nextStepsDescription: 'Open the dashboard for an overview, or visit settings to update your profile.',
    goToDashboard: 'Go to dashboard',
    openSettings: 'Open settings',
    viewWorkspace: 'View workspace',
    uploadDocuments: 'Upload documents',
  },
  personalLineIntegration: {
    title: 'LINE integration',
    description: 'Link your personal account with the LINE Official Account.',
    notLinked: 'No LINE account is linked yet.\n\nAdd the LINE Official Account and send「連携」in the chat.',
    loadFailed: 'Failed to load LINE binding status',
    actionFailed: 'Failed to update LINE integration',
    platformTitle: 'Official Account',
    platformAvailable: 'Available',
    platformUnavailable: 'Unavailable',
    addFriend: 'Add friend',
    bindingsTitle: 'Linked workspaces',
    defaultBadge: 'Default receiver',
    imageIntakeOn: 'Image intake on',
    imageIntakeOff: 'Image intake off',
    linkedAt: 'Linked at {value}',
    noDefaultWarning: 'No receiving workspace is selected. Select a default before sending documents from LINE.',
    setDefaultButton: 'Set as default',
    unlinkWorkspaceButton: 'Unlink this workspace',
    unlinkAllButton: 'Unlink all LINE integrations',
    unlinkAllConfirmTitle: 'Unlink all LINE integrations',
    unlinkAllConfirmDescription: 'All active LINE workspace bindings for your account will be unlinked.',
    selectWorkspaceTitle: 'Select a workspace for LINE linking',
    selectWorkspaceDescription: 'Choose the workspace that should receive this LINE account binding.',
    noBindableWorkspace: 'No workspace is available for LINE linking.',
    alreadyLinked: 'Linked',
    resultSuccessTitle: 'LINE linking completed',
    resultSuccessDescription: 'You can return to the LINE app.',
    resultFailedTitle: 'LINE linking could not be completed',
    resultFailedDescription: 'Start linking again from the LINE chat.',
    resultPendingTitle: 'Confirming link status',
    resultPendingDescription: 'Please try again in a moment.',
  },
  workspaceLineIntegration: {
    title: 'Workspace LINE integration',
    description: 'Enable LINE document intake for this workspace.',
    platformTitle: 'Official Account',
    platformAvailable: 'Available',
    platformUnavailable: 'Unavailable',
    addFriend: 'Add friend',
    integrationTitle: 'Workspace integration',
    statusActive: 'Enabled',
    statusDisabled: 'Disabled',
    statusNotEnabled: 'Not enabled',
    imageIntakeOn: 'Image intake on',
    imageIntakeOff: 'Image intake off',
    enableButton: 'Enable LINE integration',
    disableButton: 'Disable LINE integration',
    enableImageButton: 'Enable image intake',
    disableImageButton: 'Disable image intake',
    myBindingTitle: 'My LINE binding',
    bound: 'Linked to this workspace.',
    notBound: 'Not linked to this workspace.',
    boundAccountFallback: 'LINE account {value}',
    boundAccountUnknown: 'Linked LINE account',
    lineAccountId: 'Account ID: {value}',
    linkedAt: 'Linked at: {value}',
    defaultBadge: 'Default receiver',
    setDefaultButton: 'Set as default receiver',
    unlinkButton: 'Unlink this workspace',
    linkStepsTitle: 'Binding steps',
    linkStepAddFriend: 'Add the official LINE account as a friend below.',
    linkStepSendCommand: 'Send "連携" in a 1:1 LINE chat.',
    linkStepOpenLink: 'Tap the "連携する" button from the bot, sign in, and select this workspace.',
    linkStepConfirm: 'Complete Account Link in LINE, then return here to verify the binding.',
    manageBindingsButton: 'Manage all LINE bindings',
    accountBindingsTitle: 'Account LINE bindings',
    accountBindingsDescription: 'Manage LINE bindings across workspaces, including the default receiving workspace.',
    currentWorkspaceBadge: 'This workspace',
    inviteTitle: 'Invite document submitters',
    inviteDescription: 'Generate a 6-digit code so LINE users without web accounts can bind to this workspace and submit documents.',
    generateInviteButton: 'Generate invite code',
    inviteCodeLabel: 'Invite code',
    inviteExpiresAt: 'Expires at {value}',
    inviteInstructions: 'Ask the submitter to send the 6-digit code in a 1:1 LINE chat with the Bot (or "BIND" followed by the code).',
    copyInviteCode: 'Copy code',
    copiedInviteCode: 'Copied',
    revokeInviteCode: 'Revoke code',
    submittersTitle: 'Bound submitters',
    submittersEmpty: 'No LINE submitters have been bound via invite code yet.',
    unlinkSubmitter: 'Unlink',
    loadFailed: 'Failed to load workspace LINE integration',
    actionFailed: 'Failed to update workspace LINE integration',
  },
  personalDocuments: {
    uploadTitle: 'Document upload',
    uploadDescription: 'Upload receipts and invoices as PDF or image files.',
    listTitle: 'Documents',
    listDescription: 'Uploaded documents waiting for the next processing phase.',
    selectFileTitle: 'Select a file',
    allowedFormats: 'PDF, JPEG, PNG up to {maxMb} MB',
    dropzone: 'Click to choose a file or drag it here',
    uploadButton: 'Upload',
    uploading: 'Uploading…',
    uploadSuccess: 'Upload completed.',
    uploadFailed: 'Upload failed',
    fileTooLarge: 'File exceeds the {maxMb} MB limit',
    viewList: 'View document list',
    nextPhaseNotice: 'OCR and draft suggestions will be available in a later release.',
    empty: 'No documents uploaded yet.',
    download: 'Download',
    delete: 'Delete',
    loadFailed: 'Failed to load documents',
    downloadFailed: 'Failed to download document',
    deleteFailed: 'Failed to delete document',
    workspaceRequired: 'Select a workspace before uploading documents.',
    viewDetail: 'Details',
    detailTitle: 'Document detail',
    detailDescription: 'Review OCR output and accounting candidates',
    processingStatus: 'Processing status',
    failureCode: 'Failure code',
    ocrResult: 'OCR result',
    pageCount: 'Page count',
    detectedLanguages: 'Detected languages',
    candidateTitle: 'Accounting candidate',
    candidateDisclaimer: 'AI-generated candidate only. Not posted to accounting data.',
    warnings: 'Warnings',
    retryProcessing: 'Retry processing',
    retryFailed: 'Retry failed',
    openReview: 'Open review',
    review: {
      title: 'Review',
      description: 'Verify the original document and correct the AI candidate.',
      aiDisclaimer: 'AI suggested values. Verify against the original document before confirming.',
      previewTitle: 'Document preview',
      showOriginal: 'Show original',
      formTitle: 'Reviewed values',
      documentNumber: 'Document number',
      memo: 'Description',
      linesTitle: 'Debit / credit lines',
      debit: 'Debit',
      credit: 'Credit',
      selectAccount: 'Select account',
      addLine: 'Add line',
      clearAll: 'Clear all candidates',
      totals: 'Totals',
      unbalanced: 'Debits and credits do not match',
      save: 'Save',
      confirm: 'Confirm review',
      confirmTitle: 'Confirm review',
      confirmDescription: 'Confirm reviewed values and create an accounting draft.',
      openDraft: 'Open draft',
      loadFailed: 'Failed to load review',
      saveFailed: 'Failed to save review',
      confirmFailed: 'Failed to confirm review',
      saveState: {
        idle: 'Unsaved',
        saving: 'Saving',
        saved: 'Saved',
        failed: 'Save failed',
        conflict: 'This content was updated on another screen. Reload the latest content and save again.',
      },
      steps: {
        document: 'Document',
        ocr: 'OCR',
        review: 'Review',
        draft: 'Draft',
        approval: 'Approval',
        posting: 'Posting',
      },
    },
    field: {
      documentType: 'Document type',
      documentDate: 'Date',
      counterparty: 'Counterparty',
      totalAmount: 'Total amount',
      taxAmount: 'Tax amount',
      confidence: 'Confidence',
    },
    processingStage: {
      OCR_QUEUED: 'Waiting for OCR',
      OCR_PROCESSING: 'OCR in progress',
      OCR_COMPLETED: 'OCR completed',
      AI_EXTRACTION_QUEUED: 'Waiting for candidate extraction',
      AI_EXTRACTING: 'Generating candidate',
      REVIEW_REQUIRED: 'Review required',
      PROCESSING_FAILED: 'Processing failed',
    },
    status: {
      UPLOADED: 'Uploading',
      SECURITY_SCANNING: 'Uploading',
      STORED: 'Stored',
      SECURITY_REJECTED: 'Failed',
      QUEUED_FOR_PARSING: 'Ready for processing',
      PARSING: 'Ready for processing',
      PARSED: 'Ready for processing',
      NEEDS_REVIEW: 'Review required',
      OCR_QUEUED: 'Waiting for OCR',
      OCR_PROCESSING: 'OCR in progress',
      OCR_COMPLETED: 'OCR completed',
      AI_EXTRACTION_QUEUED: 'Waiting for candidate extraction',
      AI_EXTRACTING: 'Generating candidate',
      PROCESSING_FAILED: 'Processing failed',
      PROCESSING_CANCELLED: 'Cancelled',
      ARCHIVED: 'Deleted',
    },
  },
  legalPage: {
    effectiveDate: 'Effective date: {date}',
  },
  trial: {
    title: 'Apply for a trial',
    description: 'Submit your organization information and verify your email. After approval, you will receive an administrator invitation.',
    submittedTitle: 'Application submitted',
    submittedDescription: 'Our operations team will review your application and notify you by email.',
    organizationName: 'Organization name',
    organizationPlaceholder: 'Enter your organization name',
    contactName: 'Contact name',
    contactPlaceholder: 'Enter the contact name',
    workEmail: 'Work email',
    emailVerified: 'Verified',
    resendAfter: 'Resend in {seconds}s',
    sendingCode: 'Sending',
    sendCode: 'Send code',
    verificationCode: 'Email verification code',
    codePlaceholder: 'Enter the 6-digit code',
    verifying: 'Verifying',
    confirmEmail: 'Verify email',
    phone: 'Phone number',
    phonePlaceholder: 'Enter a phone number',
    countryRegion: 'Country or region',
    countryPlaceholder: 'Example: Japan',
    expectedMonthlyUnits: 'Estimated monthly units',
    useCase: 'Use case',
    useCasePlaceholder: 'Example: process supplier invoices and extract amount, date, and company information each month',
    notes: 'Additional notes',
    notesPlaceholder: 'Add document types, languages, or expected launch timeline',
    agreementPrefix: 'I have read and agree to the',
    agreementConnector: 'and',
    agreementSuffix: '.',
    submitting: 'Submitting...',
    submit: 'Submit trial application',
    invalidEmail: 'Enter a valid email address.',
    codeSent: 'Verification code sent. Please check your email.',
    codeSendFailed: 'Failed to send verification code',
    invalidCode: 'Enter the 6-digit verification code.',
    emailVerifiedToast: 'Email verified',
    codeVerifyFailed: 'Verification failed',
    submitFailed: 'Submission failed. Please try again later.',
    validation: {
      organizationName: 'Enter your organization name.',
      organizationNameTooLong: 'Organization name must be {max} characters or fewer.',
      contactName: 'Enter the contact name.',
      contactNameTooLong: 'Contact name must be {max} characters or fewer.',
      phone: 'Enter a valid phone number.',
      phoneTooLong: 'Phone number must be {max} characters or fewer.',
      countryRegion: 'Enter the country or region.',
      countryRegionTooLong: 'Country or region must be {max} characters or fewer.',
      useCase: 'Describe the use case in at least 10 characters.',
      useCaseTooLong: 'Use case must be {max} characters or fewer.',
      expectedMonthlyUnits: 'Estimated monthly units must be greater than 0.',
      expectedMonthlyUnitsInteger: 'Estimated monthly units must be a whole number.',
      expectedMonthlyUnitsTooLarge: 'Estimated monthly units must be {max} or fewer.',
      notesTooLong: 'Additional notes must be {max} characters or fewer.',
      termsAccepted: 'Read and agree to the Terms of Service and Privacy Policy.',
      verificationToken: 'Verify your email before submitting.',
    },
  },
  navigation: {
    operatorConsole: 'Operator Console',
    tenantConsole: 'Back to Tenant Console',
    operationManagement: 'Operations',
  },
  impersonation: {
    bannerTitle: 'Acting as tenant',
    bannerDescription: 'You are managing "{tenant}" with tenant admin permissions',
    exitToOperator: 'Back to operator console',
    exitSuccess: 'Exited tenant impersonation',
    exitFailed: 'Failed to exit impersonation',
  },
  userMenu: {
    accountSettings: 'Account settings',
    feedback: 'My feedback',
    signOut: 'Sign out',
  },
  sidebar: {
    sendFeedback: 'Send feedback',
  },
  tenantShell: {
    menuSearchPlaceholder: 'Search menus',
    menuNoResults: 'No matching menus',
    collapseSidebar: 'Collapse sidebar',
    expandSidebar: 'Expand sidebar',
    breadcrumb: 'Breadcrumb',
    back: 'Back',
    refresh: 'Refresh current page',
    home: 'Back to home',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit fullscreen',
    openedPages: 'Opened pages',
    closeTab: 'Close {label}',
    closeCurrentTab: 'Close current',
    closeOtherTabs: 'Close others',
    closeAllTabs: 'Close all',
  },
  placeholders: {
    clickToCopy: 'Click to copy placeholders',
    custom: 'Custom',
    example: 'Example: {example}',
    copiedToken: 'Copied {token}',
    copyFailedManual: 'Copy failed. Copy it manually.',
  },
  dataTableCell: {
    copiedToClipboard: 'Copied to clipboard',
    copyFailed: 'Copy failed',
  },
  menu: {
    systemSettings: { label: 'System settings' },
    personalSettings: { label: 'Personal settings', subtitle: 'Personal account and security' },
    sections: {
      documents: 'Documents',
      banking: 'Banking',
      arAp: 'Receivables & Payables',
      accounting: 'Accounting',
      reports: 'Reports & Export',
    },
    dashboard: {
      label: 'Dashboard',
      subtitle: 'Workspace KPIs and work queue',
    },
    workspace: {
      label: 'Workspaces',
      subtitle: 'Accounting data spaces and member boundaries',
    },
    documentIntake: {
      label: 'Document Intake Inbox',
      subtitle: 'Receipt and invoice intake with AI parsing review',
    },
    documentSummary: {
      label: 'Voucher summary',
      subtitle: 'Finance summary, filters, and export for completed vouchers',
    },
    reviewInbox: {
      label: 'Review Inbox',
      subtitle: 'Journal draft review requests waiting for action',
    },
    accountingBooks: {
      label: 'Accounting book',
      subtitle: 'Manage the workspace accounting book and periods',
    },
    chartOfAccounts: {
      label: 'Chart of accounts',
      subtitle: 'Manage accounts for the active accounting book',
    },
    ledger: {
      label: 'Ledger',
      subtitle: 'Posted journal ledger for the active accounting book',
    },
    financialReports: {
      label: 'Financial Reports',
      subtitle: 'Trial balance, balance sheet, and P&L projections',
    },
    accountingExports: {
      label: 'Accounting Exports',
      subtitle: 'Export posted journals as generic CSV',
    },
    bankAccounts: {
      label: 'Account settings',
      subtitle: 'Bank accounts and GL mappings',
    },
    bankImport: {
      label: 'Bank Import',
      subtitle: 'Upload bank statements and review batches',
    },
    bankMatching: {
      label: 'Bank Matching',
      subtitle: 'Match bank statement lines to candidates and confirm decisions',
    },
    bankEvidence: {
      label: 'Bank Evidence Link',
      subtitle: 'Link withdrawal lines to confirmed document segments',
    },
    bankReconciliation: {
      label: 'Period Reconciliation',
      subtitle: 'Review bank lines and business transactions by period',
    },
    bankGlMappings: {
      label: 'Bank GL Mapping',
      subtitle: 'Configure bank account to GL account mappings',
    },
    arApReceivables: {
      label: 'Receivables',
      subtitle: 'Operational AR open items',
    },
    arApPayables: {
      label: 'Payables',
      subtitle: 'Operational AP open items',
    },
    arApPayments: {
      label: 'Payments',
      subtitle: 'Incoming and outgoing cash',
    },
    openingArAp: {
      label: 'Opening AR/AP',
      subtitle: 'Import opening AR/AP balances',
    },
    japaneseFinancialStatements: {

      label: 'Japanese Financial Statements',
      subtitle: 'Japanese presentation from report snapshots',
    },
    webhooks: {
      label: 'Webhooks',
      subtitle: 'Event delivery and integrations',
    },
    team: {
      label: 'Access Management',
      subtitle: 'Menu permissions and members',
    },
    organizationHealth: {
      label: 'Organization',
      subtitle: 'Company profile, offices, and departments',
    },
    employeeManagement: {
      label: 'Employee Management',
      subtitle: 'Employees, retirees, and tags',
    },
    billing: {
      label: 'Plan & Billing',
      subtitle: 'Plan, quotas, and invoices',
    },
    settings: {
      label: 'Settings',
      subtitle: 'Account and security',
    },
    personalLineIntegration: {
      label: 'LINE Integration',
      subtitle: 'Bind LINE and manage receiving workspaces',
    },
    feedback: {
      label: 'My Feedback',
      subtitle: 'View feedback history and follow-ups',
    },
    notifications: {
      label: 'Notifications',
      subtitle: 'In-app alerts and updates',
    },
  
},
  workspace: {
    switcher: { placeholder: 'Select workspace' },
    accessSource: {
      DIRECT: 'Direct member',
      FIRM_ENGAGEMENT: 'Firm access',
      FIRM_ASSIGNMENT: 'Firm assignment',
      firmEngagement: 'Firm access',
    },
    actions: {
      createPersonal: 'Create personal workspace',
      createFirm: 'Create workspace',
      lineIntegration: 'LINE integration',
    },
    line: {
      unavailable: 'LINE linking is unavailable',
    },
    form: {
      name: 'Workspace name',
      personalPlaceholder: 'Example: Personal books',
      firmPlaceholder: 'Example: Tokyo Tax Firm',
      defaults: 'First launch is fixed to Asia/Tokyo, ja-JP, JPY, and a January 1 fiscal year start.',
    },
    page: {
      title: 'Workspaces',
      description: 'Workspace is the operating boundary for documents, bank rows, accounting drafts, reviews, and exports.',
      noAccess: 'You cannot access the current workspace. Choose another workspace or contact an administrator.',
      emptyTitle: 'No accessible workspaces',
      emptyDescription: 'You must belong to at least one Workspace before working with accounting data.',
    },
    table: { name: 'Name', type: 'Type', status: 'Status', roles: 'Roles', actions: 'Actions' },
    type: { PERSONAL: 'Personal', FIRM_INTERNAL: 'Tax firm', CLIENT_COMPANY: 'Client company' },
    status: { ACTIVE: 'Active', SUSPENDED: 'Suspended', ARCHIVED: 'Archived' },
    role: {
      PERSONAL_OWNER: 'Personal owner',
      FIRM_OWNER: 'Firm owner',
      FIRM_ADMIN: 'Firm admin',
      ACCOUNTANT: 'Accountant',
      REVIEWER: 'Reviewer',
      ASSISTANT: 'Assistant',
      COMPANY_OWNER: 'Company owner',
      COMPANY_ADMIN: 'Company admin',
      COMPANY_MEMBER: 'Company member',
      COMPANY_VIEWER: 'Company viewer',
    },
  },
  chartOfAccounts: {
    title: 'Chart of accounts',
    subtitle: 'Manage accounts for the active accounting book.',
    emptyNoBook: {
      title: 'No active accounting book',
      description: 'Create an accounting book in book settings before managing accounts.',
      openSettings: 'Open book settings',
    },
    conflict: {
      title: 'Multiple active books',
      description: 'Resolve to a single ACTIVE book in book settings before managing accounts.',
      openSettings: 'Open book settings',
    },
    actions: {
      createAccount: 'Add account',
      edit: 'Edit',
      archive: 'Archive',
      initialize: 'Initialize',
    },
    initialize: {
      confirmDescription:
        'Only missing default accounts will be added. Existing account codes and names will not be changed.',
    },
    fields: {
      code: 'Code',
      name: 'Name',
      accountType: 'Type',
      normalBalance: 'Balance',
      status: 'Status',
    },
    errors: {
      loadFailed: 'Failed to load chart of accounts.',
      bookAccessRequired:
        'You need accounting book settings access to load the active book before managing accounts.',
      initializeFailed: 'Failed to initialize default accounts.',
    },
  },
  accountingBookSettings: {
    title: 'Accounting book',
    subtitle: 'Manage the workspace active accounting book and periods.',
    tabs: {
      summary: 'Summary',
      accounts: 'Accounts',
      periods: 'Periods',
    },
    empty: {
      title: 'No active accounting book',
      description: 'Create a book before using accounting drafts or the ledger.',
      create: 'Create book',
    },
    conflict: {
      title: 'Multiple active books',
      description: 'Archive books until exactly one ACTIVE book remains before managing periods.',
      archive: 'Archive',
    },
    history: {
      title: 'Past books',
    },
    actions: {
      edit: 'Edit',
      archive: 'Archive',
      createAccount: 'Add account',
      initializePeriods: 'Initialize year',
    },
    fields: {
      name: 'Name',
      accountingBasis: 'Accounting basis',
      fiscalYearStart: 'Fiscal year start',
      defaultTaxTreatment: 'Default tax treatment',
      currency: 'Currency',
      status: 'Status',
      fiscalYear: 'Fiscal year',
    },
    errors: {
      loadFailed: 'Failed to load accounting books.',
      createFailed: 'Failed to create accounting book.',
      updateFailed: 'Failed to update accounting book.',
      archiveFailed: 'Failed to archive accounting book.',
      accountCreateFailed: 'Failed to create account.',
      accountUpdateFailed: 'Failed to update account.',
      accountArchiveFailed: 'Failed to archive account.',
      periodInitFailed: 'Failed to initialize accounting periods.',
    },
  },
  documents: {
    transaction: {
      field: {
      },
      type: {
        EXPENSE: 'Expense',
        PURCHASE: 'Purchase',
        SALE: 'Sale',
        INCOME: 'Income',
        PAYMENT: 'Payment',
        RECEIPT: 'Receipt',
        TRANSFER: 'Transfer',
        REFUND: 'Refund',
        UNKNOWN: 'Unknown',
      },
      status: {
        DRAFT: 'Draft',
        REVIEW_REQUIRED: 'Review required',
        CONFIRMED: 'Confirmed',
        REJECTED: 'Rejected',
        SUPERSEDED: 'Superseded',
        CANCELLED: 'Cancelled',
      },
      validation: {
      },
      warning: {
      },
    },
    accounting: {
      posted: 'Posted',
      book: { title: 'Accounting Book', empty: 'No active book yet.', create: 'Create book', defaultName: 'Default accounting book', openSettings: 'Open book settings' },
      classification: {
        status: { SUGGESTED: 'Suggested', REVIEW_REQUIRED: 'Review required', CONFIRMED: 'Confirmed', REJECTED: 'Rejected', SUPERSEDED: 'Superseded', CANCELLED: 'Cancelled' },
      },
      journal: {
        status: { DRAFT: 'Draft', REVIEW_REQUIRED: 'Review required', CONFIRMED: 'Draft confirmed', REJECTED: 'Rejected', SUPERSEDED: 'Superseded', CANCELLED: 'Cancelled' },
      },
      validation: { VALID: 'Balanced', WARNING: 'Warning', INVALID: 'Invalid' },
      tax: { DISPLAYED_TAX_10: 'Displayed 10%', DISPLAYED_REDUCED_TAX_8: 'Displayed 8%', DISPLAYED_EXEMPT: 'Displayed exempt', DISPLAYED_MIXED: 'Displayed mixed', UNKNOWN: 'Unknown tax suggestion' },
      period: {
        range: '{start} to {end}',
        status: { OPEN: 'Open', SOFT_LOCKED: 'Soft locked', CLOSED: 'Closed' },
      },
      posting: {
        title: 'Posting',
        request: 'Request posting',
        processingHint: 'Posting request is queued and processing.',
        status: {
          NOT_REQUESTED: 'Not requested',
          QUEUED: 'Queued',
          PROCESSING: 'Processing',
          SUCCEEDED: 'Posted',
          RETRYABLE_FAILED: 'Failed (retryable)',
          PERMANENTLY_FAILED: 'Failed permanently',
          CANCELLED: 'Cancelled',
        },
      },
      postedJournal: {
        title: 'Posted Journal',
      },
      reversal: {
        title: 'Journal Reversal',
        create: 'Create reversal request',
        submit: 'Submit for review',
        reversalDate: 'Reversal date',
        reasonCode: 'Reason code',
        reasonText: 'Reversal reason',
        displayStatus: {
          none: 'Not reversed',
          pending: 'Reversal in progress',
          posted: 'Reversed',
        },
        status: {
        },
      },
      amendment: {
        displayStatus: {
        },
        types: {
        },
        status: {
        },
      },
    },
    ledger: {
      title: 'Ledger',
      backToDocuments: 'Back to documents',
      financialReports: 'Financial reports',
      accountFilter: 'Account',
      allAccounts: 'All accounts',
      dateRange: 'Date range',
      journalNumber: 'Journal number',
      search: 'Search',
      empty: 'No ledger entries yet.',
      noPermission: 'You do not have ledger read permission in this workspace.',
      columns: { date: 'Date', journal: 'Journal', account: 'Account', side: 'Side', amount: 'Amount' },
      journalType: {
        STANDARD: 'Standard',
        REVERSAL: 'Reversal',
        AMENDMENT: 'Amendment',
      },
      reversesJournal: 'Reverses {value}',
      reversedByJournal: 'Reversed by {value}',
      amendsJournal: 'Amends {value}',
      amendedByJournal: 'Amended by {value}',
      periodClose: {
        period: 'Accounting period',
        summaryTitle: 'Period summary',
        title: 'Period close',
        ledgerEntryCount: 'Ledger entries',
        postedJournalCount: 'Posted journals',
        totalDebit: 'Total debit',
        totalCredit: 'Total credit',
        softLock: 'Soft lock period',
        create: 'Create close request',
        validate: 'Run validation',
        submit: 'Submit for review',
        cancel: 'Cancel request',
        checksTitle: 'Close validation checks',
        snapshotTitle: 'Ledger snapshot',
        snapshotGeneratedAt: 'Generated at {value}',
        openingBalanceWarning: 'Opening balance has not been generated for this period. Complete carry-forward from the prior closed period before period close.',
        summaryNone: 'No close request has been created yet.',
        summaryClosed: 'This period is already closed.',
        summarySoftLockRequired: 'Soft lock the period before creating a close request.',
        status: {
          DRAFT: 'Draft',
          VALIDATING: 'Validating',
          VALIDATION_FAILED: 'Validation failed',
          REVIEW_REQUIRED: 'Review required',
          APPROVED: 'Approved',
          CLOSING: 'Closing',
          CLOSED: 'Closed',
          REJECTED: 'Rejected',
          CANCELLED: 'Cancelled',
          SUPERSEDED: 'Superseded',
          FAILED: 'Failed',
        },
        checkStatus: {
          PASSED: 'Passed',
          WARNING: 'Warning',
          FAILED: 'Failed',
          NOT_APPLICABLE: 'N/A',
        },
        snapshotColumns: {
          code: 'Code',
          account: 'Account',
          periodDebit: 'Period debit',
          periodCredit: 'Period credit',
          closingDebit: 'Closing debit',
          closingCredit: 'Closing credit',
        },
        checkMessages: {
          POSTING_IN_PROGRESS_PASSED: 'No posting requests in progress.',
          POSTING_IN_PROGRESS_FAILED: 'Posting requests are still in progress.',
          POSTING_FAILED_PASSED: 'No permanently failed posting requests.',
          POSTING_FAILED_FAILED: 'Permanently failed posting requests exist.',
          APPROVED_REVERSAL_NOT_POSTED_PASSED: 'No approved reversals waiting to post.',
          APPROVED_REVERSAL_NOT_POSTED_FAILED: 'Approved reversals have not been posted yet.',
          APPROVED_AMENDMENT_NOT_POSTED_PASSED: 'No approved amendments waiting to post.',
          APPROVED_AMENDMENT_NOT_POSTED_FAILED: 'Approved amendments have not been posted yet.',
          POSTED_JOURNAL_BALANCE_PASSED: 'Posted journals are balanced.',
          POSTED_JOURNAL_BALANCE_FAILED: 'Posted journals are not balanced.',
          LEDGER_LINE_COUNT_PASSED: 'Ledger entry count matches posted lines.',
          LEDGER_LINE_COUNT_FAILED: 'Ledger entry count does not match posted lines.',
          LEDGER_AMOUNT_MATCH_PASSED: 'Ledger amounts match posted lines.',
          LEDGER_AMOUNT_MATCH_FAILED: 'Ledger amounts do not match posted lines.',
          ORPHAN_LEDGER_ENTRY_PASSED: 'No orphan ledger entries.',
          ORPHAN_LEDGER_ENTRY_FAILED: 'Orphan ledger entries exist.',
          DUPLICATE_LEDGER_ENTRY_PASSED: 'No duplicate ledger entries.',
          DUPLICATE_LEDGER_ENTRY_FAILED: 'Duplicate ledger entries exist.',
          PERIOD_JOURNAL_DATE_PASSED: 'All journal dates are within the period.',
          PERIOD_JOURNAL_DATE_FAILED: 'Journal dates fall outside the period.',
          PERIOD_LEDGER_DATE_PASSED: 'All ledger dates are within the period.',
          PERIOD_LEDGER_DATE_FAILED: 'Ledger dates fall outside the period.',
          JOURNAL_TYPE_RELATION_PASSED: 'Journal type relations are valid.',
          JOURNAL_TYPE_RELATION_FAILED: 'Journal type relations are invalid.',
          TOTAL_DEBIT_CREDIT_PASSED: 'Ledger debits and credits are balanced.',
          TOTAL_DEBIT_CREDIT_FAILED: 'Ledger debits and credits are not balanced.',
          UNCONFIRMED_JOURNAL_DRAFTS_PASSED: 'No unconfirmed journal drafts.',
          UNCONFIRMED_JOURNAL_DRAFTS_WARNING: 'Unconfirmed journal drafts exist.',
          UNCONFIRMED_JOURNAL_DRAFTS_NOT_APPLICABLE: 'Unconfirmed journal draft check is not applicable.',
          OPEN_REVIEWS_PASSED: 'No open review cases.',
          OPEN_REVIEWS_WARNING: 'Open review cases exist.',
          OPEN_REVIEWS_NOT_APPLICABLE: 'Open review check is not applicable.',
          UNPOSTED_DOCUMENTS_PASSED: 'No unposted documents.',
          UNPOSTED_DOCUMENTS_WARNING: 'Unposted documents exist.',
          UNPOSTED_DOCUMENTS_NOT_APPLICABLE: 'Unposted document check is not applicable.',
          OPENING_BALANCE_REQUIRED_PASSED: 'Opening balance is present for this period.',
          OPENING_BALANCE_REQUIRED_FAILED: 'Opening balance is required before closing this period.',
          OPENING_BALANCE_CHECKSUM_PASSED: 'Opening balance checksum is valid.',
          OPENING_BALANCE_CHECKSUM_FAILED: 'Opening balance checksum validation failed.',
        },
      },
      trialBalance: {
        title: 'Trial balance',
        columns: {
          code: 'Code',
          account: 'Account',
          openingDebit: 'Opening debit',
          openingCredit: 'Opening credit',
          periodDebit: 'Period debit',
          periodCredit: 'Period credit',
          closingDebit: 'Closing debit',
          closingCredit: 'Closing credit',
        },
      },
      carryForward: {
        title: 'Carry forward',
        crossYearTitle: 'Cross-fiscal-year opening balance',
        create: 'Create carry-forward request',
        validate: 'Run validation',
        submit: 'Submit for review',
        cancel: 'Cancel request',
        checksTitle: 'Carry-forward validation checks',
        summaryNone: 'No carry-forward request has been created yet.',
        summaryCrossYearNone: 'After the prior fiscal year is closed, you can create a cross-year opening balance request.',
        summaryCrossYearCompleted: 'Cross-year opening balance has been generated for this fiscal year.',
        summaryCompleted: 'Opening balance has already been generated for this period.',
        summarySourceNotClosed: 'The prior period must be closed before carry-forward.',
        summaryNotApplicable: 'Intra-year carry-forward is not required for the first period.',
        crossYearDisclaimerRevenueExpense: 'Revenue and expense accounts are not carried into the next fiscal year.',
        crossYearDisclaimerCurrentYearProfit: 'The current-year profit account must be zero after year-end close.',
        crossYearDisclaimerRetainedEarnings: 'Retained earnings already include this year profit or loss and will carry at post-close balance.',
        crossYearDisclaimerNoJournal: 'This operation does not create posted journals or ledger entries.',
        crossYearDisclaimerNoFinancialStatements: 'This does not generate formal financial statements or complete tax filing.',
        status: {
          DRAFT: 'Draft',
          VALIDATING: 'Validating',
          VALIDATION_FAILED: 'Validation failed',
          REVIEW_REQUIRED: 'Review required',
          APPROVED: 'Approved',
          PROCESSING: 'Processing',
          COMPLETED: 'Completed',
          REJECTED: 'Rejected',
          CANCELLED: 'Cancelled',
          SUPERSEDED: 'Superseded',
          FAILED: 'Failed',
        },
        checkStatus: {
          PASSED: 'Passed',
          WARNING: 'Warning',
          FAILED: 'Failed',
          NOT_APPLICABLE: 'N/A',
        },
        checkMessages: {
          SOURCE_PERIOD_CLOSED_PASSED: 'Source period is closed.',
          SOURCE_PERIOD_CLOSED_FAILED: 'Source period is not closed.',
          TARGET_PERIOD_OPEN_PASSED: 'Target period is open.',
          TARGET_PERIOD_OPEN_FAILED: 'Target period is not open.',
          SAME_ACCOUNTING_BOOK_PASSED: 'Source and target periods belong to the same book.',
          SAME_ACCOUNTING_BOOK_FAILED: 'Source and target periods are not in the same book.',
          SAME_FISCAL_YEAR_PASSED: 'Source and target periods are in the same fiscal year.',
          SAME_FISCAL_YEAR_FAILED: 'Source and target periods are not in the same fiscal year.',
          PERIOD_ADJACENT_PASSED: 'Source and target periods are adjacent.',
          PERIOD_ADJACENT_FAILED: 'Source and target periods are not adjacent.',
          SOURCE_SNAPSHOT_EXISTS_PASSED: 'Source ledger snapshot exists.',
          SOURCE_SNAPSHOT_EXISTS_FAILED: 'Source ledger snapshot is missing.',
          SOURCE_SNAPSHOT_CHECKSUM_PASSED: 'Source ledger snapshot checksum is valid.',
          SOURCE_SNAPSHOT_CHECKSUM_FAILED: 'Source ledger snapshot checksum is invalid.',
          SOURCE_SNAPSHOT_TOTAL_BALANCE_PASSED: 'Source ledger snapshot totals are balanced.',
          SOURCE_SNAPSHOT_TOTAL_BALANCE_FAILED: 'Source ledger snapshot totals are not balanced.',
          SOURCE_ACCOUNT_BALANCE_VALID_PASSED: 'Source account balances are valid.',
          SOURCE_ACCOUNT_BALANCE_VALID_FAILED: 'Source account balances are invalid.',
          TARGET_NO_OPENING_BALANCE_PASSED: 'Target period has no opening balance yet.',
          TARGET_NO_OPENING_BALANCE_FAILED: 'Target period already has an opening balance.',
          TARGET_NO_POSTED_JOURNALS_PASSED: 'Target period has no posted journals.',
          TARGET_NO_POSTED_JOURNALS_FAILED: 'Target period already has posted journals.',
          TARGET_NO_CLOSE_REQUEST_PASSED: 'Target period has no active close request.',
          TARGET_NO_CLOSE_REQUEST_FAILED: 'Target period has an active close request.',
          BOOK_ACTIVE_PASSED: 'Accounting book is active.',
          BOOK_ACTIVE_FAILED: 'Accounting book is not active.',
          REQUEST_VERSION_CURRENT_PASSED: 'Carry-forward request version is current.',
          REQUEST_VERSION_CURRENT_FAILED: 'Carry-forward request version is stale.',
          ARCHIVED_ACCOUNTS_INCLUDED_PASSED: 'No archived accounts in source snapshot.',
          ARCHIVED_ACCOUNTS_INCLUDED_WARNING: 'Archived accounts are included in the source snapshot.',
          ABNORMAL_ACCOUNT_BALANCE_PASSED: 'No abnormal account balances detected.',
          ABNORMAL_ACCOUNT_BALANCE_WARNING: 'Abnormal account balances detected.',
          ZERO_BALANCE_ACCOUNTS_PASSED: 'No zero-balance accounts in source snapshot.',
          ZERO_BALANCE_ACCOUNTS_WARNING: 'Zero-balance accounts are included in the source snapshot.',
        },
      },
      yearEndClose: {
        fiscalYear: 'Fiscal year',
        title: 'Fiscal year close',
        create: 'Create year-end close request',
        validate: 'Run validation',
        generate: 'Generate closing lines',
        submit: 'Submit for review',
        cancel: 'Cancel request',
        retry: 'Retry posting',
        checksTitle: 'Year-end validation checks',
        snapshotTitle: 'Year-end snapshot',
        snapshotGeneratedAt: 'Generated at {value}',
        totalRevenue: 'Total revenue',
        totalExpense: 'Total expense',
        profitBeforeTransfer: 'Profit before transfer',
        summaryNone: 'No year-end close request has been created yet.',
        summaryCompleted: 'This fiscal year is already closed.',
        summaryNoPeriods: 'No accounting periods exist for this fiscal year.',
        summaryPeriodsNotClosed: '{count} period(s) are not closed yet. Close all periods before year-end close.',
        summaryPolicyNotConfigured: 'Configure current-year profit and retained earnings accounts on the book first.',
        disclaimerNoNextFyOpening: 'This release does not generate next fiscal year opening balances or cross-year carry-forward.',
        disclaimerNoFinancialStatements: 'This release does not output financial statements.',
        disclaimerNoReopen: 'Completed year-end close cannot be reopened or reversed from this screen.',
        progress: {
          validating: 'Running year-end validation…',
          approved: 'Review approved. Posting will start shortly…',
          postingQueued: 'Year-end posting is queued. Please wait…',
          partiallyPosted: 'Profit and loss closing posted. Retained earnings transfer is in progress…',
          completed: 'Fiscal year close is complete.',
        },
        status: {
          DRAFT: 'Draft',
          VALIDATING: 'Validating',
          VALIDATION_FAILED: 'Validation failed',
          REVIEW_REQUIRED: 'Review required',
          APPROVED: 'Approved',
          POSTING_QUEUED: 'Posting queued',
          PARTIALLY_POSTED: 'Partially posted',
          COMPLETED: 'Completed',
          REJECTED: 'Rejected',
          CANCELLED: 'Cancelled',
          SUPERSEDED: 'Superseded',
          FAILED: 'Failed',
        },
        checkStatus: {
          PASSED: 'Passed',
          WARNING: 'Warning',
          FAILED: 'Failed',
          NOT_APPLICABLE: 'N/A',
        },
        lineGroups: {
          PROFIT_AND_LOSS_CLOSING: 'Profit and loss closing',
          RETAINED_EARNINGS_TRANSFER: 'Retained earnings transfer',
        },
        lineColumns: {
          code: 'Code',
          account: 'Account',
          side: 'Side',
          amount: 'Amount',
        },
        snapshotColumns: {
          code: 'Code',
          account: 'Account',
          preCloseDebit: 'Pre-close debit',
          preCloseCredit: 'Pre-close credit',
          postCloseDebit: 'Post-close debit',
          postCloseCredit: 'Post-close credit',
          treatment: 'Treatment',
        },
        treatment: {
          CARRIED_FORWARD: 'Carried forward',
          CLOSED_TO_PROFIT: 'Closed to current-year profit',
          CURRENT_YEAR_PROFIT: 'Current-year profit',
          RETAINED_EARNINGS: 'Retained earnings',
          ZERO_BALANCE: 'Zero balance',
        },
        checkMessages: {
          FINAL_PERIOD_EXISTS_PASSED: 'Final period exists.',
          FINAL_PERIOD_EXISTS_FAILED: 'Final period does not exist.',
          FINAL_PERIOD_CLOSED_PASSED: 'Final period is closed.',
          FINAL_PERIOD_CLOSED_FAILED: 'Final period is not closed.',
          ALL_PERIODS_CLOSED_PASSED: 'All periods are closed.',
          ALL_PERIODS_CLOSED_FAILED: 'Some periods are not closed.',
          PERIOD_SEQUENCE_COMPLETE_PASSED: 'Period sequence is complete.',
          PERIOD_SEQUENCE_COMPLETE_FAILED: 'Period sequence is incomplete.',
          FINAL_LEDGER_SNAPSHOT_EXISTS_PASSED: 'Final ledger snapshot exists.',
          FINAL_LEDGER_SNAPSHOT_EXISTS_FAILED: 'Final ledger snapshot is missing.',
          FINAL_LEDGER_SNAPSHOT_CHECKSUM_PASSED: 'Final ledger snapshot checksum is valid.',
          FINAL_LEDGER_SNAPSHOT_CHECKSUM_FAILED: 'Final ledger snapshot checksum is invalid.',
          TEMPORARY_ACCOUNT_BALANCES_VALID_PASSED: 'Temporary account balances are valid.',
          TEMPORARY_ACCOUNT_BALANCES_VALID_FAILED: 'Temporary account balances are invalid.',
          OPENING_BALANCE_CHAIN_COMPLETE_PASSED: 'Opening balance chain is complete.',
          OPENING_BALANCE_CHAIN_COMPLETE_FAILED: 'Opening balance chain is incomplete.',
          CARRY_FORWARD_CHAIN_COMPLETE_PASSED: 'Carry-forward chain is complete.',
          CARRY_FORWARD_CHAIN_COMPLETE_FAILED: 'Carry-forward chain is incomplete.',
          NO_POSTING_IN_PROGRESS_PASSED: 'No posting requests in progress.',
          NO_POSTING_IN_PROGRESS_FAILED: 'Posting requests are still in progress.',
          NO_UNRESOLVED_POSTING_FAILURE_PASSED: 'No unresolved posting failures.',
          NO_UNRESOLVED_POSTING_FAILURE_FAILED: 'Unresolved posting failures exist.',
          NO_PENDING_REVERSAL_PASSED: 'No pending reversal requests.',
          NO_PENDING_REVERSAL_FAILED: 'Pending reversal requests exist.',
          NO_PENDING_AMENDMENT_PASSED: 'No pending amendment requests.',
          NO_PENDING_AMENDMENT_FAILED: 'Pending amendment requests exist.',
          NO_ACTIVE_PERIOD_CLOSE_PASSED: 'No active period close requests.',
          NO_ACTIVE_PERIOD_CLOSE_FAILED: 'Active period close requests exist.',
          NO_ACTIVE_CARRY_FORWARD_PASSED: 'No active carry-forward requests.',
          NO_ACTIVE_CARRY_FORWARD_FAILED: 'Active carry-forward requests exist.',
          BOOK_ACTIVE_PASSED: 'Accounting book is active.',
          BOOK_ACTIVE_FAILED: 'Accounting book is not active.',
          YEAR_NOT_ALREADY_CLOSED_PASSED: 'Fiscal year is not closed yet.',
          YEAR_NOT_ALREADY_CLOSED_FAILED: 'Fiscal year is already closed.',
          YEAR_END_POLICY_CONFIGURED_PASSED: 'Year-end policy is configured.',
          YEAR_END_POLICY_CONFIGURED_FAILED: 'Year-end policy is not configured.',
          YEAR_END_ACCOUNTS_VALID_PASSED: 'Year-end accounts are valid.',
          YEAR_END_ACCOUNTS_VALID_FAILED: 'Year-end accounts are invalid.',
          GENERATED_LINES_BALANCED_PASSED: 'Generated closing lines are balanced.',
          GENERATED_LINES_BALANCED_FAILED: 'Generated closing lines are not balanced.',
          REQUEST_VERSION_CURRENT_PASSED: 'Year-end request version is current.',
          REQUEST_VERSION_CURRENT_FAILED: 'Year-end request version is stale.',
          ARCHIVED_ACCOUNTS_INCLUDED_PASSED: 'No archived accounts in snapshot.',
          ARCHIVED_ACCOUNTS_INCLUDED_WARNING: 'Archived accounts are included in snapshot.',
          ABNORMAL_ACCOUNT_BALANCE_PASSED: 'No abnormal account balances detected.',
          ABNORMAL_ACCOUNT_BALANCE_WARNING: 'Abnormal account balances detected.',
          UNCONFIRMED_JOURNAL_DRAFTS_PASSED: 'No unconfirmed journal drafts.',
          UNCONFIRMED_JOURNAL_DRAFTS_WARNING: 'Unconfirmed journal drafts exist.',
          UNCONFIRMED_JOURNAL_DRAFTS_NOT_APPLICABLE: 'Unconfirmed journal draft check is not applicable.',
          OPEN_REVIEWS_PASSED: 'No open review cases.',
          OPEN_REVIEWS_WARNING: 'Open review cases exist.',
          OPEN_REVIEWS_NOT_APPLICABLE: 'Open review check is not applicable.',
        },
      },
    },
    review: {
      staleWarning: 'Subject version is stale',
      status: {
        DRAFT: 'Draft',
        PENDING: 'Pending',
        IN_REVIEW: 'In review',
        CHANGES_REQUESTED: 'Changes requested',
        APPROVED: 'Approved',
        REJECTED: 'Rejected',
        CANCELLED: 'Cancelled',
        SUPERSEDED: 'Superseded',
      },
      stepStatus: {
      },
      subjectType: {
        EXTRACTION: 'Extraction',
        BUSINESS_TRANSACTION: 'Business transaction',
        ACCOUNTING_CLASSIFICATION: 'Classification',
        JOURNAL_DRAFT: 'Journal draft',
      },
      actionType: {
      },
      actions: {
        submit: 'Submit',
        claim: 'Claim',
        approve: 'Approve',
        requestChanges: 'Request changes',
        reject: 'Reject',
        cancel: 'Cancel',
        reassign: 'Reassign',
      },
      inbox: {
        title: 'Review inbox',
        subtitle: 'Review cases assigned to you',
        backToDocuments: 'Back to documents',
        subject: 'Subject',
        type: 'Type',
        status: 'Status',
        step: 'Step',
        actions: 'Actions',
        empty: 'No review cases are waiting in your inbox.',
      },
    },
  },
  operator: {
    menuGroups: {
      healthManagement: 'Health Certification',
      overview: 'Overview',
      tenantOperations: 'Tenant Operations',
      commercial: 'Commercial',
      platformCapabilities: 'Platform Capabilities',
      dataOperations: 'Data & Operations',
      system: 'System',
    },
    certificationStandards: 'Certification Standards',
    certificationStandardsSubtitle: 'Standard versions, requirements, and publishing',
    overview: 'Operations Overview',
    overviewSubtitle: 'Key platform metrics',
    ops: 'Ops dashboard',
    opsSubtitle: 'Process health, dependencies, and queue backlog',
    tenants: 'Tenant Management',
    tenantsSubtitle: 'Provisioning, editing, and feature switches',
    trialApplications: 'Trial Applications',
    trialApplicationsSubtitle: 'Review applications and activate trial tenants',
    plans: 'Plan Management',
    plansSubtitle: 'Quota and menu feature configuration',
    billing: 'Subscriptions & Billing',
    billingSubtitle: 'Plan versions, subscriptions, invoices, and payments',
    billingEnhancements: 'Billing Enhancements',
    billingEnhancementsSubtitle: 'Coupons, plan-change simulation, and dunning status',
    featureFlags: 'Feature Flags',
    featureFlagsSubtitle: 'Progressive rollout, environment gates, and targeted overrides',
    backups: 'Backup & Restore',
    backupsSubtitle: 'Encrypted MySQL backups and data deletion management',
    reportDemo: 'Report Demo',
    reportDemoSubtitle: 'Generate a PDF report with test data',
    featureDemo: 'Feature Demos',
    importDemo: 'Import Demo',
    importDemoSubtitle: 'Batch import/export components and batch engine demo',
    usage: 'Usage Analytics',
    usageSubtitle: 'Tenant usage and cost',
    apiPlatform: 'API Platform',
    apiPlatformSubtitle: 'Tenant API key usage and error metrics',
    apiRequestLogs: 'API Request Logs',
    apiRequestLogsSubtitle: 'Third-party API key request history',
    ai: 'AI Management',
    aiSubtitle: 'Models, secrets, prompts, and call logs',
    userFeedbacks: 'User Feedback',
    userFeedbacksSubtitle: 'Tenant feedback and replies',
    notificationSend: 'Send Notification',
    notificationSendSubtitle: 'Deliver in-app alerts to all users, a tenant, or a user',
    notificationHistory: 'Notification History',
    notificationHistorySubtitle: 'Review sent notifications and preview content',
    logs: 'Logs',
    auditLogs: 'Audit Logs',
    auditLogsSubtitle: 'Cross-tenant operation records',
    webhookLogs: 'Webhook Logs',
    webhookLogsSubtitle: 'Delivery records and errors',
    reportLogs: 'Report Logs',
    reportLogsSubtitle: 'PDF report output records',
    outbox: 'Outbox Messages',
    outboxSubtitle: 'Outbox delivery detail, status filters, and manual retry',
    emailLogs: 'Email Logs',
    emailLogsSubtitle: 'Delivery status, content, and retries',
    emailTemplates: 'Email Templates',
    emailTemplatesSubtitle: 'Shared layout and template variables',
    systemSettings: 'System Settings',
    systemSettingsSubtitle: 'Platform-wide configuration',
  },
  operatorCertificationStandards: {
    title: 'Certification Standards', description: 'Manage standards, source provenance, and immutable releases', newStandard: 'New standard', empty: 'No certification standards yet',
    requirements: 'requirements', noRequirements: 'This version has no requirements', source: 'Version source', addRequirement: 'Add requirement', publish: 'Publish', createRevision: 'Create revision',
    code: 'Standard code', program: 'Program', division: 'Division', year: 'Certification year', nameJa: 'Japanese name', nameZh: 'Chinese name', nameEn: 'English name',
    stableKey: 'Stable key', kind: 'Kind', parent: 'Parent', order: 'Order', titleJa: 'Japanese title', titleZh: 'Chinese title', titleEn: 'English title', requirement: 'Requirement',
    createDescription: 'Create the standard and its first draft version.', sourceDescription: 'Record the official source URL and file SHA-256 before publishing.', requirementDescription: 'Stable keys remain unchanged after publication for cross-version tracing.',
    status: { DRAFT: 'Draft', PUBLISHED: 'Published', RETIRED: 'Retired' },
    kinds: { section: 'Group', question: 'Question', criterion: 'Criterion' },
    messages: { created: 'Standard created', saved: 'Saved', deleted: 'Requirement deleted', published: 'Version published', revisionCreated: 'Revision created', deleteConfirm: 'Delete requirement?', publishConfirm: 'Publish this version?', publishImmutable: 'Published content is immutable. Future changes require a new revision.' },
  },
  operatorFormat: {
    tenantStatuses: {
      ACTIVE: 'Active',
      SUSPENDED: 'Suspended',
      DELETED: 'Deleted',
    },
    memberRoles: {
      ADMIN: 'Administrator',
      MANAGER: 'Manager',
      OPERATOR: 'Operator',
      VIEWER: 'Viewer',
      EMPLOYEE: 'Employee',
    },
  },
  auditActions: {
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
    ASSIGN: 'Assign',
    ARCHIVE: 'Archive',
    SEND: 'Send',
    RETRY: 'Retry',
    TEST: 'Test',
    PUBLISH: 'Publish',
    LOGIN: 'Login',
    LOGIN_FAILED: 'Login failed',
    SSO_LOGIN: 'SSO login',
    SSO_LOGIN_FAILED: 'SSO login failed',
    LOGOUT: 'Logout',
    MFA_VERIFY: 'MFA verify',
    UPDATE_PROFILE: 'Update profile',
    UPDATE_AVATAR: 'Update avatar',
    CHANGE_PASSWORD: 'Change password',
    RECOVERY_CODES_REGENERATED: 'Regenerate recovery codes',
    REVOKE_API_KEY: 'Revoke API key',
    PASSWORD_RESET_REQUESTED: 'Password reset requested',
    PASSWORD_RESET_COMPLETED: 'Password reset completed',
    USER_INVITED: 'User invited',
    INVITATION_ACCEPTED: 'Invitation accepted',
    ACCOUNT_UNLOCKED: 'Account unlocked',
    IMPERSONATE_START: 'Impersonation started',
    IMPERSONATE_END: 'Impersonation ended',
    TRIAL_APPLICATION_SUBMITTED: 'Trial application submitted',
    TRIAL_APPLICATION_APPROVED: 'Trial application approved',
    TRIAL_APPLICATION_REJECTED: 'Trial application rejected',
    transaction: {
      draft_created: 'Transaction draft created',
      validation_warning: 'Transaction validation warning',
      updated: 'Transaction updated',
      confirmed: 'Transaction confirmed',
      rejected: 'Transaction rejected',
      cancelled: 'Transaction cancelled',
      superseded: 'Transaction superseded',
      regenerated: 'Transaction regenerated',
    },
    accounting_classification: {
      pending: 'Accounting classification pending',
    },
    accounting_book: { created: 'Accounting book created', updated: 'Accounting book updated', archived: 'Accounting book archived' },
    account: { created: 'Account created', updated: 'Account updated', archived: 'Account archived' },
    classification: { created: 'Classification created', suggested: 'Classification suggested', updated: 'Classification updated', confirmed: 'Classification confirmed', rejected: 'Classification rejected', superseded: 'Classification superseded' },
    accounting: {
      classification: {
        requested: 'Accounting classification requested',
        started: 'Accounting classification started',
        failed: 'Accounting classification failed',
      },
      journal_draft: {
        requested: 'Journal draft generation requested',
      },
      posting: {
        requested_from_foundation: 'Foundation posting requested',
        completed_from_foundation: 'Foundation posting completed',
      },
    },
    journal_draft: { created: 'Journal draft created', updated: 'Journal draft updated', confirmed: 'Journal draft confirmed', rejected: 'Journal draft rejected', cancelled: 'Journal draft cancelled', superseded: 'Journal draft superseded' },
    posting: { pending: 'Pending future posting phase' },
    financial_report: {
      requested: 'Financial report generation requested',
      started: 'Financial report generation started',
      completed: 'Financial report generation completed',
      failed: 'Financial report generation failed',
      cancelled: 'Financial report generation cancelled',
      retried: 'Financial report generation retried',
    },
    financial_report_snapshot: {
      created: 'Financial report snapshot created',
    },
    financial_report_mapping: {
      created: 'Account report mapping created',
      updated: 'Account report mapping updated',
    },
    document_accounting_draft: {
      created: 'Document accounting draft created',
      updated: 'Document accounting draft updated',
      validated: 'Document accounting draft validated',
      voided: 'Document accounting draft voided',
    },
    document_accounting_posting: {
      confirmed: 'Document accounting posting confirmed',
      succeeded: 'Document accounting posting succeeded',
      failed: 'Document accounting posting failed',
      retried: 'Document accounting posting retried',
    },
    document_intake: {
      upload_session: { created: 'Document intake upload session created' },
      upload: { completed: 'Document intake upload completed' },
      original: { download_issued: 'Document intake original download issued' },
      reparse: { requested: 'Document intake reparse requested' },
      review: { submitted: 'Document intake review submitted' },
      ownership: { assigned: 'Document intake ownership assigned' },
      duplicate: { marked: 'Document intake marked duplicate' },
      deletion: { requested: 'Document intake deletion requested' },
      retention: { locked: 'Document intake retention locked' },
      legal_hold: { applied: 'Document intake legal hold applied' },
      purged: 'Document intake purged',
    },
    document: {
      processing: {
        queued: 'Document processing queued',
        retry_requested: 'Document processing retry requested',
        exhausted: 'Document processing retries exhausted',
      },
      ocr: {
        started: 'Document OCR started',
        completed: 'Document OCR completed',
        failed: 'Document OCR failed',
      },
      ai_extraction: {
        started: 'Document AI extraction started',
        completed: 'Document AI extraction completed',
        failed: 'Document AI extraction failed',
      },
      review_candidate: {
        created: 'Document review candidate created',
      },
      accounting_bridge: {
        requested: 'Document accounting bridge requested',
        started: 'Document accounting bridge started',
        succeeded: 'Document accounting bridge succeeded',
        failed: 'Document accounting bridge failed',
        ineligible: 'Document accounting bridge ineligible',
        cancelled: 'Document accounting bridge cancelled',
        amendment_required: 'Document accounting bridge amendment required',
      },
    },
    business_transaction: {
      created_from_document_review: 'Business transaction created from document review',
    },
    ar_ap: {
      obligation_created: 'AR/AP obligation created',
      obligation_updated: 'AR/AP obligation updated',
      obligation_opened: 'AR/AP obligation opened',
      obligation_cancelled: 'AR/AP obligation cancelled',
      obligation_superseded: 'AR/AP obligation superseded',
      obligation_settled: 'AR/AP obligation settled',
      obligation_reopened: 'AR/AP obligation reopened',
      opening_import_batch_created: 'Opening AR/AP import batch created',
      opening_import_batch_validated: 'Opening AR/AP import batch validated',
      opening_import_batch_executed: 'Opening AR/AP import batch execution started',
      opening_import_batch_completed: 'Opening AR/AP import batch completed',
      opening_import_batch_failed: 'Opening AR/AP import batch failed',
      opening_import_batch_cancelled: 'Opening AR/AP import batch cancelled',
    },
    payment: {
      created: 'Payment created',
      updated: 'Payment updated',
      confirmed: 'Payment confirmed (operational fact)',
      cancelled: 'Payment cancelled',
    },
    bank_match: {
      payment_draft_created: 'Bank match payment draft created',
    },
    bank_evidence: {
      linked: 'Bank evidence linked',
      excluded: 'Bank evidence excluded',
      unlinked: 'Bank evidence unlinked',
      candidates_refreshed: 'Bank evidence candidates refreshed',
      feature_disabled: 'Bank evidence feature disabled',
    },
    settlement: {
      allocation_created: 'Settlement allocation created',
      allocation_voided: 'Settlement allocation voided',
      accounting_date_resolved: 'Settlement accounting date resolved',
      accounting_transaction_created: 'Settlement accounting transaction created',
      accounting_linked: 'Settlement accounting linked',
      accounting_orchestration_requested: 'Settlement accounting orchestration requested',
      accounting_orchestration_completed: 'Settlement accounting orchestration completed',
      accounting_orchestration_failed: 'Settlement accounting orchestration failed',
      accounting_classification_ensured: 'Settlement classification ensured',
      accounting_journal_ensured: 'Settlement journal draft ensured',
      accounting_posting_requested: 'Settlement accounting posting requested',
      accounting_orchestration_waiting: 'Settlement accounting orchestration waiting',
      accounting_orchestration_recovered: 'Settlement accounting orchestration recovered',
    },
    line_webhook: {
      received: 'LINE webhook received',
      queued: 'LINE webhook queued',
    },
    line_media: {
      fetch_started: 'LINE media fetch started',
      fetch_succeeded: 'LINE media fetch succeeded',
      fetch_failed: 'LINE media fetch failed',
    },
    line_document_intake: {
      submitted: 'LINE document intake submitted',
      duplicate_suppressed: 'LINE duplicate intake suppressed',
    },
    line: {
      account_link: {
        token_issued: 'LINE account link token issued',
        nonce_created: 'LINE account link nonce created',
        completed: 'LINE account link completed',
        failed: 'LINE account link failed',
        conflict: 'LINE account link conflict',
      },
      account_unlinked: 'LINE account unlinked',
      document: {
        received: 'LINE document received',
        binding_rejected: 'LINE document binding rejected',
        download_started: 'LINE document download started',
        download_failed: 'LINE document download failed',
        storage_completed: 'LINE document storage completed',
        intake_created: 'LINE document intake created',
        processing_enqueued: 'LINE document processing enqueued',
        duplicate_ignored: 'LINE duplicate document ignored',
        rejected: 'LINE document rejected',
      },
      production_gate: { changed: 'LINE production gate changed' },
    },
    financial_report_export: {
      requested: 'Financial report export requested',
      queued: 'Financial report export queued',
      started: 'Financial report export started',
      completed: 'Financial report export completed',
      failed: 'Financial report export failed',
      retried: 'Financial report export retried',
      cancelled: 'Financial report export cancelled',
      expired: 'Financial report export expired',
      download_url_issued: 'Financial report export download URL issued',
      artifact_deleted: 'Financial report export artifact deleted',
      orphan_cleanup_failed: 'Financial report export orphan cleanup failed',
    },
    HTTP_ERROR: 'HTTP error',
    REQUEST_PERF: 'Request performance',
    AUDIT_DB_WRITE_FAILED: 'Audit write failed',
    LOGIN_ATTEMPT_WRITE_FAILED: 'Login attempt write failed',
  },
  operatorAuditLogs: {
    filters: {
      tenantName: 'Tenant name',
      userName: 'User name or email',
      allActions: 'All actions',
    },
    columns: {
      time: 'Time',
      tenant: 'Tenant',
      user: 'User',
      action: 'Action',
      resource: 'Resource',
      details: 'Details',
    },
  },
  operatorFeatureChecklist: {
    menuPermissions: 'Menu permissions',
    featureToggles: 'Feature toggles',
    features: {
      apiKeys: 'API key management',
      mfa: 'Multi-factor authentication',
      dataExport: 'Data export',
      aiGateway: 'AI gateway access',
    },
  },
  operatorUsage: {
    refreshStats: 'Refresh stats',
    filters: {
      dateRange: 'Date range',
      tenant: 'Tenant',
      tenantRequired: 'Select a tenant',
    },
    units: 'Units',
    apiCalls: 'API calls',
    storage: 'Storage',
    cost: 'Estimated cost',
    documentCount: 'Documents',
    aiRequestCount: 'AI requests',
    inputTokens: 'Input tokens',
    outputTokens: 'Output tokens',
    planLabel: 'Plan: {name}',
    planComparison: 'Plan comparison',
    planComparisonHint: 'Bars show used / limit; ≥80% warning, ≥100% over limit.',
    noPlanLimit: 'No plan limit',
    costEstimateHint: 'Estimate from Gemini token rates',
    storageSnapshotHint: 'Full snapshot (not period delta)',
    unlimited: 'Unlimited',
    selectTenant: 'Select a tenant',
    selectTenantDescription: 'Choose a tenant to load usage KPIs and plan comparison for the selected period.',
    empty: 'No usage data',
    emptyDescription: 'This tenant has no usage in the selected period.',
    notes: {
      noPlanLimitMetrics: 'Document count and tokens are observational and have no plan limits yet.',
      costEstimate: 'Cost is an estimate based on Gemini token unit prices (USD).',
      storageSnapshot: 'Storage is a full stored_object snapshot, not a period increment.',
    },
    messages: {
      loadFailed: 'Unable to load usage stats',
      loadFailedDescription: 'Check your session or network connection, then try again.',
    },
  },
  operatorApiPlatform: {
    refresh: 'Refresh stats',
    viewLogs: 'View request logs',
    openDocs: 'Open API docs',
    requestCount: 'Requests',
    activeApiKeys: 'Active keys',
    errorCount: 'Errors',
    avgDuration: 'Avg duration',
    apiKeyFilter: 'API Key ID',
    apiKeyColumn: 'API Key',
    methodColumn: 'Method',
    pathColumn: 'Path',
    durationColumn: 'Duration',
  },
  apiDocs: {
    title: 'API Documentation',
    subtitle: 'OpenAPI-based endpoint reference',
    backToLogin: 'Back to login',
  },
  apiScopes: {
    webhooks: {
      read: 'Webhook read',
      write: 'Webhook write',
      delete: 'Webhook delete',
    },
    batch: {
      read: 'Batch read',
      write: 'Batch write',
    },
  },
  operatorUserFeedbacks: {
    filters: {
      tenantName: 'Tenant name',
      allSubjects: 'All subjects',
    },
    columns: {
      time: 'Time',
      tenant: 'Tenant',
      user: 'User',
      subject: 'Subject',
      status: 'Status',
      summary: 'Summary',
      actions: 'Actions',
    },
    actions: {
      details: 'Details',
    },
    detail: {
      title: 'Feedback details',
      description: 'Review the feedback, update its status, and reply to the user.',
      tenant: 'Tenant',
      user: 'User',
      email: 'Email',
      subject: 'Subject',
      status: 'Status',
    },
    reply: {
      placeholder: 'Reply to user...',
      sending: 'Sending...',
      send: 'Send reply',
    },
    messages: {
      loadDetailFailed: 'Failed to load details',
      statusUpdated: 'Status updated',
      updateFailed: 'Update failed',
      replyRequired: 'Enter reply content',
      replySent: 'Reply sent',
      sendFailed: 'Failed to send',
    },
  },
  operatorNotifications: {
    form: {
      title: 'Send in-app notification',
      description: 'Deliver alerts to all active users, a specific tenant, or a single user.',
    },
    fields: {
      audience: 'Audience',
      tenant: 'Target tenant',
      user: 'Target user',
      severity: 'Severity',
      title: 'Title',
      body: 'Message',
      actionUrl: 'Action URL',
    },
    audience: {
      all: 'All users',
      tenant: 'Specific tenant',
      user: 'Specific user',
    },
    placeholders: {
      tenant: 'Select tenant',
      user: 'Select user',
      title: 'e.g. Scheduled maintenance',
      actionUrl: '/billing',
    },
    hints: {
      actionUrl: 'Optional. Use an in-app path (e.g. /billing) or a full URL.',
    },
    actions: {
      send: 'Send notification',
      sending: 'Sending...',
    },
    validation: {
      titleRequired: 'Title is required',
      tenantRequired: 'Select a target tenant',
      userRequired: 'Select a target user',
    },
    toast: {
      sent: 'Sent {count} notification(s)',
      failed: 'Failed to send notification',
    },
  },
  operatorNotificationHistory: {
    filters: {
      allAudiences: 'All audiences',
    },
    columns: {
      sentAt: 'Sent at',
      audience: 'Audience',
      title: 'Title',
      recipients: 'Recipients',
      severity: 'Severity',
      status: 'Status',
      actions: 'Actions',
    },
    status: {
      active: 'Active',
      revoked: 'Revoked',
    },
    actions: {
      preview: 'Preview',
      revoke: 'Revoke',
      delete: 'Delete permanently',
    },
    confirm: {
      revokeTitle: 'Revoke this notification?',
      revokeDescription: 'Users will no longer see this notification, but the operator history entry will remain.',
      deleteTitle: 'Delete this notification permanently?',
      deleteDescription: 'This cannot be undone. All related user notifications will also be removed.',
    },
    messages: {
      revoked: 'Notification revoked',
      deleted: 'Notification deleted',
      revokeFailed: 'Failed to revoke notification',
      deleteFailed: 'Failed to delete notification',
    },
    previewDescription: 'Preview the full content of a sent notification.',
  },
  notificationDetail: {
    title: 'Notification details',
    emptyBody: 'No message body',
  },
  operatorLogPages: {
    filters: {
      tenantName: 'Tenant name',
    },
    columns: {
      time: 'Time',
      tenant: 'Tenant',
      event: 'Event',
      statusCode: 'Status code',
      error: 'Error',
    },
  },
  operatorEmailLogs: {
    filters: {
      allStatuses: 'All statuses',
    },
    columns: {
      recipient: 'Recipient',
      subject: 'Subject',
      status: 'Status',
      actions: 'Actions',
    },
    actions: {
      details: 'Details',
      retry: 'Retry',
    },
    statuses: {
      QUEUED: 'Queued',
      SENDING: 'Sending',
      SENT: 'Sent',
      FAILED: 'Failed',
    },
    detail: {
      title: 'Email details',
      recipient: 'Recipient',
      tenant: 'Tenant',
      status: 'Status',
      subject: 'Subject',
      template: 'Template',
      attempts: 'Attempts',
    },
    messages: {
      retryQueued: 'Email delivery has been queued again',
    },
  },
  operatorOutbox: {
    filters: {
      allStatuses: 'All statuses',
      allChannels: 'All channels',
      tenantId: 'Tenant ID',
    },
    columns: {
      time: 'Time',
      status: 'Status',
      channel: 'Channel',
      eventType: 'Event type',
      tenantId: 'Tenant ID',
      attempts: 'Attempts',
      lastError: 'Last error',
      actions: 'Actions',
    },
    actions: {
      retry: 'Retry',
      retrying: 'Retrying…',
    },
    statuses: {
      PENDING: 'Pending',
      PROCESSING: 'Processing',
      COMPLETED: 'Completed',
      DEAD: 'Dead letter',
    },
    channels: {
      audit: 'Audit',
      webhook: 'Webhook',
      email: 'Email',
      notification: 'Notification',
      ai_call_log: 'AI call log',
      report_log: 'Report log',
      batch_event: 'Batch event',
    },
    messages: {
      retried: 'Message requeued for delivery',
      retryFailed: 'Failed to retry outbox message',
    },
  },
  operatorReports: {
    demo: {
      title: 'Report output demo',
      subtitle: 'Generate a test PDF with multilingual text, dates, amounts, tables, and nested JSON.',
      printButton: 'Output test report',
      previewTitle: 'Test data preview',
      previewHint: 'This page shows a summary only. The PDF includes {images} images and {rows} detail rows with automatic pagination.',
    },
    confirm: {
      title: 'Confirm report output',
      description: 'The system will generate a test PDF and preview it in a dialog.',
      submit: 'Output report',
    },
    preview: {
      title: 'Report preview',
      description: 'Navigate pages, zoom, download, or print. All pages are shown in one scrollable view.',
    },
    messages: {
      generating: 'Generating report...',
      printStarted: 'Report generated',
    },
  },
  operatorImportDemo: {
    title: 'Batch import demo',
    subtitle: 'Upload CSV/Excel, preview validation, submit batch import, and export with column selection.',
    importButton: 'Batch import',
    exportButton: 'Export data',
    requiredColumnsHint: 'Required columns: {columns}',
    recordsTitle: 'Imported records',
    resultsTitle: 'Import result details',
    resultsHint: 'After import completes, click "Load details" to review row-level success/failure.',
    loadResults: 'Load details',
    importDialog: {
      title: 'Batch import',
      description: 'Choose a CSV or Excel file, review the preview, then submit.',
      dropHint: 'Drop a file here or choose one below',
      chooseFile: 'Choose file',
      previewTitle: 'Data preview',
      previewCount: 'Showing first {count} rows',
      progress: 'Import progress',
      missingColumns: 'Missing required columns',
      submit: 'Start import',
    },
    exportDialog: {
      title: 'Export data',
      description: 'Choose columns and export format.',
      columns: 'Columns',
      selectAll: 'Select all',
      deselectAll: 'Clear all',
      csv: 'Export CSV',
      xlsx: 'Export Excel',
    },
    resultTable: {
      rowNo: 'Row',
      status: 'Status',
      error: 'Error',
      success: 'Success',
      fail: 'Failed',
      empty: 'No results yet',
    },
  },
  operatorReportLogs: {
    filters: {
      templateName: 'Template name',
      actor: 'Printed by',
      allStatuses: 'All statuses',
    },
    columns: {
      template: 'Template',
      fileName: 'File name',
      status: 'Status',
      actor: 'Printed by',
      locale: 'Locale',
      duration: 'Duration',
      size: 'Size',
      actions: 'Actions',
    },
    status: {
      PENDING: 'Pending',
      SUCCEEDED: 'Succeeded',
      FAILED: 'Failed',
    },
    detail: {
      title: 'Report log details',
      dataSnapshot: 'Data snapshot',
      downloadReprint: 'Download reprinted report',
      reprintLoading: 'Reprinting report...',
      reprintUnavailable: 'Only succeeded logs with a complete data snapshot can be reprinted',
    },
    reprintConfirm: {
      title: 'Confirm reprint',
      description: 'The system will regenerate a PDF from this log data snapshot and preview it in a dialog.',
      submit: 'Reprint report',
    },
    reprintPreview: {
      title: 'Reprinted report preview',
      description: 'Navigate pages, zoom, download, or print. All pages are shown in one scrollable view.',
    },
    messages: {
      reprintStarted: 'Reprinted report generated',
    },
  },
  operatorEmailTemplates: {
    testEmail: {
      label: 'Test email',
      send: 'Send test email',
      hint: 'Email is sent asynchronously. Check Email Logs for rendered content and final status.',
      recipientRequired: 'Enter recipient email',
      queued: 'Added to send queue',
      queuedWithId: 'Added to send queue ({logId})',
      sendFailed: 'Failed to send',
    },
    actions: {
      newTemplate: 'New template',
      preview: 'Preview',
      edit: 'Edit',
      add: 'Add',
      delete: 'Delete',
      cancel: 'Cancel',
      save: 'Save',
    },
    dialog: {
      createTitle: 'New template',
      editTitle: 'Edit template',
      previewTitle: 'Template preview',
      formDescription: 'Configure basic information, body content, and placeholders.',
      previewDescription: 'Preview the rendered email output.',
    },
    tabs: {
      basic: 'Basic information',
      body: 'Body',
      placeholders: 'Placeholders',
    },
    themes: {
      success: 'Success',
      warning: 'Warning',
      error: 'Error',
      info: 'Info',
    },
    fields: {
      language: 'Language',
      name: 'Name',
      subject: 'Email subject',
      theme: 'Theme',
      headerTitle: 'Header title',
      headerSubtitle: 'Header subtitle',
      greeting: 'Greeting',
      introText: 'Intro text',
      noticeText: 'Notice text',
      bodyHtml: 'Body HTML',
      customPlaceholders: 'Custom placeholders',
      displayName: 'Display name',
      exampleValue: 'Example value',
    },
    placeholders: {
      greeting: '{tenantPlaceholder} Hello',
      customLabel: 'Order number',
      emptyCustom: 'No custom placeholders yet. Add one and pass the matching variables when sending.',
      requiredOnSend: 'Required when sending',
      tagList: 'Placeholders (click to copy, then paste into fields on the left)',
      livePreview: 'Live preview',
      previewLoading: 'Loading preview...',
      previewEmpty: 'Enter a title and subject to preview',
    },
    locales: {
      zh_CN: 'Chinese',
      ja_JP: 'Japanese',
      en_US: 'English',
    },
    previewExamples: {
      loginAccount: 'Login account',
      loginTime: 'Login time',
    },
    validation: {
      keyRequired: 'Template key is required',
      nameRequired: 'Template name is required',
      subjectRequired: 'Email subject is required',
      titleRequired: 'Header title is required',
      placeholderKeyRequired: 'Custom placeholder key is required',
      placeholderKeyInvalid: 'Invalid placeholder key format: {key}',
      placeholderKeyBuiltinConflict: 'Placeholder key conflicts with a built-in key: {key}',
      placeholderKeyDuplicate: 'Duplicate placeholder key: {key}',
    },
    messages: {
      updated: 'Template updated',
      created: 'Template created',
      saveFailed: 'Failed to save',
    },
  },
  operatorPlans: {
    actions: {
      newPlan: 'New quota plan',
    },
    dialog: {
      createTitle: 'New quota plan',
      editTitle: 'Edit quota plan',
      formDescription: 'Configure quota limits, plan type, and menu feature access.',
    },
    fields: {
      name: 'Plan name',
      description: 'Description',
      planType: 'Plan type',
      maxUnits: 'Maximum units',
      maxApiCalls: 'Maximum API calls',
      maxStorageMb: 'Maximum storage (MB)',
    },
    planTypes: {
      STANDARD: 'Standard plan',
      TRIAL: 'Trial plan',
    },
    statuses: {
      enabled: 'Enabled',
      disabled: 'Disabled',
    },
    card: {
      units: 'Units',
      apiCalls: 'API calls',
      storage: 'Storage',
      menus: 'Menus',
      tenants: 'Tenants',
      defaultAll: 'All by default',
    },
    empty: {
      noDescription: 'No description',
    },
    hints: {
      blankUnlimited: 'Quota fields left blank are unlimited.',
    },
    errors: {
      nameRequired: 'Enter a quota plan name.',
    },
    unlimited: 'Unlimited',
    delete: {
      title: 'Delete quota plan',
      description: 'Bound tenants will be detached from this plan after deletion.',
    },
    messages: {
      saved: 'Quota plan saved',
      created: 'Quota plan created',
      saveFailed: 'Failed to save quota plan',
      deleted: 'Quota plan deleted',
      deleteFailed: 'Failed to delete quota plan',
    },
  },
  operatorTenantHealth: {
    title: 'Health',
    description: 'Tenant health overview: usage, subscription, dunning, feedback, and outbox status.',
    evaluatedAt: 'Evaluated at',
    statuses: {
      healthy: 'Healthy',
      warning: 'Warning',
      critical: 'Critical',
      unknown: 'Unknown',
    },
    alerts: {
      title: 'Alerts',
      none: 'No alerts',
    },
    subscription: {
      title: 'Subscription',
      status: 'Status',
      plan: 'Plan',
      periodEnd: 'Period end',
    },
    usage: {
      title: 'Usage',
      units: 'Units',
      apiCalls: 'API calls',
      storageMb: 'Storage (MB)',
    },
    dunning: {
      title: 'Dunning',
    },
    feedback: {
      unread: 'Unread feedback',
    },
    outbox: {
      dead: 'Dead messages',
    },
  },
  operatorTenants: {
    filters: {
      searchPlaceholder: 'Search tenant name or slug',
      allStatuses: 'All statuses',
    },
    columns: {
      tenant: 'Tenant',
      status: 'Status',
      quotaPlan: 'Quota plan',
      actions: 'Actions',
      role: 'Role',
    },
    statuses: {
      ACTIVE: 'Active',
      SUSPENDED: 'Suspended',
      DELETED: 'Deleted',
    },
    userStatuses: {
      ACTIVE: 'Active',
      INACTIVE: 'Inactive',
      DELETED: 'Deleted',
    },
    quotaPlan: {
      unset: 'Not set',
    },
    actions: {
      openTenant: 'Open tenant',
      accounts: 'Accounts',
      features: 'Features',
      impersonate: 'Act as tenant',
      open: 'Open',
      addAccount: 'Add account',
      resetPassword: 'Reset password',
      suspendAccount: 'Suspend account',
      activateAccount: 'Activate account',
    },
    impersonate: {
      failed: 'Failed to start tenant impersonation',
      success: 'Now acting as tenant admin',
      inactiveTenant: 'Only active tenants can be impersonated',
    },
    dialog: {
      openTenant: 'Open tenant',
      editTenant: 'Edit tenant',
      openTenantDescription: 'Enter tenant details and create the first admin account.',
      editTenantDescription: 'Update tenant name, status, quota plan, and branding settings.',
      features: 'Feature switches',
      featuresFor: '{name} · Feature switches',
      featuresDescription: 'Configure menus and feature switches available to the tenant.',
      accounts: 'Account management',
      accountsFor: '{name} · Account management',
      accountsDescription: 'Manage tenant member accounts and add new accounts.',
    },
    fields: {
      tenantName: 'Tenant name',
      tenantSlug: 'Tenant slug',
      adminEmail: 'Admin email',
      adminPassword: 'Admin password',
      email: 'Email',
      name: 'Name',
      initialPassword: 'Initial password',
      newPassword: 'New password',
      logo: 'Sidebar logo',
    },
    logo: {
      upload: 'Upload logo',
      uploading: 'Uploading...',
      reset: 'Reset to default',
      cropTitle: 'Crop logo',
      confirmCrop: 'Apply crop',
      zoom: 'Zoom',
      hint: 'PNG, JPEG, WebP, or SVG up to 2MB. Raster images are cropped to a square; SVG files are uploaded as-is.',
      saveTenantFirst: 'Save the tenant first before uploading a logo.',
      invalidType: 'Only PNG, JPEG, WebP, and SVG formats are supported',
      tooLarge: 'Image must be 2MB or smaller',
      invalidImage: 'Unable to read this image. Check that the file is valid and not corrupted.',
      emptyFile: 'The selected file is empty. Choose a valid image.',
      uploadFailed: 'Failed to upload logo',
      uploaded: 'Logo uploaded. Save the tenant to apply.',
    },
    errors: {
      nameRequired: 'Tenant name is required',
      slugRequired: 'Tenant slug is required',
      adminEmailRequired: 'Admin email is required',
      adminPasswordRequired: 'Admin password is required',
      emailRequired: 'Email is required',
      userNameRequired: 'Name is required',
      initialPasswordRequired: 'Initial password is required',
      roleRequired: 'Role is required',
      passwordMinLength: 'Password must be at least 8 characters',
    },
    messages: {
      tenantSaved: 'Tenant saved',
      tenantOpened: 'Tenant opened',
      tenantSaveFailed: 'Failed to save tenant',
      featuresUpdated: 'Tenant features updated',
      featuresSaveFailed: 'Failed to save feature configuration',
      accountCreated: 'Account created',
      accountCreateFailed: 'Failed to create account',
      passwordReset: 'Password reset',
      passwordResetFailed: 'Failed to reset password',
      accountSuspended: 'Account suspended',
      accountActivated: 'Account activated',
      accountStatusFailed: 'Failed to update account status',
    },
    confirm: {
      suspendAccountTitle: 'Suspend this account?',
      suspendAccountDescription: '{name} will no longer be able to sign in.',
    },
  },
  operatorFeatureFlags: {
    filters: {
      includeArchived: 'Include archived',
      environmentAll: 'All environments',
      environmentUnrestricted: 'No restriction',
      statusAll: 'All statuses',
    },
    columns: {
      key: 'Key',
      name: 'Name',
      status: 'Status',
      defaultValue: 'Default',
      rollout: 'Rollout',
      environments: 'Environments',
      overrides: 'Overrides',
      actions: 'Actions',
    },
    overrideColumns: {
      scopeType: 'Scope',
      scopeId: 'Scope ID',
      value: 'Value',
      reason: 'Reason',
      actions: 'Actions',
    },
    actions: {
      create: 'Create Feature Flag',
      overrides: 'Manage overrides',
      archive: 'Archive',
      evaluate: 'Evaluate',
      runEvaluate: 'Run evaluation',
      evaluating: 'Evaluating…',
    },
    evaluate: {
      title: 'Evaluation debug',
      description: 'Simulate feature-flag resolution for a tenant/user and environment without changing config.',
      loadHint: 'Fill in parameters and click “Run evaluation” (not requested on page load).',
      reason: 'Reason',
      rolloutBucket: 'Rollout bucket',
      flagId: 'Flag ID',
    },
    evaluateReasons: {
      not_found: 'Not found',
      archived: 'Archived',
      disabled: 'Disabled',
      environment: 'Environment mismatch',
      user_override: 'User override',
      tenant_override: 'Tenant override',
      default: 'Default value',
      rollout: 'Percentage rollout',
    },
    dialog: {
      createTitle: 'Create feature flag',
      createDescription:
        'Configure rollout, default behavior, and environment limits. Use Japanese for name and description by default.',
      editTitle: 'Edit feature flag',
      editDescription: 'Update runtime behavior without changing tenant menu permissions.',
    },
    overrideDialog: {
      title: 'Overrides · {key}',
      description: 'Set tenant- or user-specific behavior for this feature flag.',
    },
    fields: {
      key: 'Flag key',
      name: 'Name',
      namePlaceholder: 'e.g. 銀行証憑リンク',
      nameLocaleHint: 'Name and description should be written in Japanese by default.',
      description: 'Description',
      descriptionPlaceholder: 'e.g. 銀行明細と証憑候補の紐付け・確認を有効化する。',
      rolloutPercentage: 'Rollout percentage (0-100)',
      environments: 'Environment allowlist',
      environmentsPlaceholder: 'e.g. production, staging',
      isEnabled: 'Enable this flag',
      defaultValue: 'Default value is enabled',
      scopeType: 'Scope type',
      scopeId: 'Scope ID (UUID)',
      scopeIdPlaceholder: 'Enter tenant or user UUID',
      overrideEnabled: 'Override value is enabled',
      overrideReason: 'Override reason',
      evaluateEnvironment: 'Environment',
      evaluateEnvironmentPlaceholder: 'e.g. production',
      evaluateTenantId: 'Tenant ID (optional)',
      evaluateUserId: 'User ID (optional)',
    },
    scope: {
      tenant: 'Tenant',
      user: 'User',
    },
    status: {
      enabled: 'Enabled',
      disabled: 'Disabled',
      archived: 'Archived',
    },
    empty: 'No feature flags yet',
    emptyDescription: 'Create the first runtime switch to manage rollout, defaults, and overrides here.',
    overrideEmpty: 'No overrides yet',
    errors: {
      keyRequired: 'Flag key is required',
      nameRequired: 'Name is required',
      rolloutRange: 'Rollout percentage must be between 0 and 100',
      scopeIdRequired: 'Scope ID is required',
    },
    confirm: {
      archiveTitle: 'Archive feature flag "{key}"?',
      archiveDescription: 'Archived flags are excluded from evaluation, but history is retained.',
    },
    messages: {
      loadFailed: 'Unable to load feature flags',
      loadFailedDescription: 'Check your session or network connection, then try again.',
      created: 'Feature flag created',
      updated: 'Feature flag updated',
      saveFailed: 'Failed to save feature flag',
      archived: 'Feature flag archived',
      archiveFailed: 'Failed to archive feature flag',
      overrideSaved: 'Override saved',
      overrideSaveFailed: 'Failed to save override',
      overrideCleared: 'Override cleared',
      overrideClearFailed: 'Failed to clear override',
      evaluateFailed: 'Failed to evaluate feature flag',
    },
  },
  operatorOverview: {
    header: {
      thisMonth: 'This month {range}',
      rangeSeparator: 'to',
    },
    metrics: {
      tenantTotal: 'Total tenants',
      tenantHelper: '{active} active, {suspended} suspended',
      monthlyUnits: 'Monthly units',
      unitsHelper: '{total} units in total',
      monthlyApiCalls: 'Monthly API calls',
      apiCallsHelper: 'Storage snapshot {storage}',
      monthlyCost: 'Estimated cost',
      costHelper: 'Avg {average} / AI request · Gemini token rates',
      documents: 'Documents',
      documentsHelper: 'Created this month (UTC)',
      aiRequests: 'AI requests',
      aiRequestsHelper: 'ai_call_log this month (UTC)',
      tokens: 'Tokens',
      tokensHelper: 'In {input} / Out {output}',
      feedbackHelper: '{unread} unread, {month} this month',
    },
    health: {
      title: 'Platform health',
      description: 'Tenant status, API delivery, and email sending',
      activeTenantRate: 'Active tenant rate',
      activeTenantDetail: '{active} / {total} tenants active',
      apiUsage: 'API usage',
      emailSending: 'Email sending',
      recentFailures: '{count} recent failures',
      loadHint: 'Load recent risks to inspect delivery failures',
    },
    feedback: {
      title: 'User feedback',
      summary: '{open} pending · {unread} unread',
      total: 'Total feedback',
      monthlyNew: 'New this month',
      open: 'Pending',
      noUnread: 'No unread feedback',
    },
    quick: {
      title: 'Quick entries',
      description: 'Common operations',
      openTenant: 'Open tenant',
      userFeedback: 'User feedback',
      configurePlans: 'Configure plans',
      viewUsage: 'View usage',
      auditLogs: 'Audit logs',
      emailTemplates: 'Email templates',
      webhookLogs: 'Webhook logs',
    },
    usage: {
      title: 'Monthly usage ranking',
      description: 'Tenant usage sorted by cost',
      empty: 'No usage this month',
    },
    risks: {
      title: 'Recent risks',
      description: 'Webhook and email exceptions',
      itemCount: '{count} items',
      empty: 'No recent exception logs',
      loadHint: 'Recent Webhook and email exceptions load only after an explicit action.',
      webhookType: 'Webhook',
      emailType: 'Email',
      emailFailed: 'Send failed',
    },
    focus: {
      title: 'Operational focus',
      description: 'Metrics that need priority attention',
      planCoverage: 'Plan coverage',
      planCoverageDetail: '{covered} / {total} tenants have a plan assigned',
      costConcentration: 'Cost concentration',
      costConcentrationDetail: '{tenant} accounts for {percent} of monthly cost',
      noCostRecords: 'No cost records',
      feedbackDetail: '{unread} unread, {open} pending',
      incidentHandling: 'Incident handling',
      incidentDetail: '{count} recent exceptions need investigation',
      incidentPendingDetail: 'Load recent risks before evaluating exception handling.',
    },
    units: {
      apiCallsWithCount: '{count} API calls',
      unitsWithCount: '{count} units',
      documentsWithCount: '{count} documents',
      tokensWithCount: '{count} tokens',
    },
    badges: {
      normal: 'Normal',
      watch: 'Watch',
      pending: 'Pending',
      incomplete: 'Incomplete',
      notLoaded: 'Not loaded',
    },
    actions: {
      loadRisks: 'Load recent risks',
    },
    fallback: {
      unknownTenant: 'Unknown tenant',
      event: 'Event',
      noResponse: 'No response',
      platform: 'Platform',
      recipient: 'Recipient',
    },
    messages: {
      loadFailed: 'Failed to load operations overview',
    },
  },
  operatorOps: {
    header: {
      generatedAt: 'Generated at {time}',
    },
    processes: {
      api: 'API process',
      worker: 'Worker process',
      scheduler: 'Scheduler process',
      lastBeat: 'Last heartbeat {time}',
      noHeartbeat: 'No heartbeat',
    },
    dependencies: {
      title: 'Dependencies',
      description: 'Database, cache, object storage, and queue connectivity (no secrets)',
      mysql: 'MySQL',
      redis: 'Redis',
      objectStorage: 'Object storage',
      queue: 'Job queue',
      latency: 'Latency {ms} ms',
      provider: 'Provider {provider}',
    },
    queues: {
      title: 'Queue backlog',
      description: 'Waiting, active, and failed jobs per queue',
      waiting: 'Waiting {count}',
      active: 'Active {count}',
      failed: 'Failed {count}',
      empty: 'No queue data',
    },
    outbox: {
      title: 'Outbox status',
      description: 'Async message delivery backlog',
      pending: 'Pending',
      processing: 'Processing',
      completed: 'Completed',
      dead: 'Dead',
    },
    failedJobs: {
      title: 'Failed jobs',
      description: 'Recent failed jobs across queues',
      empty: 'No failed jobs',
    },
    recentErrors: {
      title: 'Recent errors',
      description: 'HTTP error summary from the last 24 hours',
      empty: 'No recent errors',
    },
    slowEndpoints: {
      title: 'Slow endpoints',
      description: 'API key routes with high DB time or query count',
      summary: '{count} requests · avg DB {avgDb} ms · avg queries {avgQueries}',
      empty: 'No slow endpoints',
    },
    highErrorEndpoints: {
      title: 'High error-rate endpoints',
      description: 'API key routes with error rate ≥ 10% in the last 24 hours',
      summary: '{count} requests · {errors} errors · rate {rate}',
      empty: 'No high error-rate endpoints',
    },
    statuses: {
      up: 'Up',
      down: 'Down',
      unknown: 'Unknown',
      ready: 'Ready',
      not_ready: 'Not ready',
      ok: 'OK',
    },
    messages: {
      loadFailed: 'Failed to load ops dashboard',
    },
  },
  operatorBackups: {
    backups: {
      title: 'Encrypted MySQL backups',
    },
    deletions: {
      title: 'Data deletion requests',
    },
    columns: {
      time: 'Time',
      status: 'Status',
      size: 'Size',
      backupFile: 'Backup file',
      checksum: 'Checksum',
      actions: 'Actions',
      resource: 'Resource',
      tenant: 'Tenant',
      executionTime: 'Execution time',
      step: 'Step',
    },
    actions: {
      runBackup: 'Run backup now',
      download: 'Download',
      encryptedVersion: 'Encrypted version (.age)',
      decryptedVersion: 'Decrypted version (.tar.gz)',
      restoreDatabase: 'Restore database',
      restoreDrill: 'Restore drill',
      confirmRestore: 'Confirm restore',
      restore: 'Restore',
      deleteNow: 'Delete now',
      retry: 'Retry',
    },
    backupStatuses: {
      PENDING: 'Pending',
      RUNNING: 'Running',
      COMPLETED: 'Completed',
      FAILED: 'Failed',
    },
    deletionStatuses: {
      PENDING: 'Pending deletion',
      CANCELED: 'Restored',
      PROCESSING: 'Deleting',
      COMPLETED: 'Deleted',
      FAILED: 'Failed',
    },
    hints: {
      requiresAgeIdentityFile: 'Configure AGE_IDENTITY_FILE in backend/.env',
      requiresAgeIdentity: 'AGE_IDENTITY_FILE is required',
      drillSafe: 'Validate the backup in an isolated container without modifying production data',
    },
    empty: {
      noBackups: 'No backup records',
      noDeletionRequests: 'No deletion requests',
    },
    fallback: {
      thisBackup: 'this backup',
    },
    confirm: {
      restoreTitle: 'Restore database',
      restoreDescription: 'This will overwrite the current MySQL database with the backup and cannot be undone.\n\nBackup file: {fileName}',
      deleteBackupTitle: 'Delete backup',
      deleteBackupDescription: 'Delete this backup file?\n{fileName}',
      restoreDeletionTitle: 'Restore deletion request',
      restoreDeletionDescription: 'This will cancel the deletion request. Related resources will not be deleted.',
      executeDeletionTitle: 'Delete now',
      executeDeletionDescription: 'This skips the waiting period and deletes data immediately. This action cannot be undone.',
    },
    messages: {
      backupCompleted: 'Backup completed',
      backupFailed: 'Backup failed',
      drillCompleted: 'Restore drill completed. Production database was not modified.',
      drillFailed: 'Restore drill failed',
      databaseRestored: 'Database restored to the DATABASE_URL configured in .env. Refresh the page.',
      databaseRestoreFailed: 'Database restore failed',
      missingAgeIdentity: 'AGE_IDENTITY_FILE is not configured, so decrypted downloads are unavailable',
      encryptedDownloadStarted: 'Encrypted backup download started',
      decryptedDownloadStarted: 'Decrypted backup download started',
      downloadFailed: 'Download failed',
      backupDeleted: 'Backup deleted',
      deleteFailed: 'Delete failed',
      deletionRestored: 'Deletion request restored',
      restoreFailed: 'Restore failed',
      deletionSubmitted: 'Deletion job submitted',
      submitFailed: 'Submit failed',
      deletionRetried: 'Deletion job retried',
      retryFailed: 'Retry failed',
    },
  },
  operatorAi: {
    metrics: {
      providers: 'Providers',
      models: 'Models',
      prompts: 'Prompts',
    },
    providers: {
      title: 'Provider Configuration',
      description: 'Manage model providers, API keys, and connection health.',
    },
    models: {
      title: 'Model Profiles',
      description: 'Define default models, capabilities, and request parameters.',
    },
    prompts: {
      title: 'Prompt Templates',
      description: 'Manage business prompts, variables, and published versions by stable key.',
    },
    logs: {
      title: 'Call Logs',
      description: 'Track actual provider, model, tokens, latency, and failure summaries.',
      loadHint: 'Call logs are loaded only after an explicit action to keep first paint light.',
    },
    fields: {
      name: 'Name',
      providerType: 'Provider type',
      baseUrl: 'Base URL',
      apiKey: 'API Key',
      enabled: 'Enabled',
      provider: 'Provider',
      model: 'Model',
      capability: 'Capability',
      temperature: 'Temperature',
      defaultModel: 'Default model',
      promptKey: 'Prompt key',
      purpose: 'Purpose',
      systemPrompt: 'System prompt',
      userPrompt: 'User prompt',
      variablesSchema: 'Variables schema',
      outputSchema: 'Output schema',
      status: 'Status',
      tokens: 'Tokens',
      duration: 'Duration',
    },
    hints: {
      apiKey: 'The plaintext key is never shown again after saving; lists show only a masked suffix.',
    },
    providerTypes: {
      OPENAI_COMPATIBLE: 'OpenAI-compatible',
      ANTHROPIC_COMPATIBLE: 'Anthropic-compatible',
      PRIVATE_GATEWAY: 'Private gateway',
    },
    capabilities: {
      TEXT: 'Text',
      JSON: 'JSON',
      EMBEDDING: 'Embedding',
    },
    healthStatuses: {
      UNKNOWN: 'Unknown',
      HEALTHY: 'Healthy',
      UNHEALTHY: 'Unhealthy',
    },
    promptVersionStatuses: {
      DRAFT: 'Draft',
      PUBLISHED: 'Published',
      ARCHIVED: 'Archived',
    },
    callStatuses: {
      SUCCESS: 'Success',
      FAILED: 'Failed',
    },
    placeholders: {
      selectProvider: 'Select provider',
    },
    actions: {
      test: 'Test connection',
      publish: 'Publish',
      loadLogs: 'Load call logs',
    },
    status: {
      default: 'Default',
      available: 'Available',
      published: 'Published',
      draft: 'Draft',
    },
    empty: {
      providers: 'No providers yet',
      models: 'No models yet',
      prompts: 'No prompts yet',
      logs: 'No call logs',
      noApiKey: 'No API key configured',
      noPurpose: 'No purpose',
    },
    messages: {
      providerCreated: 'Provider created',
      providerSaved: 'Provider saved',
      providerHealthy: 'Provider is reachable',
      providerUnhealthy: 'Provider is unreachable',
      modelCreated: 'Model created',
      modelSaved: 'Model saved',
      promptCreated: 'Prompt created',
      promptSaved: 'Prompt saved',
      promptPublished: 'Prompt published',
      saveFailed: 'Save failed',
    },
  },
  operatorSystemSettings: {
    title: 'Platform settings',
    description: 'Global operations configuration. Changes take effect immediately after saving.',
    fields: {
      feedbackNotifyEmail: 'Feedback notification emails',
      auditLogRetentionDays: 'Audit log retention days',
      apiRequestLogRetentionDays: 'API request log retention days',
      reportLogRetentionDays: 'Report log retention days',
      batchAuditLogRetentionDays: 'Batch audit log retention days',
      webhookLogRetentionDays: 'Webhook delivery log retention days',
      emailLogRetentionDays: 'Email log retention days',
      aiCallLogRetentionDays: 'AI call log retention days',
      logRetentionArchiveEnabled: 'Archive to object storage before deletion',
    },
    logRetention: {
      title: 'Log retention policy',
      description: 'Configure retention for all platform log tables. Cleanup runs daily at 04:00 on scheduler instances.',
    },
    hints: {
      feedbackNotifyEmail: 'New feedback notifications will be sent to these addresses. Use one address per line, or separate addresses with commas. Leave blank to disable notifications.',
      auditLogRetentionDays: 'AuditLog rows older than this are removed. Default is 365 days.',
      apiRequestLogRetentionDays: 'ApiRequestLog rows older than this are removed. Default is 90 days.',
      reportLogRetentionDays: 'ReportLog rows older than this are removed. Default is 180 days.',
      batchAuditLogRetentionDays: 'BatchAuditLog rows older than this are removed. Default is 180 days.',
      webhookLogRetentionDays: 'WebhookLog rows older than this are removed. Default is 90 days.',
      emailLogRetentionDays: 'EmailLog rows older than this are removed. Default is 180 days.',
      aiCallLogRetentionDays: 'AiCallLog rows older than this are removed. Default is 90 days.',
      logRetentionArchiveEnabled: 'When enabled, batches are gzip-compressed to NDJSON and uploaded to object storage before deletion (requires S3).',
    },
    lastUpdated: 'Last updated: {time}',
    saving: 'Saving...',
    save: 'Save settings',
    validation: {
      retentionDaysRange: 'Enter a value between 1 and 3650',
    },
    messages: {
      saved: 'Platform settings saved',
      saveFailed: 'Failed to save',
    },
    lineChannelBinding: {
      title: 'LINE platform bot (line_channel_binding)',
      description: 'Idempotently register the shared LINE bot binding from server environment variables for webhook verification and routing. Secrets and tokens are never shown or stored in the browser.',
      statusActive: 'Registered',
      statusMissing: 'Not registered',
      webhookUrl: 'Webhook URL',
      envReady: 'Configured',
      envMissing: 'Missing',
      bindingId: 'Binding ID: {value}',
      destination: 'Destination (masked): {value}',
      channelId: 'Channel ID: {value}',
      lastUpdated: 'Last updated: {time}',
      bootstrap: 'Register / update LINE bot binding',
      bootstrapping: 'Writing...',
      loadFailed: 'Failed to load LINE binding status',
      messages: {
        created: 'LINE bot binding created',
        updated: 'LINE bot binding updated (idempotent)',
        failed: 'Failed to write LINE bot binding',
      },
    },
  },
  operatorBilling: {
    subscriptions: {
      title: 'Tenant subscriptions',
      cancelAtPeriodEnd: 'Cancel at period end',
    },
    invoices: {
      title: 'Invoices',
    },
    planVersions: {
      title: 'Published plan versions',
      month: 'month',
    },
    columns: {
      tenant: 'Tenant',
      planVersion: 'Plan version',
      status: 'Status',
      periodEnd: 'Period end',
      actions: 'Actions',
      amountPaid: 'Amount paid',
    },
    actions: {
      generateInvoice: 'Generate invoice',
      resume: 'Resume',
      suspend: 'Suspend',
      cancelAtPeriodEnd: 'Cancel at period end',
      startCheckout: 'Start Stripe checkout',
    },
    subscriptionStatuses: {
      TRIALING: 'Trialing',
      ACTIVE: 'Active',
      PAST_DUE: 'Past due',
      SUSPENDED: 'Suspended',
      CANCELED: 'Canceled',
    },
    invoiceStatuses: {
      DRAFT: 'Draft',
      OPEN: 'Open',
      PAID: 'Paid',
      OVERDUE: 'Overdue',
      VOID: 'Void',
    },
    messages: {
      invoiceCreated: 'Invoice generated',
      invoiceCreateFailed: 'Failed to generate invoice',
      resume: 'Subscription resumed',
      "suspend-now": 'Subscription suspended',
      "cancel-at-period-end": 'Cancellation at period end has been set',
      statusUpdateFailed: 'Failed to update subscription status',
      checkoutCreated: 'Stripe Checkout opened',
      checkoutFailed: 'Failed to start Stripe checkout',
    },
  },
  operatorBillingEnhancements: {
    common: {
      tenantId: 'Tenant ID',
      tenantIdPlaceholder: 'Enter tenant UUID',
      loadContext: 'Load subscription context',
      openBilling: 'Open billing page',
      optional: 'Optional',
      selectSubscription: 'Select subscription',
      selectPlanVersion: 'Select target plan version',
    },
    coupon: {
      title: 'Coupon validate / redeem',
      description: 'Validate coupon discounts for a tenant, then redeem for trial or invoice relief.',
      redeemBoundary:
        'Boundary: redemption only records the coupon use. Immediate price or invoice changes depend on later billing/payment steps and do not auto-adjust subscription unit price.',
      code: 'Coupon code',
      amountMinor: 'Amount (minor units)',
      currency: 'Currency',
      subscriptionId: 'Subscription ID (optional for redeem)',
      validate: 'Validate',
      redeem: 'Redeem',
    },
    simulation: {
      title: 'Plan change simulation',
      description: 'Preview proration amounts. Persistence stores a simulation only and does not mutate the subscription.',
      subscriptionId: 'Subscription',
      targetPlanVersionId: 'Target plan version',
      effectiveAt: 'Effective at (optional)',
      persist: 'Persist simulation record',
      run: 'Run simulation',
      changeType: 'Change type',
      prorationCharge: 'Proration charge (minor)',
      prorationCredit: 'Proration credit (minor)',
      netAmount: 'Net amount (minor)',
    },
    dunning: {
      title: 'Dunning status',
      description:
        'Evaluate overdue invoices and dunning stage, linked to OPEN/OVERDUE invoices and PAST_DUE subscriptions.',
      refresh: 'Refresh status',
      empty: 'No subscription or evaluable dunning state for this tenant.',
      status: 'Status',
      daysPastDue: 'Days past due',
      overdueInvoice: 'Linked overdue invoice',
      nextActionAt: 'Next action at',
      pastDueSubscriptions: 'PAST_DUE subscriptions',
      openBillingToResume: 'Open billing page to resume subscription manually',
      collectableInvoices: 'Collectable invoices (OPEN / OVERDUE)',
      noCollectableInvoices: 'No OPEN/OVERDUE invoices with a due balance.',
      invoiceNumber: 'Invoice number',
      invoiceStatus: 'Status',
      amountDue: 'Amount due (minor)',
      actions: 'Actions',
      collectPayment: 'Collect payment',
      linkedToDunning: 'Dunning link',
      statuses: {
        NONE: 'None',
        REMINDER_1: 'Reminder 1',
        REMINDER_2: 'Reminder 2',
        FINAL_NOTICE: 'Final notice',
        ESCALATED: 'Escalated',
      },
    },
    messages: {
      tenantRequired: 'Enter a tenant ID first',
      contextLoaded:
        'Loaded {subscriptions} subscriptions ({pastDue} PAST_DUE), {plans} plan versions, {invoices} invoices ({openOrOverdue} collectable)',
      contextLoadFailed: 'Failed to load subscription context',
      couponCodeRequired: 'Enter a coupon code',
      couponValidated: 'Coupon validated',
      couponValidateFailed: 'Coupon validation failed',
      couponRedeemed:
        'Coupon redemption recorded (no automatic price change; depends on later billing/payment)',
      couponRedeemFailed: 'Coupon redemption failed',
      simulationRequired: 'Select a subscription and target plan version',
      simulationDone: 'Plan change simulation completed',
      simulationFailed: 'Plan change simulation failed',
      dunningFailed: 'Failed to load dunning status',
      checkoutCreated: 'Stripe Checkout opened',
      checkoutFailed: 'Failed to start payment collection',
    },
  },
  operatorTrialApplications: {
    filters: {
      searchPlaceholder: 'Search organization, contact, or email',
      allStatuses: 'All statuses',
    },
    columns: {
      organization: 'Organization',
      contact: 'Contact',
      expectedUnits: 'Expected monthly units',
      status: 'Status',
      submittedAt: 'Submitted at',
      actions: 'Actions',
    },
    actions: {
      details: 'Details',
      approve: 'Approve',
      reject: 'Reject',
    },
    empty: 'No trial applications',
    statuses: {
      PENDING: 'Pending review',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      CANCELED: 'Canceled',
    },
    detail: {
      title: 'Trial application details',
      description: 'Review the organization and contact information submitted by the applicant.',
      organization: 'Organization',
      contact: 'Contact',
      email: 'Email',
      phone: 'Phone',
      useCase: 'Use case',
      notes: 'Notes',
    },
    approve: {
      title: 'Approve trial application',
      description: 'Select a trial plan and end date, then activate the trial tenant.',
      trialPlan: 'Trial plan',
      selectPlaceholder: 'Select',
      trialEndsAt: 'Trial end date',
      tenantSlug: 'Tenant slug (optional)',
      tenantSlugPlaceholder: 'Generate automatically from organization name',
      confirm: 'Approve and activate',
    },
    reject: {
      title: 'Reject trial application',
      description: 'Provide a rejection reason. The applicant will be notified by email.',
      reason: 'Rejection reason',
      confirm: 'Confirm rejection',
    },
    messages: {
      approveSuccess: 'Trial tenant activated and invitation email sent',
      approveFailed: 'Failed to activate trial tenant',
      trialPlanRequired: 'Select a trial plan',
      trialEndsAtRequired: 'Select a trial end date',
      rejectReasonRequired: 'Enter the rejection reason',
      rejectSuccess: 'Application rejected and notification sent',
      rejectFailed: 'Operation failed',
    },
  },
  dashboardPage: {
    title: 'Dashboard',
    noWorkspaceTitle: 'No workspace selected',
    noWorkspaceDescription: 'Select a workspace before starting work.',
    openWorkspaces: 'Open workspaces',
    viewBilling: 'View billing',
    kpis: {
      pendingDocumentReview: 'Documents awaiting review',
      pendingDocumentReviewHelper: 'Intake items that need confirmation',
      unlinkedBankEvidence: 'Unlinked bank lines',
      unlinkedBankEvidenceHelper: 'Withdrawals not yet linked to documents',
      pendingJournalReview: 'Journals awaiting review',
      pendingJournalReviewHelper: 'Open items in the review inbox',
      confirmedDocumentsThisMonth: 'Confirmed this month',
      confirmedDocumentsThisMonthHelper: 'Documents approved this month (JST)',
    },
    actions: {
      title: 'Needs attention',
      description: 'Work waiting for you now',
      empty: 'Nothing needs attention right now.',
      pendingDocumentReview: 'Review pending documents',
      unlinkedBankEvidence: 'Link bank lines',
      pendingJournalReview: 'Review journals',
    },
    recentImports: {
      title: 'Recent bank imports',
      description: 'Latest import batch status',
      empty: 'No recent imports.',
    },
    platform: {
      title: 'Tenant settings',
      description: 'Billing, members, and integrations',
      manageMembers: 'Manage members',
      manageWebhooks: 'Manage Webhooks',
    },
    errors: {
      loadFailed: 'Failed to load dashboard.',
    },
  },
  webhooksPage: {
    createWebhook: 'Create webhook',
    createFirstWebhook: 'Create first webhook',
    empty: 'No webhooks yet',
    enabled: 'Enabled',
    disabled: 'Disabled',
    test: 'Test',
    logs: 'Logs',
    logsTitle: 'Webhook logs',
    logsDescription: 'Review delivery history for this webhook and retry failed requests.',
    editTitle: 'Edit webhook',
    createTitle: 'Create webhook',
    createDescription: 'Configure the webhook name, callback URL, trigger events, and custom headers.',
    editDescription: 'Update webhook settings. Leave Secret blank to keep the existing value.',
    name: 'Name',
    callbackUrl: 'Callback URL',
    secretPlaceholder: 'Leave blank to generate one automatically; leave blank while editing to keep the existing value',
    triggerEvents: 'Trigger events',
    customHeaders: 'Custom headers (JSON)',
    enableWebhook: 'Enable webhook',
    saving: 'Saving...',
    event: 'Event',
    eventsColumn: 'Events',
    status: 'Status',
    time: 'Time',
    actions: 'Actions',
    retry: 'Retry',
    details: 'Details',
    noLogs: 'No logs yet',
    detailTitle: 'Webhook details',
    detailDescription: 'Review webhook configuration and trigger events.',
    testResultTitle: 'Webhook test result',
    testResultDescription: 'Review the response status and payload from the test request.',
    logDetailTitle: 'Webhook log details',
    logDetailDescription: 'Review request and response details for a single delivery.',
    saved: 'Webhook saved',
    created: 'Webhook created',
    saveFailed: 'Failed to save webhook',
    deleteTitle: 'Delete webhook',
    deleteDescription: 'Delete this webhook?',
    deleted: 'Webhook deleted',
    deleteFailed: 'Failed to delete webhook',
    testSuccess: 'Test succeeded. Check whether your server received the request.',
    testFailed: 'Test failed: {error}',
    unknownError: 'Unknown error',
    testRequestFailed: 'Test request failed',
    retrySuccess: 'Webhook retry queued',
    retryFailed: 'Retry failed',
    events: {
      resource_created: 'Resource created',
      resource_updated: 'Resource updated',
      resource_deleted: 'Resource deleted',
      resource_failed: 'Resource failed',
    },
    errors: {
      nameRequired: 'Enter a webhook name.',
      urlRequired: 'Enter a valid callback URL.',
      selectEvent: 'Select at least one trigger event',
      headersJson: 'Headers must be valid JSON',
      headersObject: 'Headers must be a JSON object',
      headerValueString: 'Header {key} must be a string',
    },
  },
  billingPage: {
    currentSubscription: 'Current subscription',
    loading: 'Loading...',
    plan: 'Plan',
    monthlyFee: 'Monthly fee',
    trialEndsAt: 'Trial ends',
    currentPeriod: 'Current period',
    planDetails: 'Plan details',
    noPlanDescription: 'No plan description.',
    includedFeatures: 'Included features',
    addOnFeatures: 'Add-on features',
    noItems: 'None',
    quotaLimits: 'Quota limits',
    usage: 'Usage',
    invoices: 'Invoices',
    invoiceNumber: 'Invoice number',
    period: 'Period',
    amount: 'Amount',
    status: 'Status',
    noInvoices: 'No invoices yet',
    unlimited: 'Unlimited',
    accumulated: 'Total',
    current: 'Current',
    statuses: {
      TRIALING: 'Trialing',
      ACTIVE: 'Active',
      PAST_DUE: 'Past due',
      SUSPENDED: 'Suspended',
      CANCELED: 'Canceled',
    },
    invoiceStatuses: {
      DRAFT: 'Draft',
      OPEN: 'Open',
      PAID: 'Paid',
      OVERDUE: 'Overdue',
      VOID: 'Void',
    },
    quota: {
      units: 'Resource units',
      apiCalls: 'API calls',
      storage: 'Storage',
      unitsUnit: 'units',
      apiCallsUnit: 'calls',
      storageUnit: 'MB',
    },
  },
  notificationsPage: {
    title: 'Notifications',
    markAllRead: 'Mark all read',
    markRead: 'Mark read',
    archive: 'Archive',
    openAction: 'Open',
    loading: 'Loading...',
    empty: 'No notifications yet',
    unread: 'Unread',
    previousPage: 'Previous',
    nextPage: 'Next',
    filters: {
      all: 'All',
      unread: 'Unread',
      read: 'Read',
    },
    severity: {
      INFO: 'Info',
      SUCCESS: 'Success',
      WARNING: 'Warning',
      ERROR: 'Error',
    },
  },
  feedbackPage: {
    title: 'My feedback',
    newFeedback: 'New feedback',
    allStatuses: 'All statuses',
    loading: 'Loading...',
    empty: 'No feedback yet',
    unread: 'Unread',
    previousPage: 'Previous',
    nextPage: 'Next',
    createdAt: 'Created at {time} · {status}',
    closeFeedback: 'Close feedback',
    selectHint: 'Select a feedback item to view details',
    followUpPlaceholder: 'Add a follow-up...',
    sending: 'Sending...',
    sendFollowUp: 'Send follow-up',
    loadDetailFailed: 'Failed to load details',
    closeConfirmTitle: 'Close feedback',
    closeConfirmDescription: 'After closing, you cannot add more follow-ups and it cannot be reopened. Close it?',
    closeSuccess: 'Feedback closed',
    closeFailed: 'Failed to close feedback',
    followUpRequired: 'Enter follow-up content',
    followUpSent: 'Follow-up sent',
    sendFailed: 'Failed to send',
    statuses: {
      OPEN: 'Open',
      IN_PROGRESS: 'In progress',
      RESOLVED: 'Resolved',
      CLOSED: 'Closed',
    },
    subjects: {
      bug: 'Bug report',
      feature: 'Feature request',
      billing: 'Billing and quotas',
      account: 'Account and security',
      other: 'Other',
    },
  },
  teamPage: {
    roles: {
      title: 'Roles',
      create: 'New role',
      edit: 'Edit role',
      delete: 'Delete role',
      refresh: 'Refresh roles',
      empty: 'No roles yet',
      name: 'Role name',
      namePlaceholder: 'Example: Finance specialist',
      menuPermissions: 'Menu permissions',
      refreshSuccess: 'Role list refreshed',
      nameRequired: 'Enter a role name.',
      selectMenuPermission: 'Select at least one menu permission.',
      createSuccess: 'Role created',
      saveSuccess: 'Role saved',
      saveFailed: 'Save failed',
      createOtherRoleFirst: 'Create another role first, then move members before deleting this role.',
      deleteConfirmTitle: 'Delete role "{name}"?',
      deleteConfirmDescription: 'This action cannot be undone.',
      deleteSuccess: 'Role deleted',
      deleteFailed: 'Delete failed',
      deleteWithMembersPrefix: 'Role "{name}" still has ',
      deleteWithMembersSuffix: ' members. Select a target role before deleting it.',
      moveToRole: 'Move to role',
      moveMembersAndDelete: 'Move members and delete',
      selectTargetRole: 'Select a target role.',
      targetRoleSame: 'The target role cannot be the current role.',
    },
    members: {
      title: 'Members',
      add: 'Add member',
      searchPlaceholder: 'Search name or email',
      selectRoleHint: 'Select a role on the left to view members.',
      account: 'Account',
      loginEmail: 'Login email',
      actions: 'Actions',
      resetPassword: 'Reset password',
      edit: 'Edit member',
      name: 'Name',
      initialPassword: 'Initial password',
      memberRole: 'Member role',
      createSuccess: 'Member created',
      createFailed: 'Create failed',
      updateSuccess: 'Member updated',
      updateFailed: 'Update failed',
      statusUpdated: 'Status updated',
      resetPasswordDescription: 'Set a new sign-in password for {name} ({email}).',
      confirmReset: 'Reset password',
      emailRequired: 'Enter a valid email address.',
      nameRequired: 'Enter the member name.',
      roleRequired: 'Select a member role.',
      passwordMin: 'Password must be at least 8 characters.',
      resetPasswordSuccess: 'Password reset',
      resetPasswordFailed: 'Password reset failed',
      selectWorkspaceAccess: 'Select workspace access',
      workspaceAccessTitle: 'Select workspace access',
      workspaceAccessDescription:
        'Choose the workspaces {name} can view and edit. Unselected workspaces stay hidden from the list and cannot be switched to.',
      workspaceAccessEmpty: 'No workspaces are available to grant.',
      workspaceAccessSaveSuccess: 'Workspace access saved',
      workspaceAccessSaveFailed: 'Failed to save workspace access',
      workspaceAccessLoadFailed: 'Failed to load workspace access',
    },
    status: {
      label: 'Status',
      active: 'Active',
      inactive: 'Inactive',
      deleted: 'Deleted',
    },
    passwordMinPlaceholder: 'At least 8 characters',
  },
  forbiddenPage: {
    title: 'You do not have access to this page',
    description: 'This feature is not enabled for the current plan or tenant configuration. Contact an administrator.',
    backHome: 'Back to home',
  },
  enterpriseIdentity: {
    title: 'Enterprise identity',
    description: 'Configure tenant SSO, mandatory MFA, and login IP allowlists. Supports both OIDC and SAML SSO.',
    fields: {
      ssoEnabled: 'Enable enterprise SSO',
      allowPasswordLogin: 'Allow password sign-in',
      requireMfa: 'Require MFA for all users',
      loginIpWhitelistEnabled: 'Enable login IP allowlist',
      loginIpWhitelist: 'Allowed login IPs / CIDRs',
    },
    hints: {
      loginIpWhitelist: 'One IP or CIDR per line, e.g. 203.0.113.10 or 10.0.0.0/8',
    },
    providers: {
      title: 'Identity providers',
      description: 'Configure OIDC or SAML IdPs. SAML requires the corresponding Feature Flag.',
      add: 'Add provider',
      empty: 'No identity providers configured yet',
      default: 'Default',
      createTitle: 'Add provider',
      editTitle: 'Edit provider',
      dialogDescription: 'OIDC callback URL is fixed to /api/v1/auth/sso/callback',
      fields: {
        name: 'Display name',
        issuer: 'Issuer / EntityID',
        clientId: 'Client ID',
        clientSecret: 'Client Secret',
        providerType: 'Provider type',
      },
    },
    saml: {
      dialogDescription: 'SAML ACS URL is fixed to /api/v1/auth/sso/saml/acs',
      acsUrl: 'ACS URL',
      metadataUrl: 'SP Metadata',
      acsHint: 'Register this ACS URL and SP EntityID in your IdP configuration.',
      fields: {
        idpMetadataUrl: 'IdP Metadata URL (optional)',
        idpSsoUrl: 'IdP SSO URL',
        idpCertificatePem: 'IdP certificate (PEM)',
        spEntityId: 'SP EntityID',
        wantAssertionsSigned: 'Require signed assertions (recommended)',
        signAuthnRequests: 'Sign AuthnRequest',
      },
    },
    scim: {
      title: 'SCIM User Provisioning',
      description: 'Configure SCIM 2.0 inbound user provisioning. IdPs manage user lifecycle via Bearer Token.',
      fields: {
        scimEnabled: 'Enable SCIM',
        scimDefaultMemberRoleId: 'Default member role ID',
        scimAllowCreate: 'Allow SCIM to create users',
        scimAllowDeactivate: 'Allow SCIM to deactivate users',
      },
      token: {
        title: 'SCIM Bearer Token',
        generate: 'Generate Token',
        rotate: 'Rotate Token',
        revoke: 'Revoke Token',
        generated: 'Token generated',
        rotated: 'Token rotated',
        revoked: 'Token revoked',
        lastRotatedAt: 'Last rotated',
        endpointHint: 'SCIM endpoint',
        oneTimeWarning: 'Copy this token now. It will not be shown again.',
      },
    },
    messages: {
      policySaved: 'Enterprise identity policy saved',
      providerSaved: 'Identity provider saved',
      providerDeleted: 'Identity provider deleted',
      saveFailed: 'Save failed',
    },
  },
  settingsPage: {
    createdAt: 'Created at {date}',
    lastUsedAt: 'Last used {date}',
    passkeys: {
      title: 'Passkeys',
      description: 'Sign in quickly and securely with device biometrics or security keys',
      browserSupported: 'Browser supported',
      browserUnsupported: 'Browser not supported',
      empty: 'No security keys registered',
      platformKey: 'Device key',
      securityKey: 'Security key',
      backupEligible: 'Backup eligible',
      deleteTitle: 'Delete this key',
      add: 'Add security key',
      verifyPasswordHint: 'Enter your password to confirm your identity',
      passwordPlaceholder: 'Enter your password',
      keyNameOptional: 'Key name (optional)',
      keyNamePlaceholder: 'Example: MacBook Touch ID',
      registering: 'Registering...',
      nextStep: 'Next',
      useSecurityKey: 'Use your security key',
      browserPrompt: 'Follow the browser prompt to complete registration',
      registerSuccess: 'Security key registered',
      registerSuccessDescription: 'You can now sign in with this security key.',
      registerSuccessTitle: 'Registration complete',
      registerTitle: 'Register security key',
      passwordRequired: 'Enter your password',
      invalidServerResponse: 'The server returned an unexpected response',
      registerRequestFailed: 'Registration request failed. Check whether the password is correct.',
      registerFailed: 'Registration failed. Try again.',
      deleteConfirmTitle: 'Delete security key',
      deleteConfirmDescription: 'Delete this security key? You will no longer be able to sign in with it.',
      deleteSuccess: 'Security key deleted',
    },
    mfa: {
      title: 'Multi-factor authentication (MFA)',
      description: 'Use Google Authenticator or another app to generate 6-digit one-time codes',
      enabledMessage: 'MFA is enabled. A verification code is required when signing in.',
      disabledMessage: 'Scan the QR code after enabling to complete setup',
      setupTitle: 'Set up authenticator',
      disableTitle: 'Disable MFA',
      setupDescription: 'Scan the QR code with Google Authenticator or another TOTP app, then enter the 6-digit code to finish setup.',
      disableDescription: 'Enter your current password and the code from your authenticator app to disable MFA.',
      qrAlt: 'MFA QR code',
      manualSecretHint: 'Cannot scan? Enter this secret manually:',
      loginPassword: 'Login password',
      sixDigitCode: '6-digit code',
      passwordRequired: 'Enter your login password.',
      codeRequired: 'Enter the 6-digit verification code.',
      confirmEnable: 'Enable',
      confirmDisable: 'Disable',
      qrFailed: 'Failed to generate the QR code. Use the secret below to add it manually.',
      setupFailed: 'Failed to initialize MFA',
      enableSuccess: 'MFA enabled',
      disableSuccess: 'MFA disabled',
    },
    apiKeys: {
      description: 'Use API keys to access enabled programmatic endpoints',
      empty: 'No API keys yet',
      rateLimit: '{count} requests per minute',
      rateLimitLabel: 'Per-minute limit',
      expiresAt: 'Expires {date}',
      expiresAtLabel: 'Expiration (optional)',
      scopes: 'Scopes',
      scopesRequired: 'Select at least one scope',
      create: 'Create API key',
      createTitle: 'Create API key',
      createDescription: 'Set a name for the API key. Save the key immediately after creation.',
      namePlaceholder: 'Example: Production Key',
      nameRequired: 'Enter an API key name.',
      revealedOnce: 'API key (shown only once)',
      loadFailed: 'Failed to load API keys',
      createSuccess: 'API key created. Save it now.',
      createFailed: 'Failed to create API key',
      deleteTitle: 'Delete API key',
      deleteDescription: 'Delete this API key?',
      revokeSuccess: 'API key revoked',
      revokeFailed: 'Failed to revoke API key',
    },
    apiRequestLogs: {
      title: 'API request logs',
      description: 'Only API key request metadata is stored. Bodies and secrets are never logged.',
      empty: 'No API request logs yet',
      apiKeyFilter: 'Filter by API Key ID',
      columns: {
        time: 'Time',
        method: 'Method',
        path: 'Path',
        status: 'Status',
        duration: 'Duration',
      },
    },
    profile: {
      title: 'Profile',
      avatar: 'Profile avatar',
      avatarUpload: 'Upload avatar',
      avatarUploading: 'Uploading...',
      avatarHint: 'Upload a PNG, JPG, or WebP image up to 2 MB. Crop it to a square before saving.',
      avatarCropTitle: 'Crop profile avatar',
      avatarConfirmCrop: 'Use avatar',
      avatarZoom: 'Zoom',
      avatarInvalid: 'Upload a valid image file.',
      avatarTooLarge: 'Avatar image must be 2 MB or smaller.',
      avatarSaveSuccess: 'Avatar updated',
      avatarSaveFailed: 'Failed to update avatar',
      name: 'Name',
      save: 'Save changes',
      saveSuccess: 'Profile saved',
      saveFailed: 'Failed to save profile',
    },
    security: {
      title: 'Security settings',
      description: 'Change your login password',
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmPassword: 'Confirm new password',
      changePassword: 'Change password',
      currentPasswordRequired: 'Enter your current password.',
      passwordMismatch: 'The two passwords do not match',
      passwordTooShort: 'Password must be at least 8 characters',
      changeSuccess: 'Password changed. Sign in again.',
      changeFailed: 'Failed to change password',
    },
  },
  dataTable: {
    loading: 'Loading...',
    empty: 'No records',
    total: 'Total {total}',
    perPage: '{size} / page',
    goTo: 'Go to',
    page: 'page',
    sortAsc: 'Sort by {label} ascending',
    sortDesc: 'Sort by {label} descending',
    sortable: 'Sort by {label}',
  },
  datePicker: {
    placeholder: 'Pick a date',
    rangePlaceholder: 'Pick a date range',
    clear: 'Clear date',
    year: '{year}',
    month: '{month}',
    yearPlaceholder: 'Year',
    monthPlaceholder: 'Month',
  },
  pdfPreview: {
    title: 'PDF preview',
    description: 'Scroll to browse all pages. Page buttons jump to the selected page. Zoom, download, and print are supported.',
    loading: 'Loading PDF...',
    rendering: 'Rendering page...',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    zoomOut: 'Zoom out',
    zoomIn: 'Zoom in',
    fitWidth: 'Fit width',
    download: 'Download',
    print: 'Print',
    pageLabel: 'Page {page} / {total}',
  },
  dialog: {
    discardTitle: 'Discard unsaved changes?',
    discardDescription: 'This dialog contains unsaved changes. Closing it will discard them.',
    discardNavigationDescription: 'This page contains a dialog with unsaved changes. Leaving the page will discard them.',
    discardConfirm: 'Discard changes',
    keepEditing: 'Keep editing',
    defaultDescription: 'Dialog content',
  },


  openingArApImport: {
    page: {
      title: 'Opening AR/AP Migration',
      description: 'Import cutover outstanding receivables and payables. Creates OPEN obligations only — no journals.',
    },
    actions: {
      upload: 'Upload CSV',
      execute: 'Execute import',
      retry: 'Retry failed execute',
      cancelBatch: 'Cancel batch',
      viewReceivable: 'View receivable',
      viewPayable: 'View payable',
      viewReceivablesAging: 'Receivables aging',
      viewPayablesAging: 'Payables aging',
      openMigration: 'Import opening balances',
    },
    empty: {
      title: 'No opening import batches yet',
      description: 'Upload a UTF-8 CSV to migrate cutover outstanding AR/AP balances.',
    },
    filters: {
      allStatuses: 'All statuses',
    },
    upload: {
      title: 'Upload opening AR/AP CSV',
      description: 'UTF-8 CSV only. Amount must be cutover outstanding, not historical invoice face.',
      utf8Only: 'UTF-8 CSV only (OPENING_AR_AP_CSV_V1)',
      shiftJisUnsupported: 'Shift-JIS is not supported. Convert the file to UTF-8 before upload.',
      requiredColumns: 'Required columns',
      optionalColumns: 'Optional columns',
      downloadTemplate: 'Download UTF-8 template',
      fileLabel: 'CSV file',
      defaultReceivableAccount: 'Default receivable account',
      defaultPayableAccount: 'Default payable account',
      cutoverDate: 'Cutover date (optional)',
      sourceSystem: 'Source system (optional)',
      memo: 'Memo (optional)',
      submit: 'Upload and validate',
    },
    amountSemantics: {
      title: 'Import amount = cutover outstanding',
      body: 'Enter the unsettled balance at migration cutover — not the historical invoice face amount.',
      example: 'Example: historical invoice ¥1,000,000 with ¥400,000 already paid → import ¥600,000.',
      noHistoryReconstruction: 'The system does not reconstruct historical payments or settlements.',
    },
    zeroAccounting: {
      title: 'No journals on import',
      body: 'This import registers opening operational AR/AP balances only and does not create journals or postings.',
    },
    summary: {
      title: 'Batch summary',
      file: 'File',
      totalRows: 'Total rows',
      validRows: 'Valid rows',
      invalidRows: 'Invalid rows',
      receivableTotal: 'Receivable total',
      payableTotal: 'Payable total',
      importedRows: 'Imported',
      failedRows: 'Failed',
      cutoverDate: 'Cutover date',
      accountingBook: 'Accounting book',
      bookAtExecute: 'Resolved at execute (ACTIVE)',
      defaultReceivableAccount: 'Default AR account',
      defaultPayableAccount: 'Default AP account',
    },
    table: {
      rowNumber: 'Row #',
      side: 'Side',
      counterparty: 'Counterparty',
      externalReference: 'External reference',
      amount: 'Outstanding',
      dueDate: 'Due date',
      account: 'AR/AP account',
      status: 'Status',
      error: 'Error',
      override: 'override',
    },
    dueDate: {
      none: 'No due date',
    },
    side: {
      RECEIVABLE: 'Receivable',
      PAYABLE: 'Payable',
      unknown: '—',
    },
    batchStatus: {
      RECEIVED: 'Received',
      VALIDATING: 'Validating',
      READY: 'Preview ready',
      VALIDATION_FAILED: 'Validation failed',
      EXECUTING: 'Executing',
      COMPLETED: 'Completed',
      COMPLETED_WITH_ERRORS: 'Completed with errors',
      FAILED: 'Failed',
      CANCELLED: 'Cancelled',
    },
    rowStatus: {
      PENDING: 'Pending',
      VALID: 'Valid',
      INVALID: 'Invalid',
      DUPLICATE_CONFLICT: 'Duplicate conflict',
      IMPORTING: 'Importing',
      IMPORTED: 'Imported',
      FAILED: 'Failed',
      SKIPPED: 'Skipped',
    },
    history: {
      createdAt: 'Created at',
      file: 'File',
      status: 'Status',
      rows: 'Rows',
      imported: 'Imported',
      failed: 'Failed',
      executedAt: 'Executed at',
    },
    executeDialog: {
      title: 'Confirm opening import execute',
      description: 'This creates OPEN obligations for valid rows. Preview is the human gate — execute is explicit.',
      validRows: 'Valid rows to import',
      invalidRows: 'Invalid rows (not imported)',
      receivableTotal: 'Receivable total',
      payableTotal: 'Payable total',
      accountingBook: 'Active accounting book',
      noAutoExecute: 'Upload does not auto-execute.',
      confirm: 'Execute import',
      executing: 'Executing…',
    },
    progress: {
      executing: 'Import is executing. This page refreshes automatically until finished.',
    },
    partialSuccess: {
      banner: 'Partial success: some rows imported, some failed. This is not a full batch failure.',
      noRetry: 'Completed with errors cannot be retried. Fix conflicts (cancel unsettled obligations if needed) and upload a new batch.',
    },
    correction: {
      guidance: 'To correct an unsettled opening item: cancel it on Receivables/Payables, then re-import with a new file. Batch undo is not available.',
    },
    settlement: {
      note: 'After import, opening obligations can be settled with payments like ordinary AR/AP. No special settlement workflow.',
    },
    featureDisabled: {
      title: 'Opening AR/AP migration is disabled',
      body: 'Feature flag accounting.ar_ap.opening_migration.enabled is off for this tenant. Upload and execute are unavailable.',
    },
    cancelDialog: {
      title: 'Cancel this batch?',
      description: 'Cancels the batch before execute. Already imported obligations are not deleted.',
    },
    errors: {
      generic: 'Opening import request failed',
      permissionDenied: 'No opening import permission',
      permissionDeniedBody: 'You need ar_ap.opening_import.read to view this workspace.',
    },
    failureCodes: {
      GENERIC: 'Opening import error',
      AR_AP_OPENING_MIGRATION_FEATURE_DISABLED: 'Opening migration feature is disabled',
      AR_AP_FEATURE_DISABLED: 'AR/AP feature is disabled',
      AR_AP_OPENING_IMPORT_NOT_FOUND: 'Opening import batch not found',
      AR_AP_OPENING_IMPORT_STATUS_TRANSITION_INVALID: 'Invalid batch status transition',
      AR_AP_OPENING_IMPORT_VERSION_CONFLICT: 'Batch version conflict — refresh and retry',
      AR_AP_OPENING_CSV_ENCODING_UNSUPPORTED: 'Unsupported CSV encoding (UTF-8 required; Shift-JIS not supported)',
      AR_AP_OPENING_CSV_HEADER_REQUIRED: 'CSV header is required',
      AR_AP_OPENING_CSV_HEADER_INVALID: 'CSV header is invalid',
      AR_AP_OPENING_CSV_HEADER_DUPLICATE: 'Duplicate CSV header columns',
      AR_AP_OPENING_CSV_HEADER_MISSING_REQUIRED: 'Required CSV columns are missing',
      AR_AP_OPENING_CSV_FORBIDDEN_COLUMN: 'Forbidden CSV column present',
      AR_AP_OPENING_FORMAT_UNSUPPORTED: 'Unsupported format profile',
      AR_AP_OPENING_FILENAME_INVALID: 'Invalid filename',
      AR_AP_OPENING_FILE_SIZE_INVALID: 'File size is invalid',
      AR_AP_OPENING_CONTENT_TYPE_INVALID: 'Invalid content type — CSV required',
      AR_AP_OPENING_FILE_REQUIRED: 'CSV file is required',
      AR_AP_OPENING_SIDE_INVALID: 'Side must be RECEIVABLE or PAYABLE',
      AR_AP_OPENING_COUNTERPARTY_REQUIRED: 'Counterparty is required',
      AR_AP_OPENING_COUNTERPARTY_TOO_LONG: 'Counterparty is too long',
      AR_AP_OPENING_AMOUNT_INVALID: 'Amount must be a positive integer minor-unit string',
      AR_AP_OPENING_EXTERNAL_REFERENCE_REQUIRED: 'External reference is required',
      AR_AP_OPENING_EXTERNAL_REFERENCE_TOO_LONG: 'External reference is too long',
      AR_AP_OPENING_EXTERNAL_REFERENCE_INVALID: 'External reference is invalid',
      AR_AP_OPENING_EXTERNAL_REFERENCE_DUPLICATE_IN_BATCH: 'Duplicate external reference within this batch',
      AR_AP_OPENING_EXTERNAL_REFERENCE_CONFLICT: 'An active opening obligation already exists for this external reference in the workspace',
      AR_AP_OPENING_DUE_DATE_INVALID: 'Due date must be YYYY-MM-DD',
      AR_AP_OPENING_ACCOUNT_REQUIRED: 'AR/AP account could not be resolved',
      AR_AP_OPENING_ACCOUNT_NOT_FOUND: 'AR/AP account not found',
      AR_AP_OPENING_ACCOUNT_ID_INVALID: 'AR/AP account id is invalid',
      AR_AP_OPENING_NO_VALID_ROWS: 'No valid rows to execute',
      AR_AP_OPENING_ROW_INCOMPLETE: 'Row data is incomplete',
      AR_AP_OPENING_ROW_IMPORT_FAILED: 'Row import failed',
      ACCOUNTING_BOOK_ACTIVE_REQUIRED: 'Active accounting book is required',
      SETTLEMENT_ACCOUNTING_ACCOUNT_NOT_ELIGIBLE: 'Account is not eligible for AR/AP',
      WORKSPACE_PERMISSION_DENIED: 'Workspace permission denied',
      IDEMPOTENCY_KEY_REUSED: 'Idempotency key reused with different payload',
      IDEMPOTENCY_KEY_REQUIRED: 'Idempotency-Key is required',
    },
  },
  arAp: {
    pages: {
      receivables: {
        title: 'Receivables',
        subtitle: 'Operational receivable obligations. OPEN means unpaid open item, not posted or matched.',
      },
      payables: {
        title: 'Payables',
        subtitle: 'Operational payable obligations. OPEN means unpaid open item, not posted or matched.',
      },
    },
    status: {
      DRAFT: 'Draft',
      OPEN: 'Open (unsettled)',
      SETTLED: 'Settled',
      CANCELLED: 'Cancelled',
      SUPERSEDED: 'Superseded',
    },
    side: {
      RECEIVABLE: 'Receivable',
      PAYABLE: 'Payable',
    },
    source: {
      MANUAL: 'Manual',
      BUSINESS_TRANSACTION: 'Business transaction',
    },
    table: {
      counterparty: 'Counterparty',
      counterpartyEmpty: '(No counterparty yet)',
      amount: 'Amount',
      dueDate: 'Due date',
      dueDateEmpty: '—',
      status: 'Status',
      source: 'Source',
      createdAt: 'Created',
      updatedAt: 'Updated',
      actions: 'Actions',
    },
    filters: {
      status: 'Status',
      sourceType: 'Source',
      dueFrom: 'Due from',
      dueTo: 'Due to',
      allStatuses: 'All statuses',
      allSources: 'All sources',
      apply: 'Apply',
      reset: 'Reset',
    },
    actions: {
      create: 'New obligation',
      refresh: 'Refresh',
      viewDetail: 'Details',
      edit: 'Edit',
      save: 'Save',
      confirm: 'Confirm as open',
      cancel: 'Cancel obligation',
    },
    fields: {
      side: 'Side',
      status: 'Status',
      amount: 'Amount (JPY)',
      currency: 'Currency',
      dueDate: 'Due date',
      counterparty: 'Counterparty',
      registrationNumber: 'Invoice registration number',
      sourceType: 'Source type',
      sourceId: 'Source ID',
      version: 'Version',
      createdAt: 'Created at',
      openedAt: 'Opened at',
      cancelledAt: 'Cancelled at',
      updatedAt: 'Updated at',
      arApAccount: 'AR/AP account',
      accountingBook: 'Accounting book',
    },
    create: {
      title: 'Create obligation',
      description: 'Creates a DRAFT obligation. Confirm later to mark it OPEN (unsettled).',
      amountPlaceholder: 'e.g. 1250000',
      counterpartyHint: 'Required before confirming as OPEN. Optional while DRAFT.',
      submit: 'Create draft',
      submitting: 'Creating…',
      errors: {
        amountInvalid: 'Enter a positive integer yen amount.',
      },
    },
    detail: {
      title: 'Obligation detail',
      description: 'Operational open-item details. Cancel does not reverse journals or ledger.',
      loading: 'Loading…',
      saving: 'Saving…',
      businessTransaction: 'Business transaction',
      businessTransactionHint: 'Lineage ID shown. No separate business-transaction editor in this stage.',
      sections: {
        basic: 'Basic',
        counterparty: 'Counterparty',
        source: 'Source',
        lifecycle: 'Lifecycle',
        accounting: 'Accounting settings',
      },
    },
    account: {
      required: 'AR/AP account required',
      immutableHint: 'Once set, the AR/AP account cannot be changed.',
      dialog: {
        title: 'Set AR/AP account',
        description: 'Select an account in the same accounting book as this obligation.',
      },
      actions: {
        set: 'Set account',
        save: 'Save account',
        saving: 'Saving…',
      },
    },
    confirmDialog: {
      title: 'Confirm obligation as open?',
      description: 'This marks the obligation OPEN (unsettled). It does not create a journal, posting, or payment.',
    },
    cancelDialog: {
      title: 'Cancel this obligation?',
      description: 'This cancels the receivable/payable obligation. It is not a journal reversal and does not change the general ledger.',
    },
    empty: {
      receivables: 'No receivables yet',
      payables: 'No payables yet',
      description: 'Create a manual draft obligation to get started.',
      filteredReceivables: 'No receivables match the filters',
      filteredPayables: 'No payables match the filters',
      filteredDescription: 'Try adjusting status, source, or due date filters.',
    },
    featureDisabled: {
      banner: 'AR/AP mutations are disabled for this tenant. Existing obligations remain readable.',
    },
    errors: {
      loadFailed: 'Failed to load obligations',
      loadDetailFailed: 'Failed to load obligation detail',
      createFailed: 'Failed to create obligation',
      updateFailed: 'Failed to update obligation',
      confirmFailed: 'Failed to confirm obligation',
      cancelFailed: 'Failed to cancel obligation',
      setAccountFailed: 'Failed to set AR/AP account',
      noPermissionTitle: 'No access',
      noPermission: 'You do not have permission to view AR/AP obligations.',
    },
    failureCodes: {
      AR_AP_OBLIGATION_FEATURE_DISABLED: 'AR/AP mutations are disabled by feature flag.',
      AR_AP_OBLIGATION_NOT_FOUND: 'Obligation not found.',
      AR_AP_OBLIGATION_STATUS_TRANSITION_INVALID: 'This status change is not allowed.',
      AR_AP_OBLIGATION_SOURCE_CONFLICT: 'An active obligation already exists for this source.',
      AR_AP_OBLIGATION_AMOUNT_INVALID: 'Amount must be a positive integer yen value.',
      AR_AP_OBLIGATION_SIDE_INVALID: 'Obligation side is invalid.',
      AR_AP_OBLIGATION_CURRENCY_UNSUPPORTED: 'Only JPY is supported.',
      AR_AP_OBLIGATION_VERSION_CONFLICT: 'This record was updated elsewhere. Reloaded latest version.',
      AR_AP_OBLIGATION_COUNTERPARTY_REQUIRED: 'Counterparty name is required before opening.',
      AR_AP_OBLIGATION_BUSINESS_TRANSACTION_NOT_CONFIRMED: 'Business transaction must be confirmed first.',
      AR_AP_OBLIGATION_AR_AP_ACCOUNT_STATUS_INVALID: 'This obligation status cannot set an AR/AP account.',
      AR_AP_OBLIGATION_AR_AP_ACCOUNT_IMMUTABLE: 'AR/AP account is already configured and cannot be changed.',
      AR_AP_OBLIGATION_ACCOUNTING_BOOK_REQUIRED: 'Accounting book is required first.',
      AR_AP_OBLIGATION_HAS_CONFIRMED_ALLOCATIONS:
        'Void eligible settlement allocations first, then cancel this obligation. Do not chain mutations automatically.',
      WORKSPACE_PERMISSION_DENIED: 'You do not have permission for this action.',
      IDEMPOTENCY_KEY_REUSED: 'This request was already submitted with a different payload.',
    },
    aging: {
      title: 'Aging & due-date operations',
      asOf: 'As of {date}',
      timezoneHint: 'Based on workspace timezone: {timezone}',
      dueScheduleHint: 'Due schedule shows OPEN obligations by remaining amount and due date. Not a cash forecast.',
      noDueDate: 'No due date',
      viewMode: {
        groupLabel: 'View mode',
        list: 'List',
        aging: 'Aging',
      },
      summary: {
        outstanding: 'Outstanding',
        overdue: 'Overdue',
        dueSoon: 'Due soon',
        noDueDate: 'No due date',
        obligationCount: '{count} open items',
      },
      buckets: {
        groupLabel: 'Aging buckets',
        count: '{count} items',
        CURRENT: 'Current',
        D1_30: '1–30 days',
        D31_60: '31–60 days',
        D61_90: '61–90 days',
        D90_PLUS: '90+ days',
        NO_DUE_DATE: 'No due date',
      },
      dueState: {
        OVERDUE: 'Overdue',
        DUE_SOON: 'Due soon',
        NOT_DUE: 'Not due yet',
        NO_DUE_DATE: 'No due date',
      },
      daysPastDue: {
        noDueDate: 'No due date',
        today: 'Due today',
        overdue: 'Overdue',
        overdueCount: '{days} days overdue',
        notYetDue: 'Not due yet',
      },
      table: {
        original: 'Original',
        remaining: 'Remaining',
        allocated: 'Allocated',
        dueState: 'Due state',
        bucket: 'Aging bucket',
      },
      filters: {
        asOfDate: 'As-of date',
        dueState: 'Due state',
        counterparty: 'Counterparty name',
        counterpartyPlaceholder: 'Exact snapshot name',
        allDueStates: 'All due states',
      },
      empty: {
        receivables: 'No outstanding receivables',
        payables: 'No outstanding payables',
        filteredReceivables: 'No receivables match aging filters',
        filteredPayables: 'No payables match aging filters',
        description: 'All open items are settled or none exist yet.',
        filteredDescription: 'Try adjusting as-of date, due state, or counterparty filters.',
      },
      errors: {
        loadFailed: 'Failed to load aging data',
      },
    },
  },

  payments: {
    pages: {
      title: 'Payments',
      subtitle: 'Operational cash facts. Confirm ≠ posted; allocation happens separately on obligations.',
    },
    status: {
      DRAFT: 'Draft',
      CONFIRMED: 'Confirmed',
      CANCELLED: 'Cancelled',
    },
    direction: {
      INCOMING: 'Incoming',
      OUTGOING: 'Outgoing',
    },
    source: {
      MANUAL: 'Manual',
      BANK_MATCH: 'Bank match',
    },
    allocationState: {
      UNALLOCATED: 'Unallocated',
      PARTIALLY_ALLOCATED: 'Partially allocated',
      FULLY_ALLOCATED: 'Fully allocated',
    },
    table: {
      paymentDate: 'Payment date',
      direction: 'Direction',
      counterparty: 'Counterparty',
      counterpartyEmpty: '(No counterparty)',
      amount: 'Amount',
      allocated: 'Allocated',
      unallocated: 'Unallocated',
      status: 'Status',
      allocation: 'Allocation',
      source: 'Source',
      actions: 'Actions',
    },
    filters: {
      direction: 'Direction',
      status: 'Status',
      paymentDate: 'Payment date range',
      allStatuses: 'All statuses',
      allDirections: 'All directions',
      allocationAll: 'All allocations',
      allocationNeeds: 'Needs allocation',
      allocationFull: 'Fully allocated',
      apply: 'Apply',
      reset: 'Reset',
    },
    actions: {
      create: 'New payment',
      refresh: 'Refresh',
      viewDetail: 'Details',
      edit: 'Edit',
      confirm: 'Confirm payment',
      cancel: 'Cancel payment',
    },
    fields: {
      direction: 'Direction',
      status: 'Status',
      amount: 'Amount (JPY)',
      paymentDate: 'Payment date',
      counterparty: 'Counterparty',
      reference: 'Reference',
      description: 'Description',
      bankAccount: 'Bank account',
      version: 'Version',
      confirmedAt: 'Confirmed at',
    },
    form: {
      createTitle: 'Create payment',
      createDescription: 'Creates a MANUAL payment draft. Confirm later as an operational cash fact (not posted).',
      editTitle: 'Edit payment',
      editDescription: 'Update draft payment fields.',
      amountPlaceholder: 'e.g. 125000',
      bankAccountEmpty: '(None)',
      bankMatchHint: 'Bank-sourced fields are read-only. Adjust description or counterparty if needed.',
      submit: 'Save',
      submitting: 'Saving…',
      errors: {
        amountInvalid: 'Enter a positive integer yen amount.',
        dateRequired: 'Payment date is required.',
      },
    },
    detail: {
      title: 'Payment detail',
      description: 'Operational cash fact. Cancel does not reverse journals, bank match, or allocations.',
      loading: 'Loading…',
      total: 'Total',
      allocated: 'Allocated',
      unallocated: 'Unallocated',
      provenance: 'Source',
      bankMatchDecision: 'Decision ID',
      viewBankReconciliation: 'View bank reconciliation',
      cancelBlockedHint: 'Cancel is blocked while confirmed settlement allocations exist. Void eligible allocations here first, then cancel the payment.',
    },
    allocations: {
      title: 'Settlement allocations',
      empty: 'No settlement allocations linked to this payment.',
      summary: '{total} allocation(s) · {inProgress} in progress · {posted} posted',
      unallocatedGuidance: 'This payment is confirmed but not allocated. Settle from receivables or payables.',
      goReceivables: 'Open receivables',
      goPayables: 'Open payables',
      viewObligation: 'View obligation',
    },
    confirmDialog: {
      title: 'Confirm payment?',
      description: 'Marks the operational cash fact as confirmed. This is not ledger posting or settlement.',
      bankMatchDescription: 'Confirm this bank-linked payment draft. Verify bank lineage before confirming.',
    },
    cancelDialog: {
      title: 'Cancel payment?',
      description: 'Cancels the payment record. This is not a ledger reversal or bank unmatch.',
    },
    featureDisabled: {
      banner: 'Payment mutations are disabled by feature flag. You can still view payments.',
    },
    empty: {
      title: 'No payments yet',
      description: 'Create a manual payment or link one from bank matching.',
      filteredTitle: 'No payments match filters',
      filteredDescription: 'Try adjusting filters or allocation quick chips.',
    },
    errors: {
      loadFailed: 'Failed to load payments',
      loadDetailFailed: 'Failed to load payment detail',
      createFailed: 'Failed to create payment',
      updateFailed: 'Failed to update payment',
      confirmFailed: 'Failed to confirm payment',
      cancelFailed: 'Failed to cancel payment',
      cancelBlockedByAllocations: 'Cannot cancel while confirmed settlement allocations exist.',
      noPermissionTitle: 'No access',
      noPermission: 'You do not have permission to view payments.',
    },
    failureCodes: {
      PAYMENT_FEATURE_DISABLED: 'Payment mutations are disabled by feature flag.',
      PAYMENT_NOT_FOUND: 'Payment not found.',
      PAYMENT_STATUS_TRANSITION_INVALID: 'This status change is not allowed.',
      PAYMENT_VERSION_CONFLICT: 'This record was updated elsewhere. Reload the latest version.',
      PAYMENT_AMOUNT_INVALID: 'Amount must be a positive integer yen value.',
      PAYMENT_DIRECTION_INVALID: 'Payment direction is invalid.',
      PAYMENT_CURRENCY_UNSUPPORTED: 'Only JPY is supported.',
      PAYMENT_DATE_INVALID: 'Payment date is invalid.',
      PAYMENT_HAS_CONFIRMED_ALLOCATIONS: 'Confirmed settlement allocations must be voided before cancel.',
      WORKSPACE_PERMISSION_DENIED: 'You do not have permission for this action.',
      IDEMPOTENCY_KEY_REUSED: 'This request was already submitted with a different payload.',
    },
  },


  differenceSettlement: {
    section: {
      title: 'Difference settlements',
      empty: 'No difference settlements for this obligation.',
      viewPayment: 'View payment',
    },
    actions: {
      create: 'Confirm difference',
    },
    types: {
      BANK_FEE: 'Bank fee',
      SMALL_DIFFERENCE: 'Small difference',
    },
    status: {
      CONFIRMED: 'Confirmed',
      CANCELLED: 'Cancelled',
    },
    dialog: {
      createTitle: 'Difference settlement',
      createDescription: 'Reduce obligation remaining with an explicit difference settlement. This is not a payment allocation.',
    },
    confirmDialog: {
      title: 'Confirm difference settlement?',
      description: 'Confirming this difference settlement reduces the operational unsettled balance. Journal posting follows separately according to accounting progress—it is not completed at this step.',
    },
    fields: {
      type: 'Difference type',
      amount: 'Difference amount',
      maximum: 'Maximum amount',
      remaining: 'Obligation remaining',
      differenceAccount: 'Difference account',
      differenceAccountPlaceholder: 'Select account',
      reason: 'Reason (optional)',
      bankFeeContext: 'Related payment context (optional)',
      paymentAmount: 'Payment amount',
      allocationAmount: 'Allocation to this obligation',
      relatedPaymentOptional: 'Linking a payment is optional and does not change payment allocated amounts.',
    },
    validation: {
      INVALID: 'Enter a positive integer amount.',
      OVER_REMAINING: 'Amount exceeds obligation remaining balance.',
      OVER_TYPE_MAX: 'Amount exceeds the small-difference tolerance cap.',
    },
    errors: {
      reloadFailed: 'Failed to reload obligation version.',
      versionConflict: 'Obligation was updated elsewhere. Refresh remaining and try again.',
    },
    cancel: {
      title: 'Cancel difference settlement',
      description: 'This cancels the operational difference. It does not reverse posted journals.',
      reason: 'Reason (optional)',
      confirm: 'Cancel difference settlement',
      reversalRequired: 'Already posted. Reverse the journal before cancelling this difference.',
      viewProgress: 'View accounting progress',
    },
    failureCodes: {
      UNKNOWN: 'Difference settlement failed.',
      AR_AP_DIFFERENCE_FEATURE_DISABLED: 'Difference settlement is currently disabled.',
      AR_AP_DIFFERENCE_NOT_FOUND: 'Difference settlement not found.',
      AR_AP_DIFFERENCE_OBLIGATION_NOT_OPEN: 'Only open obligations can receive difference settlements.',
      AR_AP_DIFFERENCE_AMOUNT_INVALID: 'Difference amount is invalid.',
      AR_AP_DIFFERENCE_OVER_SETTLEMENT: 'Difference would exceed obligation original amount.',
      AR_AP_DIFFERENCE_VERSION_CONFLICT: 'Obligation was updated concurrently.',
      AR_AP_DIFFERENCE_CURRENCY_MISMATCH: 'Difference currency must match obligation currency.',
      AR_AP_DIFFERENCE_ACCOUNT_MISSING: 'Difference account is required.',
      AR_AP_DIFFERENCE_ACCOUNT_INVALID: 'Difference account is not eligible.',
      AR_AP_DIFFERENCE_ACCOUNT_INACTIVE: 'Difference account is inactive.',
      AR_AP_DIFFERENCE_ACCOUNT_REQUIRED: 'Difference account is required.',
      AR_AP_DIFFERENCE_TYPE_UNSUPPORTED: 'Difference type is not supported.',
      AR_AP_DIFFERENCE_STATUS_TRANSITION_INVALID: 'Invalid difference status transition.',
      AR_AP_DIFFERENCE_CANCEL_REQUIRES_REVERSAL: 'Already posted — reversal is required.',
      AR_AP_DIFFERENCE_PERMISSION_DENIED: 'Permission denied.',
      AR_AP_DIFFERENCE_BOOK_REQUIRED: 'Obligation accounting book is required.',
    },
  },

  differenceAccounting: {
    panel: {
      title: 'Difference accounting progress',
      differenceAmount: 'Difference amount',
    },
    stages: {
      difference: 'Difference',
      transaction: 'Transaction',
      classification: 'Classification',
      journal: 'Journal',
      posting: 'Posting',
      posted: 'Posted',
    },
    actions: {
      retry: 'Retry accounting',
      resolveDate: 'Set accounting date',
      setDifferenceAccount: 'Set difference account',
      setArApAccount: 'Set AR/AP account',
    },
    fields: {
      accountingDate: 'Accounting date',
      postedJournal: 'Posted journal',
    },
    warnings: {
      needsReversal: 'Already posted — a reversal journal is required before cancelling the difference.',
    },
    featureDisabled: {
      banner: 'Difference settlement remains valid. Accounting orchestration is disabled for this tenant.',
    },
    dateDialog: {
      title: 'Set accounting date',
      description: 'Accounting date is used only for difference accounting/posting.',
      accountingDate: 'Accounting date',
      openPeriodHint: 'Only dates in an OPEN accounting period are accepted.',
      submit: 'Confirm',
      errors: {
        required: 'Accounting date is required.',
      },
    },
    outcomes: {
      UNKNOWN: { label: 'Unknown', description: 'Accounting progress could not be determined.' },
      SOURCE_NOT_READY: { label: 'Not ready', description: 'Accounting source is not ready yet.' },
      WAITING_ACCOUNTING_DATE: { label: 'Accounting date required', description: 'Set an explicit accounting date.' },
      WAITING_AR_AP_ACCOUNT: { label: 'AR/AP account required', description: 'Set an AR/AP account on the obligation.' },
      WAITING_DIFFERENCE_ACCOUNT: { label: 'Difference account required', description: 'Set a difference P&L account.' },
      WAITING_TRANSACTION_CONFIRMATION: { label: 'Waiting for transaction confirmation', description: 'Business transaction confirmation is required.' },
      WAITING_CLASSIFICATION_CONFIRMATION: { label: 'Waiting for classification confirmation', description: 'Classification confirmation is required.' },
      CLASSIFICATION_ENSURED: { label: 'Classification created', description: 'Classification exists and awaits confirmation.' },
      JOURNAL_ENSURED: { label: 'Journal draft created', description: 'Journal draft exists and awaits confirmation.' },
      WAITING_JOURNAL_CONFIRMATION: { label: 'Waiting for journal confirmation', description: 'Journal confirmation is required.' },
      WAITING_JOURNAL_REVIEW: { label: 'Waiting for journal review', description: 'Journal review approval is required.' },
      WAITING_OPEN_PERIOD: { label: 'Waiting for OPEN period', description: 'Target period is not OPEN.' },
      ORCHESTRATION_DISABLED: { label: 'Orchestration disabled', description: 'Feature flag disabled automatic accounting.' },
      POSTING_REQUESTED: { label: 'Posting requested', description: 'Waiting for posting to start.' },
      POSTING_ALREADY_ENSURED: { label: 'Posting request ensured', description: 'Posting request already exists.' },
      POSTING_IN_PROGRESS: { label: 'Posting in progress', description: 'Posting worker is processing.' },
      POSTED: { label: 'Posted', description: 'Posted journal exists.' },
      POSTING_RETRYABLE_FAILED: { label: 'Posting failed (retryable)', description: 'Posting is in a retryable failed state.' },
      POSTING_PERMANENTLY_FAILED: { label: 'Posting failed (permanent)', description: 'Permanent posting failure.' },
      NEEDS_REVERSAL: { label: 'Reversal required', description: 'Already posted — use the reversal workflow.' },
      ACCOUNTING_REVERSED: { label: 'Accounting reversed', description: 'Corresponding reversal journal is complete.' },
      TARGET_TERMINAL: { label: 'Terminal', description: 'No further accounting ensure is needed.' },
      FAILED: { label: 'Failed', description: 'Accounting processing failed; retry may help.' },
      ALREADY_READY_FOR_POSTING: { label: 'Ready for posting', description: 'Human gates complete; posting can proceed.' },
      POSTING_BLOCKED_FOUNDATION_FLAG: { label: 'Posting blocked', description: 'Foundation posting flag blocked posting.' },
      BOOK_MISMATCH: { label: 'Book mismatch', description: 'Accounting books do not match.' },
      ACCOUNT_INACTIVE: { label: 'Inactive account', description: 'Snapshot accounts are not ACTIVE.' },
    },
    failureCodes: {
      AR_AP_DIFFERENCE_ACCOUNTING_FEATURE_DISABLED: 'Difference accounting orchestration is currently disabled.',
      AR_AP_DIFFERENCE_ACCOUNTING_BOOK_MISMATCH: 'Accounting books do not match.',
      AR_AP_DIFFERENCE_ACCOUNTING_WAITING_ACCOUNTING_DATE: 'Accounting date is required.',
      AR_AP_DIFFERENCE_ACCOUNTING_AR_AP_ACCOUNT_REQUIRED: 'AR/AP account is required.',
      AR_AP_DIFFERENCE_ACCOUNTING_DIFFERENCE_ACCOUNT_REQUIRED: 'Difference account is required.',
      AR_AP_DIFFERENCE_ACCOUNTING_PERIOD_NOT_OPEN: 'Accounting period is not OPEN.',
      AR_AP_DIFFERENCE_ACCOUNTING_PERIOD_NOT_FOUND: 'Accounting period was not found.',
      AR_AP_DIFFERENCE_ACCOUNTING_DATE_BEFORE_EVIDENCE_DATE: 'Accounting date is before evidence date.',
      AR_AP_DIFFERENCE_ACCOUNTING_DATE_FISCAL_YEAR_MISMATCH: 'Accounting date fiscal year mismatch.',
      AR_AP_DIFFERENCE_ACCOUNTING_SNAPSHOT_IMMUTABLE: 'Accounting snapshot is immutable.',
      AR_AP_DIFFERENCE_ACCOUNTING_SOURCE_NOT_ELIGIBLE: 'Difference source is not eligible for accounting.',
      AR_AP_DIFFERENCE_ACCOUNTING_ACCOUNT_INACTIVE: 'Accounting account is inactive.',
      AR_AP_DIFFERENCE_ACCOUNTING_NOT_READY: 'Difference accounting is not ready.',
      AR_AP_DIFFERENCE_ACCOUNTING_ACCOUNTS_REQUIRED: 'Accounting accounts are required.',
      AR_AP_DIFFERENCE_ACCOUNTING_ACCOUNT_COLLISION: 'Accounting account collision detected.',
    },
  },

  settlementWrite: {
    actions: {
      addAllocation: 'Add allocation',
      create: 'Allocate',
      loadMore: 'Load more',
    },
    dialog: {
      createTitle: 'Allocate payment',
      createDescription: 'Create a confirmed settlement allocation between a payment and an obligation.',
    },
    fields: {
      amount: 'Allocation amount',
      suggested: 'Suggested amount',
      maximum: 'Maximum amount',
      unallocated: 'Payment unallocated',
      remaining: 'Obligation remaining',
      selectPayment: 'Select payment',
      selectObligation: 'Select obligation',
      searchCandidates: 'Search candidates',
    },
    empty: {
      noEligiblePayment: 'No eligible payments',
      noEligibleObligation: 'No eligible obligations',
    },
    validation: {
      INVALID: 'Enter a positive integer amount.',
      OVER_PAYMENT: 'Amount exceeds payment unallocated balance.',
      OVER_OBLIGATION: 'Amount exceeds obligation remaining balance.',
    },
    errors: {
      versionConflict: 'State was updated by another operation. Reload remaining amounts and confirm again.',
      reloadFailed: 'Failed to reload payment or obligation versions.',
    },
    void: {
      title: 'Cancel settlement allocation',
      description: 'This voids the allocation. It does not reverse posted journals.',
      reason: 'Reason (optional)',
      confirm: 'Cancel settlement allocation',
      reversalRequired: 'Already posted. Reverse the journal before voiding this allocation.',
      viewProgress: 'View accounting progress',
    },
    failureCodes: {
      UNKNOWN: 'Settlement allocation failed.',
      SETTLEMENT_FEATURE_DISABLED: 'Settlement is currently disabled.',
      SETTLEMENT_PAYMENT_NOT_FOUND: 'Payment not found.',
      SETTLEMENT_OBLIGATION_NOT_FOUND: 'Obligation not found.',
      SETTLEMENT_ALLOCATION_NOT_FOUND: 'Settlement allocation not found.',
      SETTLEMENT_PAYMENT_NOT_CONFIRMED: 'Only confirmed payments can be allocated.',
      SETTLEMENT_PAYMENT_CANCELLED: 'Cancelled payments cannot be allocated.',
      SETTLEMENT_OBLIGATION_NOT_OPEN: 'Only open obligations can be allocated.',
      SETTLEMENT_DIRECTION_MISMATCH: 'Payment direction does not match obligation side.',
      SETTLEMENT_ACCOUNTING_BOOK_MISMATCH: 'Accounting books do not match.',
      SETTLEMENT_CURRENCY_UNSUPPORTED: 'Only JPY settlements are supported.',
      SETTLEMENT_AMOUNT_INVALID: 'Allocation amount is invalid.',
      SETTLEMENT_PAYMENT_OVER_ALLOCATED: 'Allocation would exceed payment amount.',
      SETTLEMENT_OBLIGATION_OVER_ALLOCATED: 'Allocation would exceed obligation amount.',
      SETTLEMENT_PAYMENT_VERSION_CONFLICT: 'Payment was updated concurrently.',
      SETTLEMENT_OBLIGATION_VERSION_CONFLICT: 'Obligation was updated concurrently.',
      SETTLEMENT_ALLOCATION_VERSION_CONFLICT: 'Allocation was updated concurrently.',
      SETTLEMENT_CONCURRENCY_CONFLICT: 'Settlement concurrency conflict. Retry.',
      SETTLEMENT_STATUS_TRANSITION_INVALID: 'Invalid allocation status transition.',
      SETTLEMENT_VOID_REQUIRES_REVERSAL: 'Already posted — reversal is required.',
      IDEMPOTENCY_KEY_REQUIRED: 'Idempotency key is required.',
      IDEMPOTENCY_KEY_INVALID: 'Idempotency key is invalid.',
      IDEMPOTENCY_IN_PROGRESS: 'The same request is still in progress.',
      IDEMPOTENCY_KEY_PAYLOAD_CONFLICT: 'Idempotency key was reused with a different payload.',
    },
  },

  settlementAccounting: {
    section: {
      title: 'Settlement & accounting progress',
      empty: 'No settlements linked to this obligation.',
      viewPayment: 'View payment',
    },
    panel: {
      title: 'Accounting progress',
      allocationAmount: 'Settlement amount',
      retryHint: 'Retry resumes accounting from the current state. It does not reconfirm settlement, transaction, classification, or journal.',
    },
    stages: {
      settlement: 'Settlement',
      transaction: 'Transaction',
      classification: 'Classification',
      journal: 'Journal',
      posting: 'Posting',
      posted: 'Posted',
    },
    allocationStatus: {
      CONFIRMED: 'Settled',
      VOIDED: 'Voided',
    },
    actions: {
      retry: 'Retry accounting',
      resolveDate: 'Set accounting date',
      configureBankGlMapping: 'Configure bank GL mapping',
      setArApAccount: 'Set AR/AP account',
    },
    fields: {
      accountingDate: 'Accounting date',
      businessTransaction: 'Business transaction',
      classification: 'Classification',
      journalDraft: 'Journal draft',
      posting: 'Posting request',
      postedJournal: 'Posted journal',
    },
    warnings: {
      needsReversal: 'Already posted — a reversal journal is required. Voiding settlement does not reverse the ledger.',
    },
    featureDisabled: {
      banner: 'Settlement remains valid. Accounting orchestration is disabled for this tenant, so automatic accounting will not run.',
    },
    dateDialog: {
      title: 'Set accounting date',
      description: 'Payment date is unchanged. Accounting date is used only for accounting/posting.',
      paymentDate: 'Payment date (read-only)',
      paymentDateHint: 'Payment date is kept as evidence and is not modified.',
      accountingDate: 'Accounting date',
      openPeriodHint: 'Only dates in an OPEN accounting period are accepted.',
      submit: 'Confirm',
      errors: {
        required: 'Accounting date is required.',
      },
    },
    outcomes: {
      UNKNOWN: { label: 'Unknown', description: 'Accounting progress could not be determined.' },
      SOURCE_NOT_READY: { label: 'Not ready', description: 'Accounting source is not ready yet.' },
      WAITING_ACCOUNTING_DATE: { label: 'Accounting date required', description: 'Payment date is not in an OPEN period; set an explicit accounting date.' },
      WAITING_BANK_ACCOUNT_MAPPING: { label: 'Bank GL mapping required', description: 'Configure bank account GL mapping.' },
      WAITING_AR_AP_ACCOUNT: { label: 'AR/AP account required', description: 'Set an AR/AP account on the obligation.' },
      WAITING_TRANSACTION_CONFIRMATION: { label: 'Waiting for transaction confirmation', description: 'Business transaction confirmation is required.' },
      WAITING_CLASSIFICATION_CONFIRMATION: { label: 'Waiting for classification confirmation', description: 'Classification confirmation is required.' },
      CLASSIFICATION_ENSURED: { label: 'Classification created', description: 'Classification exists and awaits confirmation.' },
      JOURNAL_ENSURED: { label: 'Journal draft created', description: 'Journal draft exists and awaits confirmation.' },
      WAITING_JOURNAL_CONFIRMATION: { label: 'Waiting for journal confirmation', description: 'Journal confirmation is required.' },
      WAITING_JOURNAL_REVIEW: { label: 'Waiting for journal review', description: 'Journal review approval is required.' },
      WAITING_OPEN_PERIOD: { label: 'Waiting for OPEN period', description: 'Target period is not OPEN. Check date or period.' },
      ORCHESTRATION_DISABLED: { label: 'Orchestration disabled', description: 'Feature flag disabled automatic accounting.' },
      POSTING_REQUESTED: { label: 'Posting requested', description: 'Waiting for posting to start.' },
      POSTING_ALREADY_ENSURED: { label: 'Posting request ensured', description: 'Posting request already exists.' },
      POSTING_IN_PROGRESS: { label: 'Posting in progress', description: 'Posting worker is processing.' },
      POSTED: { label: 'Posted', description: 'Posted journal exists. Settlement status remains CONFIRMED.' },
      POSTING_RETRYABLE_FAILED: { label: 'Posting failed (retryable)', description: 'Posting is in a retryable failed state.' },
      POSTING_PERMANENTLY_FAILED: { label: 'Posting failed (permanent)', description: 'Permanent posting failure; operational intervention required.' },
      NEEDS_REVERSAL: { label: 'Reversal required', description: 'Already posted — use the reversal workflow.' },
      ACCOUNTING_REVERSED: { label: 'Accounting reversed', description: 'Corresponding reversal journal is complete.' },
      TARGET_TERMINAL: { label: 'Terminal', description: 'No further accounting ensure is needed.' },
      FAILED: { label: 'Failed', description: 'Accounting processing failed; retry may help.' },
      ALREADY_READY_FOR_POSTING: { label: 'Ready for posting', description: 'Human gates complete; posting request can be ensured.' },
      POSTING_BLOCKED_FOUNDATION_FLAG: { label: 'Posting blocked', description: 'Foundation posting flag blocked posting.' },
      BOOK_MISMATCH: { label: 'Book mismatch', description: 'Payment and obligation books do not match.' },
      ACCOUNT_INACTIVE: { label: 'Inactive account', description: 'Snapshot accounts are not ACTIVE.' },
      DIRECTION_SIDE_MISMATCH: { label: 'Direction/side mismatch', description: 'Payment direction and obligation side are incompatible.' },
    },
    failureCodes: {
      UNKNOWN: 'Accounting processing failed.',
      SETTLEMENT_ACCOUNTING_FEATURE_DISABLED: 'Accounting orchestration is currently disabled.',
      SETTLEMENT_ACCOUNTING_DATE_REQUIRED: 'Accounting date is required.',
      SETTLEMENT_ACCOUNTING_PERIOD_NOT_OPEN: 'Accounting period for the date is not OPEN.',
      SETTLEMENT_ACCOUNTING_BOOK_MISMATCH: 'Accounting books do not match.',
      SETTLEMENT_ACCOUNTING_BANK_ACCOUNT_MAPPING_REQUIRED: 'Bank GL mapping is required.',
      SETTLEMENT_ACCOUNTING_AR_AP_ACCOUNT_REQUIRED: 'AR/AP account is required.',
      SETTLEMENT_VOID_REQUIRES_REVERSAL: 'Already posted — reversal is required.',
      SETTLEMENT_ALLOCATION_NOT_FOUND: 'Settlement allocation not found.',
      ACCOUNTING_PERIOD_NOT_OPEN: 'Accounting period is not OPEN.',
      REVIEW_CASE_NOT_APPROVED: 'Journal review is not approved.',
    },
  },

  bankAccountSettings: {
    page: {
      title: 'Account settings',
      subtitle: 'Manage bank and card accounts together with GL account mappings.',
    },
    tabs: {
      accounts: 'Accounts',
      glMappings: 'GL mapping',
    },
  },
  bankGlMapping: {
    page: {
      title: 'Bank GL Mapping',
      subtitle: 'Manage workspace bank account to GL account mappings.',
    },
    fields: {
      bankAccount: 'Bank account',
      maskedAccount: 'Masked account',
      bankBranch: 'Bank / branch',
      accountingBook: 'Accounting book',
      mappedAccount: 'Mapped GL account',
      status: 'Mapping status',
      updatedAt: 'Updated at',
      action: 'Action',
    },
    filters: {
      bankAccount: 'Bank account',
      mappingStatus: 'Mapping status',
      allBankAccounts: 'All accounts',
      allStatuses: 'All',
      mapped: 'Mapped',
      unmapped: 'Unmapped',
    },
    status: {
      mapped: 'Mapped',
      unmapped: 'Unmapped',
    },
    actions: {
      configure: 'Configure mapping',
      change: 'Change mapping',
      save: 'Save mapping',
      saving: 'Saving…',
      archive: 'Archive mapping',
      archiving: 'Archiving…',
      refresh: 'Refresh',
      return: 'Return',
    },
    dialog: {
      createTitle: 'Create bank GL mapping',
      editTitle: 'Edit bank GL mapping',
      description: 'Select an ACTIVE ASSET account in the same accounting book. Suspense 9990 is not allowed.',
    },
    archiveDialog: {
      title: 'Archive this mapping?',
      description: 'Archived mappings are inactive. Historical settlement snapshots are not rewritten.',
    },
    selector: {
      placeholder: 'Select account',
      ariaLabel: 'GL account selector',
    },
    empty: {
      title: 'No mapping rows',
      description: 'Add bank accounts or adjust filters.',
      manageAccounts: 'Manage bank accounts',
    },
    readOnly: 'Read-only',
    errors: {
      loadFailed: 'Failed to load bank GL mappings',
      noPermissionTitle: 'No access',
      noPermission: 'You do not have permission to view bank GL mappings.',
    },
    failureCodes: {
      UNKNOWN: 'Bank GL mapping operation failed.',
      WORKSPACE_BANK_ACCOUNT_NOT_FOUND: 'Bank account not found.',
      WORKSPACE_BANK_ACCOUNT_ARCHIVED: 'Bank account is archived; mapping cannot be updated.',
      SETTLEMENT_ACCOUNTING_BOOK_NOT_ACTIVE: 'Accounting book is not active.',
      SETTLEMENT_ACCOUNTING_ACCOUNT_INACTIVE: 'Selected account is inactive.',
      SETTLEMENT_ACCOUNTING_ACCOUNT_NOT_ELIGIBLE: 'Selected account is not eligible for bank cash mapping.',
      BANK_ACCOUNT_GL_MAPPING_NOT_FOUND: 'Mapping not found. List refreshed.',
      WORKSPACE_PERMISSION_DENIED: 'You do not have permission to manage bank GL mappings.',
    },
  },

  bankAccounts: {
    page: {
      title: 'Bank Accounts',
      subtitle: 'Register workspace bank and card accounts used for imports and GL mapping.',
    },
    actions: {
      create: 'Add account',
      edit: 'Edit',
      archive: 'Archive',
    },
    filters: {
      status: 'Status',
      active: 'Active',
      archived: 'Archived',
      all: 'All',
    },
    fields: {
      displayName: 'Display name',
      accountType: 'Account type',
      bankBranch: 'Bank / Branch',
      bankCode: 'Bank code',
      bankName: 'Bank name',
      branchCode: 'Branch code',
      branchName: 'Branch name',
      accountNumberMasked: 'Account number (masked)',
      externalAccountKey: 'External account key',
      currency: 'Currency',
      status: 'Status',
      updatedAt: 'Updated',
      action: 'Actions',
    },
    accountTypes: {
      ORDINARY: 'Ordinary',
      CURRENT: 'Current',
      SAVINGS: 'Savings',
      CREDIT_CARD: 'Credit card',
      OTHER: 'Other',
    },
    status: {
      active: 'Active',
      archived: 'Archived',
    },
    placeholders: {
      externalAccountKey: 'e.g. BANK_001',
      accountNumberMasked: 'e.g. ****1234',
    },
    hints: {
      externalAccountKey: 'Must be unique within the workspace. Cannot be changed after creation.',
      accountNumberMasked: 'Plaintext account numbers are not stored. Enter a masked value only.',
    },
    dialog: {
      createTitle: 'Add bank account',
      editTitle: 'Edit bank account',
      description: 'Set the display name and account attributes. Do not enter plaintext account numbers.',
    },
    archiveDialog: {
      title: 'Archive this account?',
      description: 'Archive “{name}”. It will no longer be selectable for new imports; existing data is kept.',
    },
    empty: {
      title: 'No bank accounts',
      descriptionWithCreate: 'Add a bank or card account to start imports and GL mapping.',
      descriptionReadOnly: 'There are no bank accounts to show.',
      filteredTitle: 'No matching accounts',
      filteredDescription: 'Try changing the status filter.',
    },
    readOnly: 'Read-only',
    errors: {
      loadFailed: 'Failed to load bank accounts',
      createFailed: 'Failed to create bank account',
      updateFailed: 'Failed to update bank account',
      archiveFailed: 'Failed to archive bank account',
      noPermissionTitle: 'No access',
      noPermission: 'You do not have permission to view bank accounts.',
    },
  },

  accountingExport: {
    title: 'Accounting Exports',
    description: 'Export posted journals in generic CSV format.',
    format: {
      GENERIC_CSV: 'Generic CSV',
      unknown: 'Unknown format',
    },
    status: {
      REQUESTED: 'Accepted',
      GENERATING: 'Generating',
      READY: 'Ready to download',
      FAILED: 'Failed',
      EXPIRED: 'Expired',
      processing: 'Generating',
    },
    table: {
      requestedAt: 'Requested at',
      book: 'Accounting book',
      period: 'Journal date range',
      format: 'Format',
      status: 'Status',
      lineCount: 'Lines',
      expiresAt: 'Expires at',
      actions: 'Actions',
    },
    filters: {
      status: 'Status',
      book: 'Accounting book',
      allStatuses: 'All statuses',
      allBooks: 'All books',
      reset: 'Reset filters',
    },
    actions: {
      create: 'New export',
      download: 'Download',
      retry: 'Retry',
      reExport: 'Create with same conditions',
    },
    create: {
      title: 'New export',
      description: 'Export posted journals for a journal date range.',
      book: 'Accounting book',
      bookPlaceholder: 'Select accounting book',
      periodLabel: 'Journal date range',
      periodHint: 'Use journalDate semantics for the selected period.',
      format: 'Format',
      submit: 'Create export',
      errors: {
        bookRequired: 'Select an accounting book.',
        periodRequired: 'Select a period.',
        invalidPeriod: 'Start date must be on or before end date.',
        periodTooLong: 'Period may not exceed {maxDays} days.',
      },
    },
    retry: {
      confirmTitle: 'Retry this export?',
      confirmDescription: 'Regenerates the file from the snapshot frozen when this export was created.',
    },
    empty: {
      title: 'No exports yet',
      descriptionWithCreate: 'Export posted journals for import into accounting software.',
      descriptionReadOnly: 'There are no export records to display.',
      filteredTitle: 'No matching records',
      filteredDescription: 'No exports match the current filters.',
    },
    featureDisabled: {
      banner: 'Creating new exports is currently unavailable. Existing exports remain visible and downloadable.',
    },
    errors: {
      listFailed: 'Failed to load exports.',
      createFailed: 'Failed to create export.',
      retryFailed: 'Failed to retry export.',
      downloadFailed: 'Failed to issue download URL.',
      readForbidden: 'You do not have permission to view this page.',
      genericFailure: 'Export generation failed.',
    },
    failureCodes: {
      ACCOUNTING_EXPORT_FEATURE_DISABLED: 'Creating new exports is currently unavailable.',
      ACCOUNTING_EXPORT_INVALID_PERIOD: 'The selected period is invalid.',
      ACCOUNTING_EXPORT_TOO_LARGE: 'The export is too large.',
      ACCOUNTING_EXPORT_EXPIRED: 'This export file has expired. Create a new export.',
      ACCOUNTING_EXPORT_NOT_READY: 'The export file is not ready yet.',
      ACCOUNTING_EXPORT_RETRY_NOT_ALLOWED: 'This export cannot be retried.',
      ACCOUNTING_EXPORT_FORMAT_NOT_SUPPORTED: 'The selected format is not supported.',
      ENQUEUE_FAILED: 'Failed to start export processing. Please retry.',
      WORKER_TIMEOUT: 'Export processing timed out.',
      GENERATION_FAILED: 'Failed to generate export file.',
      STORAGE_UPLOAD_FAILED: 'Failed to store export file.',
      ACCOUNTING_EXPORT_SOURCE_QUERY_FAILED: 'Failed to load export source data.',
      WORKSPACE_PERMISSION_DENIED: 'You do not have permission for this action.',
      IDEMPOTENCY_KEY_REUSED: 'This request was already processed.',
    },
  },
  bankImport: {
    title: 'Bank Import',
    description: 'Upload bank statement CSV files and review import batches.',
    status: {
      RECEIVED: 'Received',
      QUEUED: 'Queued',
      PARSING: 'Parsing',
      PARSED: 'Parsed',
      NORMALIZING: 'Normalizing',
      READY_FOR_MATCHING: 'Ready for matching',
      PARTIALLY_READY: 'Partially ready',
      FAILED: 'Failed',
      CANCELLED: 'Cancelled',
      processing: 'Processing',
    },
    statusPhase: {
      WAITING: 'Waiting',
      RUNNING: 'Running',
      SUCCEEDED: 'Import succeeded',
      FAILED: 'Import failed',
    },
    parseStatus: {
      STORED: 'Stored',
      PARSE_ERROR: 'Parse error',
    },
    normalizationStatus: {
      NORMALIZED: 'Normalized',
      FAILED: 'Normalization failed',
      DUPLICATE_SKIPPED: 'Duplicate skipped',
      unknown: 'Unknown',
    },
    direction: {
      WITHDRAWAL: 'Withdrawal',
      DEPOSIT: 'Deposit',
      unknown: 'Unknown',
    },
    errorStage: {
      PARSING: 'Parsing',
      NORMALIZATION: 'Normalization',
      unknown: 'Unknown',
    },
    table: {
      created: 'Created',
      bankAccount: 'Bank account',
      file: 'File',
      rows: 'Rows',
      normalized: 'Normalized',
      duplicate: 'Duplicate',
      failed: 'Failed',
      status: 'Status',
      createdBy: 'Created by',
      actions: 'Actions',
    },
    filters: {
      status: 'Status',
      bankAccount: 'Bank account',
      allStatuses: 'All statuses',
      allAccounts: 'All accounts',
      apply: 'Filter',
      reset: 'Reset',
    },
    actions: {
      upload: 'Upload statement',
      download: 'Download',
      retry: 'Retry',
      cancel: 'Cancel',
      detail: 'Detail',
    },
    upload: {
      title: 'Upload bank statement',
      description: 'Select a workspace account and upload a supported CSV format.',
      bankAccount: 'Workspace account',
      bankAccountPlaceholder: 'Select account',
      file: 'CSV file',
      fileHint: 'Choose a .csv file. Rakuten e-navi supports UTF-8 / Shift-JIS.',
      fileDrop: 'Click to select or drag a file here',
      clearFile: 'Remove file',
      noAccounts: 'No bank accounts are available in this workspace.',
      noCardAccounts: 'No credit-card account is available for card statement import. Create one below.',
      format: 'Parser format',
      formatOptions: {
        GENERIC_BANK_CSV_V1: 'GENERIC_BANK_CSV_V1 (generic bank CSV)',
        RAKUTEN_ENAVI_CARD_CSV_V1: 'Rakuten Card e-navi CSV',
      },
      formatHints: {
        GENERIC_BANK_CSV_V1: 'UTF-8 CSV with English headers (transaction_date, amount, direction, description).',
        RAKUTEN_ENAVI_CARD_CSV_V1: 'e-navi CSV with 利用日 / 利用店名・商品名 / 利用金額. Requires a CREDIT_CARD account.',
      },
      downloadTemplate: 'Download template',
      createCardAccountTitle: 'Create credit-card account',
      createCardAccountHint: 'e-navi statements can only be imported into CREDIT_CARD accounts.',
      createCardAccountPlaceholder: 'e.g. Rakuten Card',
      createCardAccount: 'Create card account',
      creatingCardAccount: 'Creating…',
      submit: 'Start import',
      errors: {
        bankAccountRequired: 'Please select a workspace account.',
        fileRequired: 'Please select a CSV file.',
        fileType: 'Only .csv files are accepted.',
        cardAccountNameRequired: 'Enter a credit-card account name.',
      },
    },
    retry: {
      confirmTitle: 'Retry this import?',
      confirmDescription: 'The original file will be re-parsed and re-normalized.',
    },
    cancel: {
      confirmTitle: 'Cancel this import?',
      confirmDescription: 'Queued batches will be cancelled. Already-processed data is retained for audit.',
    },
    empty: {
      title: 'No import batches yet',
      descriptionWithCreate: 'Upload a bank statement CSV to start an import.',
      descriptionReadOnly: 'There are no import batches to display.',
      filteredTitle: 'No matching batches',
      filteredDescription: 'No import batches match the current filters.',
    },
    featureDisabled: {
      banner: 'New import batches cannot be created right now. You can still review existing batches and download history.',
    },
    errors: {
      listFailed: 'Failed to load import batches.',
      createFailed: 'Failed to create the import batch.',
      createCardAccountFailed: 'Failed to create the credit-card account.',
      retryFailed: 'Failed to retry the import batch.',
      cancelFailed: 'Failed to cancel the import batch.',
      downloadFailed: 'Failed to obtain the download URL.',
      detailFailed: 'Failed to load batch detail.',
      bankAccountsFailed: 'Failed to load bank accounts.',
      readForbidden: 'You do not have permission to view this page.',
      genericFailure: 'Import processing failed.',
    },
    detail: {
      title: 'Batch detail',
      empty: 'No data',
      noErrors: 'No errors',
      prevPage: 'Previous page',
      nextPage: 'Next page',
      pageOf: 'Page {page} of {total}',
      tabs: {
        summary: 'Summary',
        raw: 'Raw lines',
        normalized: 'Normalized lines',
        errors: 'Errors',
        audit: 'Audit timeline',
      },
      summary: {
        status: 'Status',
        created: 'Created',
        finished: 'Finished',
        attempt: 'Attempt',
        rawCount: 'Raw lines',
        normalizedCount: 'Normalized lines',
        duplicateCount: 'Duplicate lines',
        failedCount: 'Failed lines',
        blankCount: 'Blank lines',
      },
      raw: {
        lineNumber: 'Line',
        attempt: 'Attempt',
        parseStatus: 'Parse status',
        payload: 'Raw payload',
      },
      normalized: {
        line: 'Line',
        date: 'Transaction date',
        amount: 'Amount',
        direction: 'Direction',
        description: 'Description',
        status: 'Normalization status',
      },
      errors: {
        line: 'Line',
        stage: 'Stage',
        code: 'Error code',
        message: 'Message',
      },
      audit: {
        actor: 'Actor',
      },
    },
    audit: {
      'bank_import.received': 'Received',
      'bank_import.queued': 'Queued',
      'bank_import.retried': 'Retried',
      'bank_import.cancelled': 'Cancelled',
      'bank_import.enqueue_failed': 'Enqueue failed',
      'bank_import.download_url_issued': 'Download URL issued',
      'bank_import.parsing_started': 'Parsing started',
      'bank_import.parsed': 'Parsed',
      'bank_import.normalization_started': 'Normalization started',
      'bank_import.ready_for_matching': 'Ready for matching',
      'bank_import.partially_ready': 'Partially ready',
      'bank_import.failed': 'Failed',
      'bank_import.line_failed': 'Line failed',
    },
    lineErrorMessages: {
      BANK_IMPORT_REQUIRED_HEADER_MISSING: 'A required header is missing.',
      BANK_IMPORT_DUPLICATE_HEADER: 'A duplicate header is present.',
      BANK_IMPORT_COLUMN_LIMIT_EXCEEDED: 'Column limit exceeded.',
      BANK_IMPORT_ROW_LIMIT_EXCEEDED: 'Row limit exceeded.',
      BANK_IMPORT_CSV_MALFORMED: 'Malformed CSV.',
      BANK_IMPORT_ROW_COLUMN_COUNT_MISMATCH: 'Row column count does not match the header.',
      BANK_LINE_INVALID_DATE: 'Invalid date format.',
      BANK_LINE_INVALID_AMOUNT: 'Invalid amount format.',
      BANK_LINE_INVALID_DIRECTION: 'Invalid direction field.',
      BANK_LINE_UNSUPPORTED_CURRENCY: 'Unsupported currency.',
      BANK_LINE_EMPTY_DESCRIPTION: 'Empty description.',
      BANK_LINE_DUPLICATE: 'Duplicate line.',
      BANK_LINE_NORMALIZATION_FAILED: 'Normalization failed.',
    },
    failureCodes: {
      BANK_IMPORT_FEATURE_DISABLED: 'New import batches cannot be created right now.',
      BANK_IMPORT_ENQUEUE_FAILED: 'Failed to start import processing. Please retry.',
      BANK_IMPORT_STORAGE_FAILED: 'Failed to store the file.',
      BANK_IMPORT_UNSUPPORTED_FILE_TYPE: 'Unsupported file type.',
      BANK_IMPORT_FILE_TOO_LARGE: 'File size exceeds the limit.',
      BANK_IMPORT_UNSUPPORTED_ENCODING: 'Unsupported file encoding.',
      BANK_IMPORT_EMPTY_FILE: 'File is empty.',
      BANK_IMPORT_REQUIRED_HEADER_MISSING: 'A required header is missing.',
      BANK_IMPORT_DUPLICATE_HEADER: 'A duplicate header is present.',
      BANK_IMPORT_COLUMN_LIMIT_EXCEEDED: 'Column limit exceeded.',
      BANK_IMPORT_ROW_LIMIT_EXCEEDED: 'Row limit exceeded.',
      BANK_IMPORT_CSV_MALFORMED: 'Malformed CSV.',
      BANK_IMPORT_SOURCE_UNAVAILABLE: 'Original file is unavailable.',
      BANK_IMPORT_PARSING_FAILED: 'Parsing failed.',
      BANK_IMPORT_NORMALIZATION_FAILED: 'Normalization failed.',
      BANK_IMPORT_ORIGINAL_UNAVAILABLE: 'Original file is unavailable.',
      BANK_IMPORT_ORIGINAL_EXPIRED: 'Original file has expired.',
      BANK_IMPORT_NOT_FOUND: 'Import batch not found.',
      BANK_IMPORT_RETRY_NOT_ALLOWED: 'This batch cannot be retried.',
      BANK_IMPORT_CANCEL_NOT_ALLOWED: 'This batch cannot be cancelled.',
      BANK_LINE_INVALID_DATE: 'Invalid date format.',
      BANK_LINE_INVALID_AMOUNT: 'Invalid amount format.',
      BANK_LINE_INVALID_DIRECTION: 'Invalid direction field.',
      BANK_LINE_UNSUPPORTED_CURRENCY: 'Unsupported currency.',
      BANK_LINE_EMPTY_DESCRIPTION: 'Empty description.',
      BANK_LINE_DUPLICATE: 'Duplicate line.',
      BANK_LINE_NORMALIZATION_FAILED: 'Normalization failed.',
      IDEMPOTENCY_KEY_REQUIRED: 'Idempotency key is required.',
      IDEMPOTENCY_KEY_REUSED: 'Idempotency key is already used.',
      ENQUEUE_FAILED: 'Enqueue failed.',
      BANK_ACCOUNT_NOT_FOUND: 'Bank account not found.',
      BANK_ACCOUNT_ARCHIVED: 'Bank account is archived.',
      BANK_IMPORT_ACCOUNT_TYPE_MISMATCH: 'The selected account type is not compatible with this CSV format.',
      WORKSPACE_PERMISSION_DENIED: 'You do not have permission for this action.',
    },
  },

  periodReconciliation: {
    title: 'Period Reconciliation',
    subtitle: 'Review bank lines and business transactions by period',
    status: {
      matched: 'Matched',
      bankOnly: 'Bank only',
      businessOnly: 'Business only',
      possibleMatch: 'Possible match',
      ignored: 'Ignored',
    },
    summary: {
      bankMatchRate: 'Bank match rate',
      bankMatchRateHelp: 'Share of bank lines that are matched (ignored excluded from denominator)',
      businessCoverageRate: 'Business coverage rate',
      businessCoverageRateHelp: 'Share of eligible business transactions matched to a bank line',
      bankAmount: 'Bank amount',
      businessAmount: 'Business amount',
      bankCount: '{count} bank lines',
      businessCount: '{count} business transactions',
      periodGap: 'Period amount gap (reference)',
      periodGapTooltip: 'Period total difference between bank lines and business transactions. Not an accounting difference settlement.',
      periodGapNote: 'Reference only — not difference settlement.',
      gapOutgoing: 'Outgoing',
      gapIncoming: 'Incoming',
    },
    exceptions: {
      bankOnly: 'Bank only',
      businessOnly: 'Business only',
      possibleMatch: 'Possible match',
    },
    filters: {
      period: 'Period',
      account: 'Bank account',
      matchState: 'Match state',
      direction: 'Direction',
      flow: 'Cash flow',
      allAccounts: 'All accounts',
      allMatchStates: 'All states',
      allDirections: 'All',
      allFlows: 'All',
      thisMonth: 'This month',
      prevMonth: 'Previous month',
      advanced: 'Advanced filters',
      includeAllSources: 'Show all eligible sources',
      timezoneHint: 'Period uses workspace timezone calendar days ({timezone})',
    },
    tabs: {
      label: 'Period reconciliation tabs',
      overview: 'Overview',
      bank: 'Bank lines',
      business: 'Business transactions',
      difference: 'Exceptions',
    },
    direction: {
      WITHDRAWAL: 'Withdrawal',
      DEPOSIT: 'Deposit',
    },
    flow: {
      OUTFLOW: 'Outflow',
      INFLOW: 'Inflow',
    },
    bankTable: {
      date: 'Date',
      account: 'Account',
      description: 'Description',
      counterparty: 'Counterparty',
      reference: 'Reference',
      direction: 'Direction',
      amount: 'Amount',
      status: 'Status',
      actions: 'Actions',
    },
    businessTable: {
      date: 'Date',
      counterparty: 'Counterparty',
      type: 'Type',
      amount: 'Amount',
      paymentMethod: 'Payment method',
      source: 'Source',
      status: 'Status',
      reconStatus: 'Reconciliation',
      actions: 'Actions',
    },
    actions: {
      refresh: 'Refresh',
      apply: 'Apply',
      openMatching: 'Review match',
      openBankLine: 'Open bank line',
      openDocument: 'Open document',
    },
    difference: {
      bankSection: 'Bank side (bank only / possible match)',
      bankHint: 'Bank pagination is independent.',
      businessSection: 'Business side (business only)',
      businessHint: 'Business pagination is independent.',
    },
    overview: {
      hint: 'Click an exception card to jump to the matching list tab.',
    },
    empty: {
      noSummary: 'Summary is not available.',
      noExceptionsTitle: 'No unmatched exceptions',
      noExceptionsBody: 'No bank-only, business-only, or possible-match items in this period.',
    },
    errors: {
      featureDisabled: 'Period reconciliation is disabled.',
      featureDisabledHelp: 'Enable bank.reconciliation.period.enabled. Bank matching is unaffected.',
      invalidPeriod: 'Invalid period. Use YYYY-MM-DD with periodFrom <= periodTo.',
      periodTooLarge: 'Period span exceeds the 12-month maximum.',
      unsupportedCurrency: 'Only JPY is supported for period reconciliation.',
      permissionDenied: 'You do not have permission to view period reconciliation.',
      permissionDeniedHelp: 'Requires both bank_match.read and transaction.read.',
      summaryFailed: 'Failed to load summary.',
      bankFailed: 'Failed to load bank lines.',
      businessFailed: 'Failed to load business transactions.',
    },
  },
  bankEvidence: {
    title: 'Bank Evidence Link',
    description: 'Link withdrawal statement lines to confirmed document segments. Deposits are not shown.',
    lineStatus: {
      UNLINKED: 'Unlinked',
      LINKED: 'Linked',
      EXCLUDED: 'Excluded',
      NEEDS_REVIEW: 'Needs review',
    },
    rule: {
      EXACT: 'Exact match',
      FUZZY_AMOUNT_DATE: 'Amount and date approximate',
      FUZZY_AMOUNT_CPTY: 'Amount and counterparty approximate',
      AMOUNT_ONLY: 'Amount only',
      MANUAL: 'Manual',
      unknown: 'Unknown',
    },
    linkSource: {
      CANDIDATE: 'Candidate',
      MANUAL: 'Manual',
      AUTO: 'Batch auto-link',
    },
    reason: {
      exact: 'Amount, date, and counterparty match',
      fuzzyAmountDate: 'Amount and date are close',
      fuzzyAmountCpty: 'Amount and counterparty are close',
      amountOnly: 'Amount matches',
      manual: 'Selected manually',
      unknown: 'Unknown',
    },
    excludeReasons: {
      FEE: 'Fee',
      PERSONAL: 'Personal use',
      PAPER_HELD: 'Paper evidence held separately',
      DUPLICATE_ADJUST: 'Duplicate / adjustment',
      OTHER: 'Other',
    },
    historyEvent: {
      CANDIDATES_GENERATED: 'Generated candidates',
      LINKED: 'Linked to a document',
      EXCLUDED: 'Excluded the line',
      UNLINKED: 'Unlinked evidence',
      NEEDS_REVIEW: 'Marked as needs review',
      unknown: 'Unknown',
    },
    filters: {
      status: 'Status',
      bankAccount: 'Bank account',
      dateFrom: 'From',
      dateTo: 'To',
      dateFromPlaceholder: 'Select start date',
      dateToPlaceholder: 'Select end date',
      allStatuses: 'All statuses',
      allAccounts: 'All accounts',
      apply: 'Filter',
      reset: 'Reset',
    },
    table: {
      date: 'Date',
      amount: 'Amount',
      description: 'Description',
      evidenceStatus: 'Evidence status',
      documentNumber: 'Document number',
      documentNumberUnknown: 'Number unknown',
      candidateCount: 'Candidates',
      actions: 'Actions',
      total: '{total} total · page {page} ({pageSize}/page)',
    },
    pagination: {
      prev: 'Previous',
      next: 'Next',
    },
    actions: {
      detail: 'Detail',
      link: 'Link',
      exclude: 'Exclude',
      unlink: 'Unlink',
      refreshCandidates: 'Refresh candidates',
      batchRefreshCandidates: 'Batch refresh candidates',
      batchAutoLink: 'Batch auto-link',
      searchSegments: 'Search segments',
      manualSearch: 'Manual search',
      addCandidate: 'Add as candidate',
    },
    featureDisabled: {
      banner: 'Bank evidence linking is currently disabled. You can browse the list and details, but link, exclude and unlink are blocked.',
    },
    empty: {
      title: 'No withdrawal lines',
      description: 'Import bank statements first. Only withdrawals appear here.',
      filteredTitle: 'No matching lines',
      filteredDescription: 'Try adjusting status, account, or date filters.',
    },
    detail: {
      title: 'Evidence detail',
      tabs: {
        summary: 'Summary',
        history: 'History',
      },
      summary: {
        date: 'Date',
        amount: 'Amount',
        evidenceStatus: 'Evidence status',
        candidateCount: 'Candidates',
        linkSource: 'Link source',
        segment: 'Document segment',
        counterparty: 'Counterparty',
        reference: 'Reference',
        excludeReason: 'Exclude reason',
        linkedDocument: 'Linked document',
      },
    },
    candidate: {
      sectionTitle: 'Candidate documents',
      hint: 'Compare the bank line with candidate segments, then confirm the link.',
      empty: 'No pending candidates. Refresh or use manual search.',
      segment: 'Segment',
      score: 'Score',
      matchScore: 'Match score {score}%',
      rule: 'Rule',
      reason: 'Reason',
      date: 'Date',
      amount: 'Amount',
      counterparty: 'Counterparty',
      documentNumber: 'Document number',
      description: 'Description',
      actions: 'Actions',
      selected: 'Linked',
    },
    manual: {
      title: 'Manual search',
      hint: 'Search confirmed document segments by the same amount, add as a candidate, then link from Summary.',
      search: 'Keyword',
      searchPlaceholder: 'Counterparty, document number…',
      date: 'Date',
      amount: 'Amount',
      counterparty: 'Counterparty',
      documentNumber: 'Document number',
      empty: 'No segments found. Adjust search or date range.',
      moreHidden: '{count} more not shown',
    },
    history: {
      empty: 'No history yet.',
      systemActor: 'System',
    },
    batchRefresh: {
      title: 'Batch refresh candidates',
      description: 'Asynchronously refresh candidates for up to 1000 unlinked lines under the current filters. You will receive a notification when finished.',
    },
    batchAutoLink: {
      title: 'Batch auto-link',
      description: 'Refresh candidates for unlinked lines under the current filters. Lines with exactly one same-day same-amount candidate are linked; lines with multiple candidates are marked needs review.',
    },
    toast: {
      batchRefreshQueued: 'Queued candidate refresh for {count} unlinked lines. You will be notified when done.',
      batchAutoLinkQueued: 'Queued batch auto-link for {count} unlinked lines. You will be notified when done.',
      candidateAdded: 'Added as a candidate.',
    },
    exclude: {
      title: 'Exclude statement line',
      description: 'Mark this withdrawal as not requiring document evidence. The bank line is not deleted.',
      reasonLabel: 'Reason',
      noteLabel: 'Note',
      notePlaceholder: 'At least {min} characters required for Other',
      noteHint: 'Other requires a note of at least {min} characters.',
    },
    unlink: {
      title: 'Unlink evidence',
      description: 'Remove the active link or exclusion so this line can be linked again. Accounting records are not changed.',
    },
    failureCodes: {
      BANK_EVIDENCE_FEATURE_DISABLED: 'Bank evidence linking is disabled.',
      BANK_EVIDENCE_NOT_FOUND: 'Statement line or candidate was not found.',
      BANK_EVIDENCE_NOT_WITHDRAWAL: 'Only withdrawal lines can be linked.',
      BANK_EVIDENCE_SEGMENT_NOT_ELIGIBLE: 'This document segment is not eligible for linking.',
      BANK_EVIDENCE_EXCLUDE_REASON_REQUIRED: 'Select an exclude reason.',
      BANK_EVIDENCE_EXCLUDE_NOTE_REQUIRED: 'Other requires a note of at least 8 characters.',
      BANK_EVIDENCE_LINE_ALREADY_ACTIVE: 'This line is already linked or excluded.',
      BANK_EVIDENCE_CANDIDATE_NOT_AVAILABLE: 'This candidate is expired or unavailable. Refresh and try again.',
      BANK_EVIDENCE_BATCH_TOO_LARGE: 'More than 1000 unlinked lines match. Narrow filters and try again.',
      BANK_EVIDENCE_BATCH_EMPTY: 'No unlinked lines match the current filters.',
      WORKSPACE_PERMISSION_DENIED: 'You do not have permission for this action.',
      IDEMPOTENCY_KEY_REQUIRED: 'Idempotency key is required.',
      IDEMPOTENCY_KEY_REUSED: 'Idempotency key has already been used.',
      VALIDATION_FAILED: 'Validation failed.',
    },
    errors: {
      listFailed: 'Failed to load evidence lines.',
      detailFailed: 'Failed to load evidence detail.',
      linkFailed: 'Failed to link evidence.',
      excludeFailed: 'Failed to exclude line.',
      unlinkFailed: 'Failed to unlink evidence.',
      refreshFailed: 'Failed to refresh candidates.',
      batchRefreshFailed: 'Failed to enqueue batch candidate refresh.',
      batchAutoLinkFailed: 'Failed to enqueue batch auto-link.',
      addCandidateFailed: 'Failed to add candidate.',
      segmentSearchFailed: 'Failed to search document segments.',
      readForbidden: 'You do not have permission to view bank evidence.',
      conflict: 'This line was updated elsewhere. Refresh and try again.',
      genericFailure: 'Something went wrong. Please try again.',
    },
  },
  bankMatch: {
    title: 'Bank Matching',
    description: 'Match bank statement lines to candidates and confirm matching decisions.',
    status: {
      UNKNOWN: 'Unknown',
      UNMATCHED: 'Unmatched',
      CANDIDATES_READY: 'Candidates Ready',
      MATCH_SELECTED: 'Candidate Selected',
      REVIEW_REQUIRED: 'Review Required',
      CONFIRMED: 'Confirmed',
    },
    lineStatus: {
      UNMATCHED: 'Unmatched',
      MATCHED: 'Matched',
      IGNORED: 'Ignored',
    },
    direction: {
      WITHDRAWAL: 'Withdrawal',
      DEPOSIT: 'Deposit',
      unknown: 'Unknown',
    },
    rule: {
      EXACT: 'Exact',
      FUZZY_AMOUNT_DATE: 'Fuzzy Amount/Date',
      FUZZY_AMOUNT_COUNTERPARTY: 'Fuzzy Amount/Counterparty',
      AMOUNT_ONLY: 'Amount Only',
      unknown: 'Unknown',
    },
    targetType: {
      BUSINESS_TRANSACTION: 'Transaction',
      POSTED_JOURNAL: 'Journal',
      unknown: 'Unknown',
    },
    candidateStatus: {
      PENDING: 'Pending',
      SELECTED: 'Selected',
      REJECTED: 'Rejected',
      EXPIRED: 'Expired',
      unknown: 'Unknown',
    },
    historyEvent: {
      CANDIDATE_SELECTED: 'Candidate Selected',
      REVIEW_REQUESTED: 'Review Requested',
      REVIEW_REJECTED: 'Review Rejected',
      MATCH_CONFIRMED: 'Match Confirmed',
      MATCH_OVERRIDDEN: 'Match Overridden',
      VERSION_CONFLICT: 'Version Conflict',
      unknown: 'Unknown',
    },
    filters: {
      status: 'Status',
      bankAccount: 'Bank account',
      dateFrom: 'From date',
      dateFromPlaceholder: 'Pick a start date',
      allStatuses: 'All statuses',
      allAccounts: 'All accounts',
      reset: 'Reset filters',
    },
    table: {
      date: 'Date',
      amount: 'Amount',
      direction: 'Direction',
      description: 'Description',
      matchStatus: 'Match status',
      candidateCount: 'Candidates',
      decisionStatus: 'Decision',
      actions: 'Actions',
    },
    actions: {
      detail: 'Detail',
      select: 'Select',
      confirm: 'Confirm',
      override: 'Override',
      ignore: 'Ignore',
      unmatch: 'Unmatch',
    },
    featureDisabled: {
      banner: 'Bank matching is currently disabled. You can browse the list and details, but select, confirm and override are blocked.',
    },
    ignore: {
      title: 'Ignore statement line',
      description: 'Mark this bank statement line as not requiring a match. The line itself is not deleted.',
    },
    unmatch: {
      title: 'Unmatch',
      description: 'Unmatch does not reverse, delete, or void any accounting entries already created. Candidates will be regenerated.',
    },
    orchestration: {
      title: 'Accounting orchestration',
      retry: 'Retry',
      lastAttempt: 'Last attempt',
      postingRequest: 'Posting request',
      postedJournal: 'Posted journal',
      waitingReason: 'Waiting reason',
      reversedWarning: 'A reversal journal was created after this match. The match still points to the original journal (it is not migrated or unmatched automatically).',
      waitingReasons: {
        TRANSACTION: 'Waiting for transaction confirmation',
        CLASSIFICATION: 'Waiting for classification confirmation',
        JOURNAL: 'Waiting for journal confirmation',
        PERIOD: 'Waiting for accounting period to open',
        FEATURE: 'Waiting for accounting orchestration flag',
      },
      labels: {
        reconciliationOnly: 'Linked for reconciliation',
        alreadyComplete: 'Accounting complete',
        postingRequested: 'Posting accepted',
        waitingTransaction: 'Waiting confirmation (transaction)',
        waitingClassification: 'Waiting confirmation (classification)',
        waitingJournal: 'Waiting confirmation (journal)',
        periodBlocked: 'Accounting period not open',
        featureDisabled: 'Accounting orchestration disabled',
        targetIssue: 'Target has an issue',
        inProgress: 'Accounting orchestration in progress',
      },
      descriptions: {
        reconciliationOnly: 'Linked to an existing journal; will not post again.',
        alreadyComplete: 'Accounting processing is already complete.',
        postingRequested: 'A posting request was created; a worker will continue.',
        waitingTransaction: 'Human confirmation is required for the transaction. This is not an error.',
        waitingClassification: 'Human confirmation is required for classification. This is not an error.',
        waitingJournal: 'Human confirmation is required for the journal. This is not an error.',
        periodBlocked: 'Posting cannot proceed until the target period is OPEN. Retry after the period is fixed.',
        featureDisabled: 'Accounting orchestration is off. Retry after it is enabled.',
        targetNotFound: 'Match target was not found.',
        targetTerminal: 'Match target is in a terminal state and cannot continue.',
        inProgress: 'Missing accounting stages are being completed.',
      },
    },
    paymentEvidence: {
      title: 'Payment evidence',
      description: 'Create an operational payment draft from this confirmed bank match. This does not allocate the payment against obligations.',
      createDraft: 'Create Payment Draft',
      featureDisabled: 'Bank match → payment draft is currently disabled. You can still view evidence, but creating a draft is blocked.',
      sourceMatchSuperseded: 'The linked payment still exists, but this bank match is no longer the active source.',
      linkedPayment: 'Linked payment',
      viewPayment: 'View payment in workspace',
      paymentStatus: {
        DRAFT: 'Draft',
        CONFIRMED: 'Confirmed',
        CANCELLED: 'Cancelled',
        unknown: 'Unknown',
      },
      eligibility: {
        ELIGIBLE: 'Eligible to create a payment draft.',
        NOT_CONFIRMED: 'Confirm the match before creating a payment draft.',
        LINE_NOT_MATCHED: 'The statement line must be matched.',
        POSTED_JOURNAL_RECONCILIATION_ONLY: 'Posted journal matches are for reconciliation only; payment draft is not applicable.',
        MANUAL_TARGET_NOT_ELIGIBLE: 'Manual match targets cannot create a payment draft.',
        UNSUPPORTED_TARGET: 'This match target cannot create a payment draft.',
        PAYMENT_ALREADY_EXISTS: 'A payment is already linked to this match.',
        PAYMENT_CANCELLED_REPLACEMENT_DEFERRED: 'A cancelled payment is linked; replacement is deferred.',
        FEATURE_DISABLED: 'Payment draft creation is disabled.',
        UNSUPPORTED_CURRENCY: 'Only JPY statement lines can create a payment draft.',
        INVALID_AMOUNT: 'Statement amount is invalid for a payment draft.',
        UNKNOWN: 'Payment draft is not available for this match.',
      },
    },
    candidate: {
      topHint: 'Showing the top {n} candidates by score',
      empty: 'No candidates available.',
      moreHidden: '{count} more candidates hidden.',
      type: 'Type',
      target: 'Target',
      score: 'Score',
      rule: 'Rule',
      date: 'Generated',
      amount: 'Status',
      actions: 'Actions',
      selected: 'Selected',
    },
    decision: {
      title: 'Decision',
      reviewRequired: 'This match requires review. A member with confirm permission must confirm it.',
      confirmed: 'This statement line is confirmed.',
      noActions: 'No actions available right now.',
    },
    confirm: {
      title: 'Confirm Match',
      description: 'Confirm the match with this candidate?',
    },
    override: {
      title: 'Force Override',
      description: 'Force-confirm this match? Override permission is required.',
    },
    detail: {
      title: 'Statement Line Detail',
      overridden: 'Confirmed via override',
      tabs: {
        summary: 'Summary',
        candidates: 'Candidates',
        history: 'History',
        audit: 'Audit',
      },
      summary: {
        date: 'Transaction date',
        amount: 'Amount',
        direction: 'Direction',
        matchStatus: 'Match status',
        candidateCount: 'Candidate count',
        decisionStatus: 'Decision status',
        version: 'Version',
        overridden: 'Overridden',
        counterparty: 'Counterparty',
        reference: 'Reference',
      },
    },
    history: {
      empty: 'No history recorded.',
    },
    audit: {
      hint: 'Audit logs are available in the Audit Center.',
      viaAuditCenter: 'View in Audit Center',
    },
    empty: {
      title: 'No bank statement lines',
      description: 'Import a bank statement to review matching candidates here.',
      filteredTitle: 'No matching statement lines',
      filteredDescription: 'Adjust your filters.',
    },
    errors: {
      listFailed: 'Failed to load statement lines.',
      detailFailed: 'Failed to load detail.',
      selectFailed: 'Failed to select candidate.',
      confirmFailed: 'Failed to confirm match.',
      overrideFailed: 'Failed to override match.',
      ignoreFailed: 'Failed to ignore.',
      unmatchFailed: 'Failed to unmatch.',
      retryOrchestrationFailed: 'Failed to retry accounting orchestration.',
      createPaymentDraftFailed: 'Failed to create payment draft.',
      versionConflict: 'This line was updated by another member. Please refresh.',
      genericFailure: 'An error occurred.',
      readForbidden: 'You do not have permission to view bank matching.',
    },
    failureCodes: {
      BANK_MATCH_FEATURE_DISABLED: 'Bank matching is disabled.',
      FEATURE_DISABLED: 'Period reconciliation is disabled.',
      INVALID_PERIOD: 'Invalid period. Provide valid YYYY-MM-DD dates with periodFrom <= periodTo.',
      PERIOD_RANGE_TOO_LARGE: 'Period span exceeds the 12-month maximum.',
      UNSUPPORTED_CURRENCY_FILTER: 'Period reconciliation currently supports JPY only.',
      BANK_MATCH_NOT_FOUND: 'Statement line not found.',
      BANK_MATCH_NOT_READY: 'Matching is not ready.',
      BANK_MATCH_CANDIDATE_NOT_FOUND: 'Candidate not found.',
      BANK_MATCH_INVALID_TRANSITION: 'This action is not allowed in the current state.',
      BANK_MATCH_DECISION_CONFLICT: 'Another member updated concurrently; the operation conflicted.',
      BANK_MATCH_NO_CANDIDATES: 'No candidate or target specified.',
      BANK_MATCH_OVERRIDE_REQUIRED: 'Override permission is required.',
      BANK_MATCH_TARGET_NOT_FOUND: 'Match target was not found.',
      BANK_MATCH_TARGET_ALREADY_CONFIRMED: 'This target is already confirmed by another statement line.',
      BANK_MATCH_ORCH_RETRY_NOT_CONFIRMED: 'Only confirmed matches can retry orchestration.',
      BANK_MATCH_ORCH_RETRY_FLAG_DISABLED: 'Accounting orchestration is disabled; retry is blocked.',
      BANK_MATCH_PAYMENT_LINK_FEATURE_DISABLED: 'Bank match → payment draft is disabled.',
      BANK_MATCH_PAYMENT_DECISION_CONFLICT: 'The match was updated concurrently; refresh and try again.',
      BANK_MATCH_PAYMENT_SOURCE_NOT_CONFIRMED: 'Only a confirmed, active bank match can create a payment draft.',
      BANK_MATCH_PAYMENT_TARGET_NOT_ELIGIBLE: 'This match target cannot create a payment draft.',
      BANK_MATCH_PAYMENT_UNSUPPORTED_DIRECTION: 'Unsupported bank line direction for payment draft.',
      BANK_MATCH_PAYMENT_UNSUPPORTED_CURRENCY: 'Only JPY statement lines can create a payment draft.',
      BANK_MATCH_PAYMENT_INVALID_AMOUNT: 'Statement amount is invalid for a payment draft.',
      WORKSPACE_PERMISSION_DENIED: 'You do not have permission for this action.',
      IDEMPOTENCY_KEY_REQUIRED: 'An idempotency key is required.',
      VALIDATION_FAILED: 'Invalid input.',
    },
  },
  financialReports: {
    title: 'Financial Reports',
    subtitle: 'Read-only projections from immutable accounting facts',
    disclaimer: 'These reports are system-generated accounting projections and are not tax filings or statutory submissions.',
    filters: {
      reportType: 'Report type',
      basis: 'Basis',
      period: 'Accounting period',
      fiscalYear: 'Fiscal year',
      account: 'Account',
      comparativeMode: 'Comparative mode',
    },
    reportTypes: {
      trialBalance: 'Trial balance',
      balanceSheet: 'Balance sheet',
      profitAndLoss: 'Profit and loss',
      accountDetail: 'Account detail',
      fiscalYearSummary: 'Fiscal year summary',
    },
    basis: {
      liveLedger: 'Live ledger (provisional)',
      periodSnapshot: 'Period close snapshot (final)',
      yearEndSnapshot: 'Year-end snapshot (final)',
    },
    comparative: {
      none: 'None',
      previousPeriod: 'Previous period',
      previousFiscalYear: 'Previous fiscal year',
    },
    actions: { generate: 'Generate report' },
    export: {
      title: 'Export',
      disclaimer: 'This file is a system-generated accounting report export. It is not a statutory filing or tax submission document.',
      format: 'Export format',
      locale: 'Locale',
      generate: 'Generate export',
      download: 'Download',
      cancel: 'Cancel export',
      retry: 'Retry export',
      polling: 'Waiting for export worker…',
      status: 'Status',
      checksum: 'File checksum',
      sizeBytes: 'File size (bytes)',
      expiresAt: 'Expires at',
      noSnapshot: 'Generate a report snapshot before exporting',
      formats: { csv: 'CSV', xlsx: 'XLSX', json: 'JSON package' },
      statuses: {
        QUEUED: 'Queued',
        PROCESSING: 'Processing',
        COMPLETED: 'Completed',
        FAILED: 'Failed',
        CANCELLED: 'Cancelled',
        EXPIRED: 'Expired',
      },
      failureCodes: {
        EXPORT_STORAGE_UPLOAD_FAILED: 'Export file upload failed. Please retry later.',
        EXPORT_CHECKSUM_MISMATCH: 'Export file verification failed. Please retry.',
        EXPORT_ARTIFACT_EXPIRED: 'This export file has expired.',
      },
      errors: { failed: 'Export failed', noPermission: 'No export permission' },
    },
    header: {
      period: 'Period',
      basis: 'Basis',
      version: 'Version',
      checksum: 'Checksum',
    },
    balanceSheet: {
      equation: 'Assets {assets} = Liabilities {liabilities} + Equity {equity}',
    },
    profitAndLoss: {
      summary: 'Revenue {revenue} - Expense {expense} = Net income {netIncome}',
    },
    sections: { title: 'Sections' },
    table: {
      account: 'Account',
      amount: 'Amount',
      comparative: 'Comparative',
      variance: 'Variance',
    },
    errors: {
      generateFailed: 'Failed to generate report',
    },
  },
  japaneseFinancialStatements: {
    title: 'Japanese Financial Statements',
    subtitle: 'Japanese presentation generated from immutable financial report snapshots',
    disclaimer: 'This statement is a system-generated presentation of accounting report data. It is not a statutory filing, tax submission, audit report, or official filing document. Review the contents before use.',
    fields: {
      statementType: 'Statement type',
      amountUnit: 'Amount unit',
      snapshotId: 'Financial report snapshot ID',
      status: 'Status',
      profile: 'Profile',
      comparativeMode: 'Comparative mode',
      locale: 'Display locale',
      period: 'Period',
      checksum: 'Source checksum',
      sourceChecksum: 'Source report checksum',
      presentationChecksum: 'Presentation checksum',
      generatedAt: 'Generated at',
    },
    sourceSnapshot: {
      title: 'Source financial report snapshot',
    },
    profile: {
      profileVersion: 'Profile version',
      labelVersion: 'Label version',
      sectionOrderVersion: 'Section order version',
      disclaimerVersion: 'Disclaimer version',
    },
    comparative: {
      currentOnly: 'Current period only',
      currentAndPrior: 'Current and prior period',
    },
    types: {
      balanceSheet: 'Balance sheet',
      profitAndLoss: 'Profit and loss',
      trialBalance: 'Trial balance',
      accountDetail: 'Account detail',
      comparative: 'Comparative',
    },
    amountUnits: {
      yen: 'Yen',
      thousandYen: 'Thousand yen',
      millionYen: 'Million yen',
    },
    actions: {
      prepareSource: 'Generate source report',
      generate: 'Generate Japanese presentation',
    },
    statuses: {
      QUEUED: 'Queued',
      PROCESSING: 'Processing',
      COMPLETED: 'Completed',
      FAILED: 'Failed',
      CANCELLED: 'Cancelled',
      SUPERSEDED: 'Superseded',
    },
    table: {
      section: 'Section',
      line: 'Line',
      amount: 'Amount',
      comparative: 'Prior period',
      variance: 'Variance',
    },
    validation: {
      title: 'Validation results',
      infoCount: 'Info',
      warningCount: 'Warnings',
      errorCount: 'Errors',
      blockingCount: 'Blocking',
      severities: {
        INFO: 'Info',
        WARNING: 'Warning',
        ERROR: 'Error',
        BLOCKING: 'Blocking',
      },
    },
    unmapped: {
      title: 'Unmapped accounts',
      linkLabel: 'Review account mapping',
    },
    presentation: {
      metadata: 'Presentation metadata',
    },
    export: {
      title: 'Export',
      disclaimer: 'This file is a system-generated Japanese financial statement export. It is not a statutory filing or tax submission document.',
      format: 'Export format',
      locale: 'Locale',
      generate: 'Generate export',
      download: 'Download',
      polling: 'Waiting for export worker…',
      status: 'Status',
      checksum: 'File checksum',
      sizeBytes: 'File size (bytes)',
      blockedHint: 'Export is blocked until validation blocking issues are resolved.',
      formats: { csv: 'CSV', xlsx: 'XLSX', json: 'JSON package' },
      statuses: {
        QUEUED: 'Queued',
        PROCESSING: 'Processing',
        COMPLETED: 'Completed',
        FAILED: 'Failed',
        CANCELLED: 'Cancelled',
        EXPIRED: 'Expired',
      },
      errors: {
        failed: 'Export failed',
        noPermission: 'You do not have export permission',
        blocked: 'Export is blocked due to validation issues',
      },
    },
    warnings: {
      unmapped: 'Unmapped accounts are present. Review account report mapping.',
    },
    errors: {
      noBook: 'Accounting book not found',
      noPermission: 'You do not have permission to generate Japanese financial statements',
      generateSourceFailed: 'Failed to generate source report',
      generateFailed: 'Failed to generate Japanese presentation',
    },
  },
  feedback: {
    title: 'Send feedback',
    subject: 'Subject',
    selectPlaceholder: 'Select',
    details: 'Details',
    detailsPlaceholder: 'Describe the issue or suggestion...',
    attachments: 'Attachments (optional, up to 3)',
    submitting: 'Submitting...',
    incomplete: 'Please complete all required fields.',
    submitted: 'Feedback submitted. We will review it as soon as possible.',
    submitFailed: 'Submission failed',
    thread: {
      me: 'Me',
      operations: 'Operations',
      user: 'User',
    },
  },
  feedbackLabels: {
    subjects: {
      bug: 'Bug report',
      feature: 'Feature request',
      billing: 'Billing and quota',
      account: 'Account and security',
      other: 'Other',
    },
    statuses: {
      OPEN: 'Open',
      IN_PROGRESS: 'In progress',
      RESOLVED: 'Resolved',
      CLOSED: 'Closed',
    },
  },
  emailEditor: {
    bold: 'Bold',
    italic: 'Italic',
    underline: 'Underline',
    strike: 'Strikethrough',
    paragraph: 'Paragraph',
    heading2: 'Heading 2',
    heading3: 'Heading 3',
    textColor: 'Text color',
    highlight: 'Highlight',
    clearFormatting: 'Clear color',
    clearHighlight: 'Clear highlight',
    alignLeft: 'Align left',
    alignCenter: 'Align center',
    alignRight: 'Align right',
    bulletList: 'Bullet list',
    numberedList: 'Numbered list',
    link: 'Link',
    insertTable: 'Insert table',
    undo: 'Undo',
    redo: 'Redo',
    linkPrompt: 'Link URL',
  },
  addressLocation: {
    label: 'Address',
    areaCodePlaceholder: 'Enter 7-digit postal code',
    prefixPlaceholder: 'Prefecture city area',
    addressPlaceholder: 'Street, building, floor, room number, etc.',
    loading: 'Looking up address...',
    notFound: 'No address was found.',
  },
  employeeManagement: {
    menu: { employees: 'Employees', retired: 'Retired employees', tags: 'Tags' },
    list: { title: 'Employees', description: 'Manage employee profiles, assignment, and account issue status.', empty: 'No employees yet.' },
    retired: { title: 'Retired employees', description: 'Review employees marked as retired.', badge: 'Retired', retiredNotice: 'Retirement date: {date} / Reason: {reason}' },
    tags: { title: 'Tags', description: 'Manage tags assigned to employees.', add: 'Add tag', edit: 'Edit tag', empty: 'No tags yet.', dialogDescription: 'Set tag name, color, and remarks.', deleteTitle: 'Delete tag?', importTitle: 'Import tags', importDescription: 'Bulk import tag names and colors.', exportTitle: 'Export tags', exportDescription: 'Select tag fields to export.' },
    detail: { title: 'Employee detail', subtitle: 'Employee profile', profile: 'Profile', employment: 'Employment', location: 'Address', account: 'Account', notFound: 'Employee not found.' },
    dialog: { addEmployee: 'Add employee', editEmployee: 'Edit employee', employeeDescription: 'Enter employee profile and assignment information.' },
    actions: { addEmployee: 'Add employee', issueAccount: 'Issue account', resetPassword: 'Reset password', retire: 'Retire', restore: 'Restore', bulkInvite: 'Bulk invite', bulkEmail: 'Bulk email', selectVisible: 'Select visible employees' },
    summary: { total: 'Employees', dimension: 'Dimension', dimensions: { employmentType: 'Employment type', workStatus: 'Work status', employmentStatus: 'Employment status', position: 'Position', jobType: 'Job type' } },
    filters: { keyword: 'Keyword' },
    columns: { employee: 'Employee', tags: 'Tags' },
    fields: { employeeNo: 'Employee no.', avatarUrl: 'Avatar', birthDate: 'Birth date', gender: 'Gender', name: 'Name', nameKanji: 'Name (kanji)', familyName: 'Family name', givenName: 'Given name', nameKana: 'Name (katakana)', familyNameKana: 'Family name (katakana)', givenNameKana: 'Given name (katakana)', hiredOn: 'Hire date', retiredOn: 'Retirement date', retiredReason: 'Retirement reason', hireType: 'Hire type', phone: 'Phone', email: 'Email', location: 'Address', office: 'Office', department: 'Department', workStatus: 'Work status', employmentType: 'Employment type', employmentStatus: 'Employment status', jobType: 'Job type', position: 'Position', insuranceNumber: 'Insurance number', insuranceSymbol: 'Insurance symbol', remarks: 'Remarks', accountIssueStatus: 'Account issue status', userId: 'System account ID', lastLoginAt: 'Last login', tagName: 'Tag name', tagColor: 'Tag color', tagColorDescription: 'Remarks', employeeCount: 'Employees' },
    common: { unselected: 'Unset', ageSuffix: ' yrs', personSuffix: ' people' },
    validation: { required: 'This field is required.', email: 'Enter a valid email address.', japanesePhone: 'Enter a valid Japanese phone number.', maxLength: 'Enter {max} characters or fewer.' },
    avatar: { upload: 'Upload avatar', uploading: 'Uploading...', clear: 'Remove', hint: 'PNG, JPG, or WebP up to 2 MB. Crop to a square before saving.', cropTitle: 'Crop employee avatar', confirmCrop: 'Use avatar', zoom: 'Zoom', invalid: 'Upload a valid image file.', tooLarge: 'Avatar image must be 2 MB or smaller.', uploadFailed: 'Failed to upload avatar', uploaded: 'Avatar uploaded' },
    bulkInvite: { confirmTitle: 'Send bulk invitations?', confirmDescription: 'Invitation emails will be sent to {count} employees.' },
    bulkEmail: { title: 'Send bulk email', description: 'Send a message to the selected employee email addresses.', subject: 'Subject', message: 'Message' },
    resetPassword: { title: 'Reset password', description: 'Enter the new sign-in password.', newPassword: 'New password', confirmPassword: 'Confirm new password', passwordPlaceholder: 'At least 8 characters', passwordMin: 'Password must be at least 8 characters.', passwordMismatch: 'The two passwords do not match.' },
    import: { title: 'Import employees', description: 'Bulk import employee master data.', employeeNoRequired: 'Unique employee identifier.', familyNameRequired: 'Family name for name (kanji) is required.', givenNameRequired: 'Given name for name (kanji) is required.', familyNameKanaRequired: 'Family name for name (katakana) is required.', givenNameKanaRequired: 'Given name for name (katakana) is required.', tagNameRequired: 'Tag name is required.' },
    export: { title: 'Export employees', description: 'Select fields to export.' },
    retire: { title: 'Retire employee', description: 'Enter the retirement date and reason.' },
    history: { title: 'Change history', actions: { CREATE: 'Created', UPDATE: 'Updated', RETIRE: 'Retired', RESTORE: 'Restored' } },
    messages: { saved: 'Saved', deleted: 'Deleted', accountIssued: 'Account invitation sent', passwordResetSent: 'Password reset', passwordResetRequested: 'Password reset', retired: 'Employee retired', restoreTitle: 'Restore this employee?', restored: 'Employee restored', emailRequiredForAccount: 'This employee has no email address, so an account cannot be issued.', selectEmployeesRequired: 'Select employees first.', bulkInviteAccepted: 'Bulk invitations sent ({count} succeeded, {failed} failed).', bulkInviteFailed: 'Bulk invitations could not be sent.', bulkEmailAccepted: 'Bulk email accepted.' },
    enums: {
      gender: { MALE: 'Male', FEMALE: 'Female', OTHER: 'Other', UNDISCLOSED: 'Unset' },
      hireType: { NEW_GRADUATE: 'New graduate', EXPERIENCED: 'Experienced' },
      employmentType: { EXECUTIVE: 'Executive', REGULAR: 'Regular employee', SECONDMENT_IN: 'Seconded-in employee', CONTRACT: 'Contract employee', SHOKUTAKU: 'Shokutaku employee', PART_TIME: 'Part-time / arubaito', FREELANCE: 'Freelance / contractor', TEMPORARY_DISPATCH_IN: 'Dispatched-in employee', DISPATCH_STAFF: 'Dispatch staff', TRAINEE: 'Trainee' },
      workStatus: { NORMAL_DOMESTIC: 'Normal work (domestic)', NORMAL_OVERSEAS: 'Normal work (overseas)', LEAVE_PRIVATE_INJURY: 'Leave (illness/injury)', LEAVE_CHILDCARE: 'Leave (childcare)', LEAVE_CAREGIVING: 'Leave (caregiving)', LEAVE_OTHER: 'Leave (other)', WORK_RESTRICTED: 'Work restricted' },
      employmentStatus: { ACTIVE: 'Active', SECONDED: 'Seconded', CONTRACT_ENDED: 'Contract ended', RETIRED: 'Retired' },
      position: { STAFF: 'Staff', STAFF_J: 'Staff (J)', STAFF_M: 'Staff (M)', STAFF_H: 'Staff (H)', MANAGER: 'Manager', MANAGER_J: 'Manager (J)', MANAGER_M: 'Manager (M)', MANAGER_H: 'Manager (H)', EXECUTIVE: 'Executive', OTHER: 'Other' },
      jobType: { MANAGEMENT: 'Management', PROFESSIONAL_TECHNICAL: 'Professional / technical', CLERICAL: 'Clerical', SALES: 'Sales', SERVICE: 'Service', SECURITY: 'Security', AGRICULTURE_FORESTRY_FISHERY: 'Agriculture / forestry / fishery', PRODUCTION: 'Production', TRANSPORT_MACHINE: 'Transport / machine operation', CONSTRUCTION_MINING: 'Construction / mining', CARRYING_CLEANING_PACKAGING: 'Carrying / cleaning / packaging', OTHER: 'Other' },
      accountIssueStatus: { NOT_ISSUED: 'Not issued', ISSUED: 'Issued', ACTIVATED: 'Activated', SUSPENDED: 'Suspended' },
      userStatus: { ACTIVE: 'Active', INACTIVE: 'Inactive', DELETED: 'Deleted' },
    },
  },
  organizationHealth: {
    tabs: { profile: 'Basic information', offices: 'Offices', departments: 'Departments' },
    profile: { title: 'Company information', description: 'Maintain legal entity and primary contact details.', logo: 'Company logo', logoHint: 'Read-only; sourced from tenant branding.', employeeCount: 'Employees' },
    offices: { title: 'Offices', description: 'Maintain office contacts and labor insurance details.', add: 'Add office', edit: 'Edit office', empty: 'No offices', dialogDescription: 'Enter the office details.', importTitle: 'Import offices', importDescription: 'Bulk add offices using the documented field names.', exportTitle: 'Export offices', exportDescription: 'Select fields to export.' },
    departments: { title: 'Department hierarchy', description: 'Maintain parent relationships and display order.', add: 'Add department', edit: 'Edit department', empty: 'No departments', root: 'Top level', memberCountSuffix: ' ({count} people)', dialogDescription: 'Set the name, parent, and display order.', importTitle: 'Import departments', importDescription: 'Parent departments must exist or appear earlier in the file.', exportTitle: 'Export departments', exportDescription: 'Export the current hierarchy.' },
    fields: { companyName: 'Company name', representativeName: 'Representative', corporateNumber: 'Corporate number', location: 'Address', areaCode: 'Postal code', postalCode: 'Postal code', prefecture: 'Prefecture', city: 'City', area: 'Area', address: 'Street / building', addressLine: 'Address', contactName: 'Contact', contactDepartment: 'Contact department', contactPhone: 'Contact phone', contactEmail: 'Contact email', websiteUrl: 'Website URL', industry: 'Industry', foundedOn: 'Founded on', name: 'Office name', laborInsuranceNumber: 'Labor insurance number', departmentName: 'Department name', parentDepartment: 'Parent department', sortOrder: 'Order' },
    import: { dropHint: 'Drop a CSV/XLSX file or choose one.', choose: 'Choose file', preview: 'Preview', previewCount: 'Showing up to 20 rows', progress: 'Import progress', missing: 'Missing required columns', requiredFields: 'Required fields', downloadTemplate: 'Download template', officeNameRequired: 'The name used to identify the office. This field is required.', departmentNameRequired: 'The name used to identify the department. This field is required.' },
    export: { columns: 'Export fields', selectAll: 'Select all', deselectAll: 'Clear all' },
    messages: { saved: 'Saved', deleted: 'Deleted', deleteTitle: 'Confirm deletion' },
  },
  documentSummary: {
    title: 'Voucher summary',
    subtitle: 'Finance summary for completed vouchers',
    noWorkspace: 'Select a workspace first.',
    noWorkspaceHint: 'An active workspace is required to view voucher summary.',
    readOnly: 'You do not have permission to view voucher summary.',
    readOnlyHint: 'document_intake.read permission is required.',
    empty: 'No completed vouchers match the current filters.',
    split: {
      progress: 'Split {index}/{count}',
    },
    total: '{total} total (page {page}, {pageSize} per page)',
    errors: {
      loadFailed: 'Failed to load voucher summary.',
    },
    stats: {
      title: 'Annual summary',
      dateRange: 'Summary transaction dates',
      fiscalYear: 'Fiscal year',
      fiscalYearOption: 'FY {year}',
      apply: 'Refresh summary',
      syncList: 'Match list dates',
      count: 'Count',
      totalAmount: 'Total incl. tax',
      averageAmount: 'Average amount',
      byAccount: 'By account',
      byMonth: 'Amount by month',
      monthLabel: '{year}/{month}',
      emptyChart: 'No summary data',
      unknownAccount: 'Unassigned account',
    },
    filters: {
      keyword: 'Keyword',
      keywordPlaceholder: 'Counterparty / description',
      documentDate: 'Transaction date',
      account: 'Account',
      taxRate: 'Tax rate',
      invoicePresence: 'Invoice',
      invoicePresent: 'Present',
      invoiceAbsent: 'Absent',
      allInvoicePresence: 'All',
      amount: 'Amount',
      amountMin: 'Min amount',
      amountMax: 'Max amount',
      amountMinPlaceholder: 'Min',
      amountMaxPlaceholder: 'Max',
      allAccounts: 'All accounts',
      allTaxRates: 'All tax rates',
      apply: 'Apply',
      reset: 'Reset',
    },
    taxRate: {
      '10': '10%',
      '8': '8% (reduced)',
      '10_NO_INVOICE': '10% (no invoice number)',
      '8_NO_INVOICE': '8% (reduced, no invoice number)',
      NON_TAXABLE: 'Non-taxable',
      OUT_OF_SCOPE: 'Out of scope',
      EXEMPT: 'Unknown',
      UNKNOWN: 'Unknown',
      multiple: 'Multiple rates',
    },
    columns: {
      documentDate: 'Transaction date',
      documentNumber: 'Document number',
      counterparty: 'Counterparty',
      description: 'Description',
      account: 'Account',
      accountCode: 'Account code',
      accountName: 'Account name',
      taxRate: 'Tax rate',
      amount: 'Amount incl. tax',
      amountMinor: 'Amount (minor)',
      paymentMethod: 'Payment method',
      invoiceRegistrationNumber: 'Registration number',
      cardLastFour: 'Card last 4',
      currency: 'Currency',
      filename: 'Intake filename',
      actions: 'Actions',
      documentIntakeId: 'Intake ID',
      documentSegmentId: 'Segment ID',
    },
    actions: {
      detail: 'Detail',
    },
    pagination: {
      prev: 'Previous',
      next: 'Next',
    },
    export: {
      button: 'Export',
      title: 'Export voucher summary',
      description: 'Export the current filter result as CSV or Excel (max 5000 rows).',
      columns: 'Columns',
      selectAll: 'Select all',
      deselectAll: 'Clear all',
      csv: 'CSV',
      xlsx: 'Excel',
      tooLarge: 'The export exceeds the row limit',
    },
  },
  documentIntake: {
    inbox: {
      title: 'Document Intake Inbox',
      subtitle: 'Intake documents in the active workspace',
      noWorkspace: 'Select a workspace first.',
      readOnly: 'You do not have permission to view the document intake inbox.',
      emptyTitle: 'No intake documents yet',
      emptyDescription: 'Uploaded receipts and invoices will appear here.',
      total: '{total} total (page {page}, {pageSize} per page)',
      bulkApprove: {
        action: 'Approve selected',
        running: 'Approving...',
        select: 'Select documents awaiting confirmation',
        selectAll: 'Select all awaiting confirmation on this page',
        failed: '{count} documents could not be approved.',
      },
      foundation: {
        columns: {
          file: 'File',
          processing: 'Processing',
          accounting: 'Accounting',
          documentType: 'Document type',
          counterparty: 'Counterparty',
          date: 'Transaction date',
          amount: 'Amount',
          taxRate: 'Tax rate',
          invoiceRegistrationNumber: 'Registration no.',
          account: 'Account',
          actions: 'Actions',
        },
        taxRate: {
          multiple: 'Multiple rates',
        },
        split: {
          badge: 'Split {count}',
          expand: 'Show split lines',
          collapse: 'Hide split lines',
        },
        file: {
          multipleDocuments: 'Multiple documents',
        },
        processing: {
          UPLOADED: 'Received',
          QUEUED: 'Queued',
          PROCESSING: 'Processing',
          REPROCESSING: 'Reprocessing',
          NEEDS_REVIEW: 'Needs review',
          COMPLETED: 'Completed',
          FAILED: 'Processing error',
          CANCELLED: 'Cancelled',
          UNKNOWN: 'Unknown',
        },
        processingStage: {
          PREPROCESSING: 'Image preprocessing',
          OCR: 'OCR in progress',
          FIELD_EXTRACTION: 'Field extraction',
          REVIEW: 'Awaiting review',
          COMPLETED: 'Processing complete',
          FAILED: 'Processing failed',
          CANCELLED: 'Processing cancelled',
          LEGACY_PARSING: 'Legacy parsing',
          UNKNOWN: 'Unknown',
        },
        review: {
          NOT_READY: 'Not ready',
          NEEDS_REVIEW: 'Unreviewed',
          PARTIALLY_REVIEWED: 'Partially reviewed',
          COMPLETED: 'Reviewed',
          REJECTED: 'Rejected',
          NON_ACCOUNTING: 'Non-accounting',
          MIXED: 'Partially reviewed',
          UNKNOWN: 'Pending review (unaggregated)',
          progress: '{confirmed}/{total} reviewed',
        },
        accounting: {
          NOT_STARTED: 'Not started',
          PROCESSING: 'Accounting in progress',
          TRANSACTION_REVIEW_REQUIRED: 'Transaction review required',
          DOWNSTREAM_PROCESSING: 'Journal processing',
          COMPLETED: 'Journal confirmed',
          FAILED: 'Error',
          AMENDMENT_REQUIRED: 'Amendment required',
          MIXED: 'Partially complete',
          UNKNOWN: 'Unknown',
        },
        segments: {
          zero: '0',
          count: '{count}',
          updating: 'Segment update in progress',
          updatingShort: 'Updating',
        },
        amount: {
          multiple: 'Multiple',
        },
        actions: {
          review: 'Review',
          details: 'Details',
          retry: 'Retry',
          reprocess: 'Reprocess',
          cancelProcessing: 'Cancel processing',
          cancelFailed: 'Failed to cancel processing.',
          retryFailed: 'Failed to retry processing.',
        },
        rowAriaLabel: 'Open {filename}',
        tooltips: {
          reviewStaleMetadata: 'Refreshing aggregates',
          reviewMixed: 'Reviewed {confirmed} · Rejected {rejected} · Total {total}',
          accountingMasked: 'Accounting details are not visible',
          accountingSegmentPartial: '{completed} of {total} complete',
          accountingSegmentReviewRequired: '{count} need review',
          partialAmount: 'Some amounts could not be loaded',
          processingFailedGeneric: 'Processing failed',
        },
        foreignCurrency: {
          badge: 'Overseas document · enter the yen amount',
        },
        failureCodes: {
          PROCESSING_FAILED: 'Processing failed',
          OCR_FAILED: 'OCR failed',
          EXTRACTION_FAILED: 'Extraction failed',
        },
      },
    },
    upload: {
      title: 'Upload document',
      subtitle: 'Submit receipts and invoices to the workspace',
      backToInbox: 'Back to inbox',
      dropTitle: 'Click or drag and drop a file',
      dropHint: 'PDF, JPEG, PNG, HEIC (max {maxMb} MB)',
      browse: 'Choose file',
      progress: 'Uploading {progress}%',
      ownership: 'Ownership',
      limits: 'Maximum file size: {maxMb} MB',
      allowedTypes: 'Supported: PDF, JPEG, PNG, HEIC',
      submit: 'Upload and parse',
      uploading: 'Uploading...',
      viewInbox: 'View inbox',
      privacyNotice: 'Uploaded documents are stored securely for accounting and access is restricted by permission.',
      aiNotice: 'AI field extraction is a suggestion only. Review content before final approval.',
      invalidType: 'Unsupported file type.',
      tooLarge: 'File exceeds the {maxMb} MB limit.',
      sessionFailed: 'Failed to create upload session.',
      failed: 'Upload failed.',
      noPermission: 'You do not have permission to upload documents.',
      csvRoutedToBankImport: 'CSV files must be uploaded from the bank statement import page.',
    },
    review: {
      title: 'Document review',
      backToInbox: 'Back to inbox',
      originalPreview: 'Original preview',
      originalRestricted: 'You do not have permission to view the original file.',
      previewLoading: 'Loading preview...',
      parsedFields: 'Parsed and suggested fields',
      aiNotice: 'AI suggestions require confirmation before approval.',
      warnings: 'Warnings',
      note: 'Reviewer note',
      weakGrounding: {
        needsConfirm: 'Needs confirmation',
      },
      accountingDraft: {
        create: 'Create accounting draft',
        open: 'Open accounting draft',
        creating: 'Creating draft...',
        featureDisabled: 'Document accounting demo is not enabled.',
        notApproved: 'Only approved documents can create an accounting draft.',
      },
      reprocessQuery: {
        permissionDenied: 'Reprocess is not available right now.',
        segmentModeRequired: 'Open segment review to reprocess this document.',
      },
      foundationAccounting: {
        title: 'Foundation accounting',
        subtitle: 'Business transaction, classification, and journal draft after segment confirmation.',
        accountingStatus: {
          NOT_STARTED: 'Not started',
          CLASSIFICATION_PENDING: 'Classification pending',
          CLASSIFICATION_REVIEW_REQUIRED: 'Classification review required',
          CLASSIFICATION_CONFIRMED: 'Classification confirmed',
          CLASSIFICATION_TERMINAL: 'Classification terminal',
          JOURNAL_REVIEW_REQUIRED: 'Journal draft review required',
          JOURNAL_CONFIRMED: 'Journal draft confirmed',
          JOURNAL_TERMINAL: 'Journal draft terminal',
        },
        bridge: {
          empty: 'Bridge not requested',
          status: {
            PENDING: 'Bridge queued',
            PROCESSING: 'Bridge processing',
            SUCCEEDED: 'Bridge succeeded',
            FAILED: 'Bridge failed',
            INELIGIBLE: 'Bridge ineligible',
            CANCELLED: 'Bridge cancelled',
            AMENDMENT_REQUIRED: 'Amendment required',
          },
        },
        transaction: {
          title: 'Business transaction',
          total: 'Total {amount} JPY',
          confirm: 'Confirm transaction',
          pending: 'Transaction draft is being created. Refresh shortly.',
          empty: 'The transaction fact has not been generated yet.',
          splitList: 'Split transactions',
        },
        classification: {
          title: 'Classification',
          confirm: 'Confirm classification',
          request: 'Re-request classification',
          taxTreatment: 'Tax treatment',
        },
        journal: {
          title: 'Journal draft',
          date: 'Journal date',
          debitTotal: 'Debit total',
          creditTotal: 'Credit total',
          balance: 'Balance',
          balanced: 'Balanced',
          unbalanced: 'Not balanced',
          reviewedAt: 'Reviewed at',
          confirmedHint: 'Journal draft confirmed',
          reviewRequiredHint: 'Journal draft review is required',
          confirm: 'Confirm journal',
          openDocumentDetail: 'Open document detail',
        },
        refreshing: 'Refreshing…',
        retry: 'Retry',
        failure: {
          retryStatus: 'Recheck status',
          BRIDGE_FAILED: {
            title: 'Accounting bridge failed',
          },
          BRIDGE_INELIGIBLE: {
            title: 'Not eligible for accounting bridge',
            message: 'This document is not eligible for accounting bridge processing.',
          },
          BRIDGE_CANCELLED: {
            title: 'Accounting bridge cancelled',
            message: 'Accounting bridge processing was cancelled.',
          },
          BRIDGE_AMENDMENT_REQUIRED: {
            title: 'Amendment required',
            message: 'Review or amend the document confirmation before accounting can continue.',
          },
          TRANSACTION_FAILED: {
            title: 'Transaction failed',
            message: 'The business transaction cannot continue. Check the document review result.',
          },
          CLASSIFICATION_FAILED: {
            title: 'Classification failed',
            message: 'Classification cannot continue. Check the accounting status.',
          },
          JOURNAL_FAILED: {
            title: 'Journal draft failed',
            message: 'The journal draft cannot continue. Check the accounting status.',
          },
          bridge: {
            generic: 'Accounting bridge could not complete. Recheck the status or review the document.',
            codes: {
              BRIDGE_PAYLOAD_CONFLICT: 'Bridge payload conflict. Review the confirmed document fields.',
              PROCESSING_FAILED: 'Bridge processing failed. Recheck the status shortly.',
            },
          },
        },
        error: {
          loadFailed: 'Could not load accounting data. Try again.',
          confirmTransactionFailed: 'Could not confirm the transaction.',
          confirmClassificationFailed: 'Could not confirm the classification.',
          confirmJournalFailed: 'Could not confirm the journal draft.',
          permissionDenied: 'You do not have permission for this accounting action.',
          versionConflict: 'Accounting data changed. Refresh and try again.',
          amendmentRequired: 'This segment needs amendment before accounting can continue.',
          workerDisabled: 'Accounting processing is not available in this environment.',
          notFound: 'Accounting data was not found.',
          periodLocked: 'The accounting period is locked.',
        },
        workflow: {
          v2: {
            navLabel: 'Accounting workflow',
            awaitingSegmentConfirm: 'Accounting processing starts after document confirmation',
            unknownStatus: 'Status could not be determined',
            stages: {
              BRIDGE: 'Accounting bridge',
              TRANSACTION: 'Transaction',
              CLASSIFICATION: 'Classification',
              JOURNAL: 'Journal draft',
            },
            status: {
              BRIDGE: {
                NOT_STARTED: 'Not started',
                PROCESSING: 'Processing',
                REVIEW_REQUIRED: 'Review required',
                CONFIRMED: 'Confirmed',
                FAILED: 'Failed',
              },
              TRANSACTION: {
                NOT_STARTED: 'Not started',
                PROCESSING: 'Processing',
                REVIEW_REQUIRED: 'Review required',
                CONFIRMED: 'Confirmed',
                FAILED: 'Failed',
              },
              CLASSIFICATION: {
                NOT_STARTED: 'Not started',
                PROCESSING: 'Processing',
                REVIEW_REQUIRED: 'Review required',
                CONFIRMED: 'Confirmed',
                FAILED: 'Failed',
              },
              JOURNAL: {
                NOT_STARTED: 'Not started',
                PROCESSING: 'Processing',
                REVIEW_REQUIRED: 'Review required',
                CONFIRMED: 'Journal confirmed',
                FAILED: 'Failed',
              },
            },
            description: {
              BRIDGE: {
                NOT_STARTED: 'Waiting for document confirmation.',
                PROCESSING: 'Creating the accounting bridge.',
                REVIEW_REQUIRED: 'Bridge review is required.',
                CONFIRMED: 'Accounting bridge completed.',
                FAILED: 'Accounting bridge did not complete.',
              },
              TRANSACTION: {
                NOT_STARTED: 'Waiting for the accounting bridge.',
                PROCESSING: 'Creating the business transaction.',
                REVIEW_REQUIRED: 'Confirm the business transaction.',
                CONFIRMED: 'Business transaction confirmed.',
                FAILED: 'Business transaction cannot continue.',
              },
              CLASSIFICATION: {
                NOT_STARTED: 'Waiting for transaction confirmation.',
                PROCESSING: 'Generating classification.',
                REVIEW_REQUIRED: 'Confirm the classification.',
                CONFIRMED: 'Classification confirmed.',
                FAILED: 'Classification cannot continue.',
              },
              JOURNAL: {
                NOT_STARTED: 'Waiting for classification confirmation.',
                PROCESSING: 'Generating the journal draft.',
                REVIEW_REQUIRED: 'Review the journal draft.',
                CONFIRMED: 'Journal draft confirmed.',
                FAILED: 'Journal draft cannot continue.',
              },
            },
          },
          status: {
            generic: {
              NOT_STARTED: 'Not started',
              PROCESSING: 'Processing',
              REVIEW_REQUIRED: 'Review required',
              COMPLETED: 'Completed',
              FAILED: 'Failed',
              DISABLED: 'Not available',
            },
            JOURNAL: {
              COMPLETED: 'Journal confirmed',
            },
            POSTING: {
              DISABLED: 'Posting not available here',
              NOT_STARTED: 'Not started',
            },
          },
        },
      },
      workspace: {
        foundationBadge: 'Foundation review',
        accountingRail: {
          expand: 'Expand accounting flow',
          collapse: 'Collapse accounting flow',
        },
      },
      preview: {
        ocr: 'OCR image',
        original: 'Original file',
        mineruDebug: 'MINERU',
        geminiRequestDebug: 'Gemini request',
        geminiResponseDebug: 'Gemini response',
        debugEmpty: 'No debug data available.',
        debugReconstructedHint: 'Reconstructed from saved OCR/candidate data; reprocess to persist full request and response artifacts.',
        originalFullUploadHint: 'Original file is the full uploaded image. The OCR tab shows the current segment crop, so the visible area may differ.',
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
        rotateLeft: 'Rotate left',
        rotateRight: 'Rotate right',
        fitWidth: 'Fit width',
        reset: 'Reset zoom',
        previousPage: 'Previous page',
        nextPage: 'Next page',
        loadFailed: 'Could not load preview.',
        permissionDenied: 'You do not have permission to view the original file.',
        ocrLoadFailed: 'Could not load OCR image',
        ocrReload: 'Reload OCR image',
        ocrUrlExpiredHint: 'The OCR image link may have expired',
        noFieldLocation: 'Location data is not available',
        focusActive: 'Showing the selected item',
        invalidCoordinates: 'Coordinate data is invalid',
      },
      summary: {
        legacy: 'Legacy document format',
        empty: 'No active segments',
        progress: '{confirmed}/{total} confirmed',
        needsReview: '{count} need review',
        excluded: '{count} excluded',
        rejected: '{count} rejected',
        duplicate: '{count} duplicate',
        nonAccounting: '{count} non-accounting',
        processing: '{count} processing',
        selectedOutcome: 'Selected: {outcome}',
      },
      legacyBanner: {
        badge: 'Legacy',
        title: 'Legacy document format',
        description: 'This document is shown in the legacy review screen.',
      },
      tabs: {
        document: 'Document',
        fields: 'Fields',
        validation: 'Validation',
        accounting: 'Accounting',
      },
      validation: {
        title: 'Validation',
        empty: 'No validation items.',
        failures: 'Failures',
        warnings: 'Warnings',
        passed: 'Passed ({count})',
        unknown: 'Unknown state',
        fallback: 'Validation issue ({ruleCode})',
        overridden: 'Exception approved',
        locateField: 'Go to field',
        locateDocument: 'Locate on document',
        noLocation: 'Location data is not available for this field.',
        showPassed: 'Show passed',
        hidePassed: 'Hide passed',
        summary: {
          fail: 'FAIL {count}',
          overridden: 'Exceptions {count}',
          warning: 'WARNING {count}',
          pass: 'PASS {count}',
          unknown: 'UNKNOWN {count}',
        },
        messages: {
          required_total_missing: 'Total amount is missing.',
          required_date_missing: 'Document date is missing.',
          supplier_missing: 'Counterparty is missing.',
          subtotal_plus_tax_equals_total: 'Subtotal plus tax does not equal total.',
          date_in_future: 'Document date is in the future.',
          date_too_old: 'Document date is older than 10 years.',
          service_end_before_start: 'Service end date is before start date.',
          registration_number_format_invalid: 'Registration number format is invalid.',
          qualified_invoice_without_registration_number: 'Qualified invoice requires registration number.',
          possible_full_card_number: 'Possible full card number detected.',
          sensitive_data_redacted: 'Sensitive data was redacted.',
        },
      },
      confirmGuard: {
        unsavedChanges: 'Unsaved changes',
        saveBeforeConfirm: 'Save your changes before confirming.',
        unresolvedFailures: 'Unresolved validation failures remain. Fix them or approve an exception if permitted.',
        unknownValidation: 'Some validation results could not be interpreted. Review validation before confirming.',
        processing: 'Processing is still running.',
        alreadyConfirmed: 'This segment is already confirmed.',
        overrideSyncPending: 'Validation override is syncing. Wait for save to finish before confirming.',
      },
      missingDate: {
        title: 'No document date',
        message: 'Confirming without a date may require manual review of accounting dates and journal dates. Continue without a date?',
        cancel: 'Cancel',
        confirmWithoutDate: 'Confirm without date',
      },
      override: {
        title: 'Approve as exception',
        message: 'This validation error will be treated as an exception. Enter a reason.',
        comment: 'Reason',
        submit: 'Save exception',
        action: 'Approve exception',
        forbidden: 'You do not have permission to approve validation exceptions.',
      },
      error: {
        versionConflict: 'Another user updated this review. Reload the latest content.',
        candidateSuperseded: 'Extraction results were updated. Reloading the latest content.',
        alreadyConfirmed: 'This segment is already confirmed.',
        invalidDate: 'Document date is required or must be confirmed without date.',
        locationUnavailable: 'OCR location is not available for this field.',
      },
      actions: {
        more: 'More',
      },
    },
    reviewMode: {
      segmentLoadFailed: 'Could not load document segments. Please try again later.',
      retry: 'Retry',
      unavailable: 'This document cannot be reviewed right now.',
      invalidSelection: 'The selected document is not available.',
      processing: {
        waitingForSegments: 'Review will be available after processing completes.',
      },
    },
    segmentReview: {
      segmentsTitle: 'Segments',
      previewTitle: 'Image & overlay',
      processing: 'Processing recognition results…',
      candidateStale: 'Recognition updated. Reload before confirming.',
      reload: 'Reload',
      operations: {
        editSplit: 'Edit split',
        manualSplit: 'Manual split',
        recrop: 'Recrop',
        merge: 'Merge segments',
        reopen: 'Reopen review',
        editBoundary: 'Edit boundary',
        dialogDescription: 'Adjust segment boundaries on the full page. OCR will be reallocated and fields re-extracted after submit.',
        selectSegments: 'Select segments to merge',
        reasonCode: 'Reason code',
        reasonComment: 'Reason comment',
        submit: 'Submit boundary change',
        overlapWarning: 'Some boxes overlap too much. Adjust before submitting.',
        phaseCreatingRevision: 'Creating revision…',
        phaseGeneratingCrop: 'Generating crop images…',
        phaseReallocatingOcr: 'Reallocating OCR…',
        phaseExtractingFields: 'Re-extracting fields…',
        phaseFailed: 'Segment operation failed. Please retry.',
      },
      list: {
        pageSegment: 'Page {page} · Segment {index}',
        failCount: 'FAIL {count}',
        warningCount: 'WARN {count}',
        manualEdit: 'Manual boundary edit',
      },
      outcome: {
        NEEDS_REVIEW: 'Needs review',
        CONFIRMED: 'Confirmed',
        REJECTED: 'Rejected',
        DUPLICATE: 'Duplicate',
        NON_ACCOUNTING: 'Non-accounting',
        MANUALLY_EXCLUDED: 'Excluded',
        PROCESSING: 'Processing',
      },
      field: {
        documentNumber: 'Document number',
        memo: 'Memo',
        subtotal: 'Subtotal (JPY)',
        tax: 'Tax (JPY)',
        taxRate: 'Tax rate',
        taxRateUnset: 'Unset',
        taxRate10: '10%',
        taxRate8: '8% (reduced)',
        taxRateExempt: 'Unknown',
        paymentMethod: 'Payment method',
        currency: 'Currency',
        registrationNumber: 'Registration number',
        taxSummary: 'Tax summary',
        suggestedAccount: 'Suggested account',
        accountHint: 'Account',
        cardLastFour: 'Card last four',
        lineItemsEmpty: 'No line items detected.',
        lineItemsTotal: '{count} line items total',
        confidence: 'Confidence {value}%',
        source: 'Source: {value}',
        moreCandidates: '{count} more candidates',
        locate: 'Locate',
        lowConfidence: 'Low confidence: {fields}',
      },
      taxBreakdown: {
        title: 'Tax rate breakdown',
        amount: 'Amount',
        taxAmount: 'Tax',
        addRow: 'Add row',
        removeRow: 'Remove row',
        empty: 'No tax breakdown yet. Add a row if needed.',
        exceedsTotal: 'Tax breakdown total (amount + tax) cannot exceed the document total.',
      },
      voucherSplit: {
        open: 'Split voucher',
        edit: 'Edit split',
        detailsTitle: 'Split lines',
        dialogTitle: 'Split voucher',
        dialogDescription: 'Set the tax-inclusive amount, tax rate, and account on each row. The rows must add up to the document total.',
        amount: 'Tax-inclusive amount',
        addRow: 'Add row',
        removeRow: 'Remove row',
        save: 'Save split',
        clear: 'Undo split',
        totalHint: 'Document total {total} / split total {sum}',
        sumMismatch: 'The split tax-inclusive amounts do not add up to the document total.',
        incomplete: 'Enter an amount, tax rate, and account on every row.',
        amendmentRequired: 'Accounting has already been created, so this split cannot be changed.',
        readonlyEmpty: 'No tax breakdown.',
      },
      paymentMethod: {
        CASH: 'Cash',
        CREDIT_CARD: 'Credit card',
        DEBIT_CARD: 'Debit card',
        BANK_TRANSFER: 'Bank transfer',
        QR_PAYMENT: 'QR payment',
        ELECTRONIC_MONEY: 'E-money',
        POINT: 'Points',
        UNKNOWN: 'Unknown',
        OTHER: 'Other',
      },
      sections: {
        basic: 'Basic information',
        amountTax: 'Amount & tax',
        paymentAccounting: 'Payment & account',
        lineItems: 'Line items (read-only)',
      },
      foreignCurrency: {
        notice: 'This is an overseas document. Enter the yen amount, preferably the card or bank settlement in yen.',
        faceAmount: 'Face amount: {amount}',
      },
      overlay: {
        noImage: 'No image available.',
      },
      actions: {
        save: 'Save',
        saveTemporary: 'Save draft',
        saving: 'Saving…',
        approve: 'Approve',
        approving: 'Approving…',
        correct: 'Correct',
        correcting: 'Correcting…',
        confirm: 'Confirm segment',
        confirming: 'Confirming…',
        reject: 'Reject',
        exclude: 'Exclude',
        reprocess: 'Reprocess',
      },
      reprocess: {
        title: 'Reprocess',
        description: 'Choose scope. Re-OCR may incur provider fees; full reprocess creates new segments.',
        descriptionFullOnly: 'Reprocess this document from the start. Existing extraction results will be replaced.',
        reExtract: 'Re-extract only',
        reOcr: 'Re-OCR',
        full: 'Full reprocess',
      },
      reject: {
        title: 'Reject segment',
        description: 'Segment will be rejected and excluded from accounting.',
        commentPlaceholder: 'Optional comment',
      },
      exclude: {
        title: 'Exclude segment',
        description: 'Manually exclude from accounting results.',
        commentPlaceholder: 'Optional comment',
      },
      errors: {
        multipleSegments: 'Multiple segments require per-segment review.',
        amendmentRequired: 'Bridged or posted — amendment required.',
        reviewAlreadyConfirmed: 'Review is confirmed. Reopen review before reprocessing.',
        segmentEditForbidden: 'Segment boundary edit is not allowed.',
        confirmedReopenRequired: 'Confirmed segment requires reopen before editing.',
        segmentCrossPageMerge: 'Cross-page merge is not allowed.',
        bboxOverlap: 'Segment boxes overlap too much.',
        operationNoChange: 'Boundary unchanged; nothing to submit.',
        operationRunning: 'Another segment operation is already running.',
        reopenForbidden: 'Reopen review is not allowed.',
        generic: 'Operation failed. Try again.',
      },
    },
    unassigned: {
      title: 'Unassigned queue',
      subtitle: 'Assign ownership to unassigned intake documents',
      noPermission: 'You do not have permission to assign document ownership.',
      emptyTitle: 'No unassigned documents',
      emptyDescription: 'All documents have ownership assigned.',
    },
    retention: {
      title: 'Retention status',
      description: 'Current retention and deletion policy state.',
      status: 'Intake status',
      fileStatus: 'Original file',
      uploadedAt: 'Uploaded at',
      scanStatus: 'Security scan',
      lockedNotice: 'This document is retention-locked or under legal hold and cannot be deleted.',
    },
    filters: {
      status: 'Processing status',
      documentDate: 'Transaction date',
      keyword: 'Keyword',
      keywordPlaceholder: 'Counterparty or description',
      account: 'Account',
      taxRate: 'Tax rate',
      invoicePresence: 'Invoice',
      invoicePresent: 'Present',
      invoiceAbsent: 'Absent',
      allInvoicePresence: 'All',
      amount: 'Amount',
      amountMinPlaceholder: 'Min',
      amountMaxPlaceholder: 'Max',
      allAccounts: 'All accounts',
      allTaxRates: 'All tax rates',
      all: 'All',
      apply: 'Search',
      reset: 'Reset',
    },
    fields: {
      documentType: 'Document type',
      documentDate: 'Document date',
      counterparty: 'Counterparty',
      amount: 'Amount',
    },
    table: {
      file: 'File',
      status: 'Status',
      source: 'Source',
      uploader: 'Uploader',
      ownership: 'Ownership',
      type: 'Type',
      date: 'Transaction date',
      amount: 'Amount',
      review: 'Review',
      actions: 'Actions',
    },
    documentType: {
      RECEIPT: 'Receipt',
      INVOICE: 'Invoice',
      REQUEST_FOR_PAYMENT: 'Payment request',
      OTHER: 'Other',
      UNKNOWN: 'Unknown',
    },
    status: {
      UPLOADED: 'Uploaded',
      SECURITY_SCANNING: 'Scanning',
      SECURITY_REJECTED: 'Scan rejected',
      STORED: 'Stored',
      OCR_QUEUED: 'OCR queued',
      OCR_PROCESSING: 'OCR processing',
      OCR_COMPLETED: 'OCR completed',
      AI_EXTRACTION_QUEUED: 'AI extraction queued',
      AI_EXTRACTING: 'AI extracting',
      PROCESSING_FAILED: 'Processing failed',
      PROCESSING_CANCELLED: 'Cancelled',
      QUEUED_FOR_PARSING: 'Queued for parsing',
      PARSING: 'Parsing',
      PARSED: 'Parsed',
      NEEDS_REVIEW: 'Needs review',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      DUPLICATE: 'Duplicate',
      PARSING_FAILED: 'Parsing failed',
      ARCHIVED: 'Archived',
      UNKNOWN: 'Unknown',
    },
    reviewStatus: {
      PENDING: 'Pending review',
      IN_REVIEW: 'In review',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      DUPLICATE: 'Duplicate',
      REPARSE_REQUESTED: 'Reparse requested',
    },
    ownershipStatus: {
      UNASSIGNED: 'Unassigned',
      AUTO_ASSIGNED: 'Auto-assigned',
      ASSIGNED: 'Assigned',
    },
    retentionStatus: {
      ACTIVE: 'Active',
      RETENTION_LOCKED: 'Retention locked',
      DELETION_REQUESTED: 'Deletion requested',
      LEGAL_HOLD: 'Legal hold',
      PURGED: 'Purged',
    },
    ownership: {
      personal: 'Personal',
      soleProprietor: 'Sole proprietor',
      corporation: 'Corporation',
      client: 'Client account',
      unassigned: 'Unassigned',
    },
    usage: {
      PERSONAL_USE: 'Personal use',
      BUSINESS_USE: 'Business use',
      MIXED_USE: 'Mixed use',
      UNKNOWN: 'Unknown',
    },
    suggestion: {
      title: 'Classification and account candidates',
      usage: 'Usage type',
      accountCandidate: 'Account candidate',
      accountCode: 'Account code',
      businessUseRatio: 'Business use ratio (%)',
    },
    duplicate: {
      probable: 'Possible duplicate',
      viewCandidate: 'View possible duplicate document',
      previewTitle: 'Possible duplicate document',
      previewDescription: 'Review the existing document that may be a duplicate.',
    },
    duplicates: {
      button: 'Duplicate documents ({count})',
      title: 'Duplicate documents',
      subtitle: 'Documents that are the same file, or share a counterparty, date, and tax-inclusive amount.',
      candidate: 'Candidate',
      duplicate: 'Duplicate',
      missingCandidate: 'The candidate document has been deleted',
      emptyTitle: 'No duplicate documents',
      emptyDescription: 'Documents that are the same file, or share a counterparty, date, and tax-inclusive amount, appear here.',
      edit: 'Edit',
      counterparty: 'Counterparty',
      documentDate: 'Transaction date',
      amount: 'Tax-inclusive amount',
      loadFailed: 'Could not load duplicate documents',
      kind: {
        exact: 'Same file',
        probable: 'Possible duplicate',
      },
    },
    source: {
      WEB_UPLOAD: 'Web upload',
      LINE_OFFICIAL_ACCOUNT: 'LINE',
    },
    actions: {
      review: 'Review',
      approve: 'Approve',
      correctAndApprove: 'Correct and approve',
      reparse: 'Reparse',
      reject: 'Reject',
    },
    delete: {
      action: 'Delete',
      deleting: 'Deleting...',
      confirmTitle: 'Delete this document?',
      confirmDescription: 'This will permanently delete "{name}" and its original file. This cannot be undone.',
      confirmAction: 'Delete',
      failed: 'Failed to delete document.',
      linkedToAccounting: 'This document has been posted to the ledger and cannot be deleted.',
      notAllowed: 'This document cannot be deleted in its current state.',
      retentionLocked: 'This document is retention-locked and cannot be deleted.',
      legalHold: 'This document is under legal hold and cannot be deleted.',
    },
    loading: 'Loading...',
    reviewMeta: {
      source: 'Source channel',
      receivedAt: 'Received at',
      uploader: 'Uploaded by',
      documentStatus: 'Processing status',
    },
    readonlyPreview: {
      title: 'Document preview',
      description: 'Review the matched document.',
      loadFailed: 'Could not load the document.',
    },
  },
  documentAccountingDemoPage: {
    title: 'Document Accounting Demo',
    subtitle: 'Approved document → accounting draft → human confirm posting → journal and reports',
    banner: 'Demo — approved documents are not posted automatically. Create a draft from review, then confirm posting on the draft page.',
    actions: {
      openInbox: 'Open document inbox',
      openUpload: 'Upload document',
      continueInInbox: 'Continue in inbox',
    },
    scenarios: {
      title: 'Recommended scenarios',
      hint: 'After running the seed script, find matching intakes in the inbox.',
      travel: {
        title: 'Travel receipt',
        description: 'Standard approved receipt that can be drafted and posted.',
      },
      office: {
        title: 'Office supplies',
        description: 'Office expense account mapping demo.',
      },
      mixed: {
        title: 'Mixed allocation (70/30)',
        description: 'Business/personal split draft lines demo.',
      },
      closedYear: {
        title: 'Closed fiscal year (posting blocked)',
        description: 'Transaction date in a closed period; validate/posting should be blocked.',
      },
    },
    seed: {
      title: 'Local seed data',
      description: 'Run with MySQL/MinIO available and backend built:',
    },
  },
  documentAccountingDemo: {
    title: 'Document accounting draft',
    subtitle: 'Edit and confirm posting for approved documents',
    banner: 'Demo — Approved documents are not posted automatically; confirm explicitly on this page.',
    sections: {
      source: 'Source document',
      editor: 'Draft lines',
      status: 'Status',
      actions: 'Actions',
    },
    fields: {
      transactionDate: 'Transaction date',
      description: 'Description',
      counterparty: 'Counterparty',
      totalAmount: 'Total amount',
      businessAllocation: 'Business use ratio',
      account: 'Account',
      debit: 'Debit',
      credit: 'Credit',
      amount: 'Amount',
    },
    status: {
      DRAFT: 'Draft',
      READY_TO_POST: 'Ready to post',
      POSTING: 'Posting',
      POSTED: 'Posted',
      VOIDED: 'Voided',
      POSTING_FAILED: 'Posting failed',
    },
    actions: {
      save: 'Save draft',
      validate: 'Validate balance',
      approve: 'Approve',
      confirmPosting: 'Post',
      void: 'Void draft',
      openJournal: 'View journal',
      openLedger: 'View ledger',
      openReport: 'View report',
      backToReview: 'Back to review',
    },
    confirmApproval: {
      title: 'Approve draft',
      description: 'Approve this accounting draft. Posting will not run until you confirm posting separately.',
      confirm: 'Approve',
      cancel: 'Cancel',
    },
    confirmPosting: {
      title: 'Confirm posting',
      description: 'Posted journals cannot be edited in place. Post this draft now?',
      confirm: 'Post',
      cancel: 'Cancel',
    },
    messages: {
      saved: 'Draft saved',
      validated: 'Draft validated',
      validationOk: 'Balance validation passed',
      validationFailed: 'Balance validation failed',
      approved: 'Draft approved',
      voided: 'Draft voided',
      postingConfirmed: 'Posting request submitted',
      postingSucceeded: 'Posting completed',
      postingFailed: 'Posting failed',
      loadFailed: 'Failed to load draft',
      periodClosed: 'Accounting period is closed; posting is blocked.',
    },
  },
  lineDocumentIntakeDemo: {
    title: 'LINE Document Intake Demo',
    subtitle: 'Simulated LINE message delivery (no real LINE traffic)',
    banner: 'Demo / Simulated LINE Event — No real LINE traffic. A future real LINE adapter will reuse the same normalized event and Document Intake service.',
    controls: {
      identity: 'Demo LINE user',
      fixture: 'Attachment fixture',
      workspaceHint: 'Optional target workspace',
      autoWorkspace: 'Resolve via binding',
      send: 'Send simulated event',
    },
    identities: {
      single: 'Single-workspace user',
      multiple: 'Multi-workspace user',
      unbound: 'Unbound user',
    },
    fixtures: {
      receiptPng: 'JPY receipt (PNG)',
      receiptJpg: 'JPY receipt (JPEG)',
      invoicePdf: 'JPY invoice (PDF)',
      invalidTxt: 'Invalid file (TXT)',
    },
    messages: {
      title: 'Inbound messages',
      empty: 'No simulated messages yet. Choose a user and fixture, then send.',
      workspaceSelectionRequired: 'Select a workspace before the document can be submitted.',
    },
    notifications: {
      title: 'Recent notifications (log)',
    },
    actions: {
      openInbox: 'Open Document Inbox',
      openReview: 'Open Review',
      selectWorkspace: 'Confirm workspace',
      retry: 'Retry',
    },
    status: {
      RECEIVED: 'Received',
      RESOLVING_OWNER: 'Resolving owner',
      FETCHING_ATTACHMENT: 'Fetching attachment',
      SUBMITTING_DOCUMENT: 'Submitting document',
      ACCEPTED: 'Accepted',
      NEEDS_WORKSPACE_SELECTION: 'Workspace selection required',
      DUPLICATE: 'Duplicate',
      FAILED: 'Failed',
    },
  },
  errors: {
    unauthorized: 'Your session has expired. Please sign in again.',
    requestFailed: 'Request failed. Please try again later.',
    routeLoadFailed: 'Page failed to load',
    routeLoadFailedDescription: 'This page asset may have been updated, or the network was interrupted. Refresh to continue.',
    badRequest: 'The request parameters are invalid.',
    forbidden: 'You do not have permission to perform this action.',
    notFound: 'The requested resource was not found.',
    unavailable: 'The service is temporarily unavailable. Please try again later.',
    quotaLimitExceeded: 'Your current plan quota has been reached. Upgrade your plan or contact an administrator.',
    quotaUsage: ' (used {used} / {limit})',
    quotaReset: ', resets at {resetAt}',
    quotaAction: '. Please go to Plan & Billing to upgrade, or contact an administrator.',
    subscriptionReadOnly: 'Your trial or subscription has expired. You can view existing data only. Renew to continue processing documents.',
  },
} as const;
